export type ArticleBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "cta"; text: string; buttonLabel: string }
  | { type: "table"; headers: string[]; rows: string[][] };

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  date: string;
  dateModified: string;
  readTime: string;
  excerpt: string;
  relatedProductPath: string;
  relatedProductLabel: string;
  body: ArticleBlock[];
  faqs?: { question: string; answer: string }[];
  metaTitle?: string;
  shortAnswer?: string[];
  tags: string[];
  ogImage?: string;
  heroScreenshot?: string;
  heroScreenshotAlt?: string;
  relatedSlugs?: string[];
  externalSources?: { label: string; url: string }[];
};

export const BLOG_TAGS = ["Setup & Troubleshooting", "Screen Time & Habits", "Risky Apps", "About CrenelView", "Family & Relatives"] as const;

export function getRelatedPosts(post: BlogPost, count = 2): BlogPost[] {
  if (post.relatedSlugs) {
    return post.relatedSlugs
      .map((s) => blogPosts.find((p) => p.slug === s))
      .filter((p): p is BlogPost => Boolean(p))
      .slice(0, count);
  }
  return blogPosts
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({ post: p, sharedTags: p.tags.filter((t) => post.tags.includes(t)).length }))
    .sort((a, b) => {
      if (b.sharedTags !== a.sharedTags) return b.sharedTags - a.sharedTags;
      return new Date(b.post.date).getTime() - new Date(a.post.date).getTime();
    })
    .slice(0, count)
    .map((s) => s.post);
}

export const blogPosts: BlogPost[] = [
  {
    slug: "block-apps-child-uses-my-phone",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/block-apps-child-uses-my-phone.jpg",
    title: "How to block apps on your phone when your child uses it",
    metaTitle: "Block Apps When Your Child Uses Your Phone",
    description:
      "A step by step look at blocking apps on the one phone you hand to your child, without a second device or an account.",
    date: "2026-08-29",
    dateModified: "2026-08-29",
    readTime: "5 min read",
    excerpt:
      "If your child uses your phone rather than one of their own, here is exactly what blocking apps looks like in practice, start to finish.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See the full setup, step by step",
    body: [
      {
        type: "p",
        text: "If your child uses your phone rather than a phone of their own, most parental control advice does not apply to you. It is written for a second device with its own account. This is what blocking apps actually looks like on the one phone you both use.",
      },
      { type: "h2", text: "What does 'blocking an app' actually mean here?" },
      {
        type: "p",
        text: "It means the app closes itself and returns to the home screen the moment it is opened while blocked. CrenelView does not uninstall anything, hide icons, or lock the whole phone. The apps you did not block keep working normally.",
      },
      { type: "h2", text: "How do I choose which apps get blocked?" },
      {
        type: "p",
        text: "You tick them from a list of the apps already installed on your phone. There is nothing to download separately and nothing to configure per app beyond that tick. That list is the whole selection step.",
      },
      { type: "h2", text: "Can I block some apps always and others only sometimes?" },
      {
        type: "p",
        text: "No. Blocking is one setting for your entire list, not a per app schedule. You choose either 'all the time' or 'at set times', for example 20:00 to 07:00, and that single choice applies to every app you have ticked.",
      },
      { type: "h2", text: "What is the one step I have to do by hand?" },
      {
        type: "p",
        text: "After installing CrenelView and setting a parent PIN, you have to open your phone's own Settings app and switch on CrenelView's accessibility service yourself. This is required for blocking to work at all, and it is the only part of setup that happens outside the app.",
      },
      { type: "h2", text: "What happens when my child needs an app for a few minutes?" },
      {
        type: "p",
        text: "Tapping 'Allow for a while' unlocks your whole blocked list for 10, 15, 30 or 60 minutes. Everything on the list works again until the timer runs out, then it all blocks itself again automatically, with no second step from you.",
      },
      {
        type: "p",
        text: "One app, on the phone you already have, doing one job: keeping a fixed list of apps closed until you decide otherwise.",
      },
      {
        type: "cta",
        text: "Ready to set up your own block list?",
        buttonLabel: "Get it on Google Play",
      },
    ],
  },
  {
    slug: "stop-child-opening-youtube",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/stop-child-opening-youtube.jpg",
    title: "How to stop a child opening YouTube on your phone",
    metaTitle: "How to Stop a Child Opening YouTube",
    description:
      "A plain, practical look at why kids end up on YouTube on a shared phone, and how to block it without spying or new hardware.",
    date: "2026-08-27",
    dateModified: "2026-08-27",
    readTime: "4 min read",
    excerpt:
      "If you have handed your phone over for a game and found YouTube open ten minutes later, here is why it happens and what actually stops it.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how CrenelView blocks apps, step by step",
    body: [
      {
        type: "p",
        text: "If you have handed your phone to your child for a game or a video call and found YouTube open ten minutes later, you are not alone. On a single shared phone, YouTube is usually one tap away from anything else your child is doing, and kids find that tap fast.",
      },
      { type: "h2", text: "Why does my child always end up on YouTube?" },
      {
        type: "p",
        text: "YouTube is built to keep a viewer watching. Autoplay moves from one video into the next without asking. There is no natural stopping point, and a 3 to 12 year old has no reason to look for one. The app is not doing anything unusual. It is doing exactly what it is designed to do.",
      },
      { type: "h2", text: "Why don't turning off wifi or hiding the icon work?" },
      {
        type: "p",
        text: "Turning off wifi stops YouTube for everyone, including you. Hiding the app icon does not stop it from being found through search or a shared link. Asking a young child to just not open it works for a while, then does not.",
      },
      { type: "h2", text: "How do I actually block YouTube on a shared phone?" },
      {
        type: "p",
        text: "CrenelView lets you tick YouTube along with any other apps you want blocked. You then choose one setting for the entire list: blocked all the time, or blocked only during hours you set, like homework time or after a set bedtime. The block happens on the phone itself. Nothing about your child's activity is recorded, reported, or sent anywhere.",
      },
      { type: "h2", text: "What if my child genuinely needs YouTube for a few minutes?" },
      {
        type: "p",
        text: "CrenelView lets you unlock your whole blocked list for a fixed window, 10, 15, 30 or 60 minutes, so YouTube and anything else on the list works again until the timer runs out. It locks itself again automatically after that, no separate step needed.",
      },
      { type: "h2", text: "How do I stop my child switching the block back off?" },
      {
        type: "p",
        text: "A PIN stops your child from opening CrenelView and switching the block off themselves. You set a recovery option in case you forget the PIN, so you are never locked out of your own settings.",
      },
      {
        type: "p",
        text: "None of this requires a new phone, a second device, or an account anywhere. It is one app, on the phone you already have, doing one job.",
      },
      {
        type: "cta",
        text: "Block YouTube, and anything else, in one setup.",
        buttonLabel: "Get it on Google Play",
      },
    ],
  },
  {
    slug: "parental-control-no-phone-of-their-own",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/parental-control-no-phone-of-their-own.jpg",
    title: "Parental control when your child has no phone of their own",
    metaTitle: "Parental Control When Your Child Has No Phone",
    description:
      "For families where the child does not have their own device, here is what parental control actually means on a phone you both use.",
    date: "2026-08-27",
    dateModified: "2026-08-27",
    readTime: "4 min read",
    excerpt:
      "Most parental control apps assume your child owns a phone. Here is what to do when the phone in question is yours, and only sometimes theirs.",
    relatedProductPath: "/privacy",
    relatedProductLabel: "Read what CrenelView does and does not do",
    body: [
      {
        type: "p",
        text: "Most parental control apps assume your child owns a phone. You install an app, it treats the device as belonging to your child, and everything about it works from that one assumption. That entire model breaks down the moment the phone in question is yours, and your child only uses it sometimes.",
      },
      { type: "h2", text: "Why don't normal parental control apps work on a shared phone?" },
      {
        type: "p",
        text: "On a shared phone, you cannot block apps permanently, because you use those same apps yourself. You need blocking that switches on when you hand the phone over, and switches off, cleanly, when you take it back.",
      },
      { type: "h2", text: "What actually works when there is no second device?" },
      {
        type: "p",
        text: "CrenelView is built around handing the phone over, not around owning a separate device. You tick the apps to block, then choose one setting for that whole list: blocked all the time, or blocked only during hours you set in advance, so it happens automatically without you touching the phone again. When you take the phone back, you decide whether to keep the block running or turn it off.",
      },
      { type: "h2", text: "Can I temporarily allow an app without unblocking everything?" },
      {
        type: "p",
        text: "Not a single app on its own. If your child needs something on the list for a few minutes, a temporary unblock for 10, 15, 30 or 60 minutes opens the whole list back up for that window, then blocks itself again automatically. You do not have to open CrenelView twice.",
      },
      { type: "h2", text: "Do I need to create an account or buy a second phone?" },
      {
        type: "p",
        text: "No. Because the phone is shared, CrenelView does not ask for an account, does not need a server, and does not send anything about how the phone is used anywhere else. It is a set of on and off switches for apps, controlled by a PIN only you know.",
      },
      {
        type: "p",
        text: "If your family's setup is one phone, passed back and forth, this is the situation CrenelView was actually built for.",
      },
      {
        type: "cta",
        text: "One phone, one setup, no second device needed.",
        buttonLabel: "Get it on Google Play",
      },
    ],
  },
  {
    slug: "stop-child-downloading-new-games",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/stop-child-downloading-new-games.jpg",
    title: "How to stop your child downloading new games on your phone",
    metaTitle: "Stop Your Child Downloading New Games",
    description:
      "New games can land on your phone without you noticing, and your child finds them first. Here is how to make sure they stay blocked automatically.",
    date: "2026-09-08",
    dateModified: "2026-09-08",
    readTime: "4 min read",
    excerpt:
      "New games can land on your phone without you noticing, and your child finds them first. Here is how to make sure they stay blocked automatically.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how CrenelView blocks apps, step by step",
    body: [
      {
        type: "p",
        text: "A new game doesn't ask permission. Your child installs one, a friend airdrops it over, or it shows up bundled inside another app, and suddenly it's just there alongside everything else on your phone. Turning on CrenelView's automatic game-blocking setting closes that gap: any app your phone's own system recognizes as a game gets added to your blocked list the moment it lands, before your child has a chance to open it.",
      },
      { type: "h2", text: "How do new games end up on my phone in the first place?" },
      {
        type: "p",
        text: "Kids find games fast. A friend recommends one at school, your child searches the Play Store on their own, or a game arrives bundled inside another app your child was already using. None of it needs your permission. By the time you notice the icon, it's usually already been opened once or twice.",
      },
      { type: "h2", text: "Can I stop new games from being installed automatically?" },
      {
        type: "p",
        text: "Not the install itself, no. CrenelView doesn't touch the Play Store, and it can't stop a download from starting. What it can do is make sure a newly installed game never actually opens for your child. Turn the setting on once, and from then on, anything your phone's own system labels as a game gets added to your blocked list the moment it's installed. You don't have to notice it, remember it, or go add it yourself.",
      },
      {
        type: "cta",
        text: "Turn on automatic game-blocking in a few taps.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this block games I already have installed?" },
      {
        type: "p",
        text: "No, not on its own. This particular setting only catches games installed after you turn it on. Anything already on your phone needs to be ticked once, the same way you'd block any other app. After that it behaves like everything else on your list: closed the moment your child opens it.",
      },
      { type: "h2", text: "What if the new app is not a game?" },
      {
        type: "p",
        text: "Then it stays off the automatic list. The setting only catches whatever your phone's own operating system files under the Games category, not every app installed later. A calculator, a photo editor, or a messaging app won't be swept in on its own. Want one of those blocked too? Tick it by hand, same as anything already on your phone.",
      },
      { type: "h2", text: "Can my child turn this back off?" },
      {
        type: "p",
        text: "Not without your PIN. Every setting in CrenelView sits behind the same parent PIN, including the block list itself. A child who finds a new game already blocked has no way to unblock it, switch off the automatic setting, or remove it from the list. They'd need your PIN for any of that.",
      },
      {
        type: "p",
        text: "One setting, turned on once. Every game that shows up after it stays closed until you decide otherwise.",
      },
      {
        type: "cta",
        text: "One setup on your own phone, and new games stay off your child's list from here on.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How do new games end up on my phone in the first place?",
        answer:
          "Kids find games fast. A friend recommends one at school, your child searches the Play Store on their own, or a game arrives bundled inside another app. None of it needs your permission, and by the time you notice the icon, it has probably already been opened.",
      },
      {
        question: "Can I stop new games from being installed automatically?",
        answer:
          "Not the install itself. CrenelView does not touch the Play Store and cannot stop a download from starting. What it does is make sure a newly installed game never actually opens for your child. Turn the setting on once, and anything your phone's own system labels as a game gets added to your blocked list the moment it lands.",
      },
      {
        question: "Does this block games I already have installed?",
        answer:
          "Not on its own. The automatic setting only catches games installed after you turn it on. Anything already on your phone needs to be ticked once, the same way you would block any other app.",
      },
      {
        question: "What if the new app is not a game?",
        answer:
          "It stays off the automatic list. The setting only catches whatever your phone's own operating system files under the Games category. A calculator or a messaging app installed later will not be swept in automatically; tick it yourself if you want it blocked too.",
      },
      {
        question: "Can my child turn this back off?",
        answer:
          "Not without your PIN. Every setting in CrenelView, including the block list itself, sits behind the same parent PIN, so a child who finds a new game already blocked has no way to switch it back on.",
      },
    ],
  },
  {
    slug: "block-apps-only-at-bedtime",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/block-apps-only-at-bedtime.jpg",
    title: "How to block apps on your phone only at bedtime",
    metaTitle: "How to Block Apps on Your Phone at Bedtime",
    description:
      "Want your child's apps to close themselves every night without you doing anything? Here is how to set a bedtime schedule that runs on its own.",
    date: "2026-09-08",
    dateModified: "2026-09-08",
    readTime: "4 min read",
    excerpt:
      "Want your child's apps to close themselves every night without you doing anything? Here is how to set a bedtime schedule that runs on its own.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how CrenelView blocks apps, step by step",
    body: [
      {
        type: "p",
        text: "CrenelView lets you block your whole list of apps during hours you choose, instead of all day. Turn on scheduled blocking, set a start and end time such as 20:00 to 07:00, and every app you have ticked closes itself automatically once that window begins, then works again on its own once it ends. No reminder, no second step, nothing to undo the next morning.",
      },
      { type: "h2", text: "How do I set a bedtime block on my phone?" },
      {
        type: "p",
        text: "Tick the apps you want blocked, then choose the scheduled-hours option instead of blocking all the time. Set a start time and an end time, for example 20:00 to 07:00, and save it. From that point on, every app on your list follows that same schedule without you touching it again each night.",
      },
      { type: "h2", text: "Does the block turn off by itself in the morning?" },
      {
        type: "p",
        text: "Yes. Once the end time arrives, every app on your list unblocks itself automatically. You don't have to open CrenelView, flip a switch, or remember anything at 7am. The same thing happens in reverse at the start time each evening, whether you're there to see it or not.",
      },
      {
        type: "cta",
        text: "Set a bedtime schedule for your phone in a few taps.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can I still let my child use an app during blocked hours if I need to?" },
      {
        type: "p",
        text: "Yes. Tap 'Allow for a while' and choose 10, 15, 30 or 60 minutes, and every app on your list opens again for exactly that long, even during blocked hours. It locks itself back up automatically once the timer runs out, so a late video call with grandma doesn't mean rebuilding your whole schedule afterward.",
      },
      { type: "h2", text: "Can I block some apps only at bedtime and others all the time?" },
      {
        type: "p",
        text: "No. Blocking is one schedule for your entire list, not a mix of rules per app. Every app you've ticked follows the same choice: blocked all the time, or blocked only during the hours you set. Wanting some apps gone all day and others just at bedtime means treating those as two different decisions, not one list.",
      },
      { type: "h2", text: "What happens to the apps I have not blocked at all?" },
      {
        type: "p",
        text: "Nothing changes for them. Only the apps you've ticked follow the bedtime schedule. Everything else on your phone, calls, messages, your camera, anything you didn't select, works exactly the same at every hour of the day.",
      },
      {
        type: "p",
        text: "One schedule, set once, and your evenings stop being a negotiation.",
      },
      {
        type: "cta",
        text: "One schedule, set once, and bedtime stops being something you have to enforce by hand.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How do I set a bedtime block on my phone?",
        answer:
          "Tick the apps you want blocked, then choose the scheduled-hours option instead of blocking all the time. Set a start time and an end time, for example 20:00 to 07:00, and save it. From that point on, every app on your list follows that same schedule automatically.",
      },
      {
        question: "Does the block turn off by itself in the morning?",
        answer:
          "Yes. Once the end time arrives, every app on your list unblocks itself automatically, with no need to open the app or do anything by hand. The same happens in reverse at the start time each evening.",
      },
      {
        question: "Can I still let my child use an app during blocked hours if I need to?",
        answer:
          "Yes. Tap \"Allow for a while\" and choose 10, 15, 30 or 60 minutes, and every app on your list opens again for exactly that long, even during blocked hours, then locks itself back up automatically.",
      },
      {
        question: "Can I block some apps only at bedtime and others all the time?",
        answer:
          "No. Blocking is one schedule for your entire list, not a mix of rules per app. Every app you have ticked follows the same choice, either blocked all the time or blocked only during set hours.",
      },
      {
        question: "What happens to the apps I have not blocked at all?",
        answer:
          "Nothing changes for them. Only the apps you have ticked follow the bedtime schedule. Everything else on your phone works exactly the same at every hour.",
      },
    ],
  },
  {
    slug: "stop-child-downloading-games-on-my-phone",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/stop-child-downloading-games-on-my-phone.jpg",
    title: "How to stop your child downloading games on your phone",
    metaTitle: "Stop Your Child From Downloading Games",
    description:
      "Handed over your phone and got it back with three new games on it? Here is what actually stops a child installing games on Android, and what doesn't.",
    date: "2026-09-09",
    dateModified: "2026-09-09",
    readTime: "4 min read",
    excerpt:
      "You can't switch off installing on your own phone, but you can make every new game close the moment it opens.",
    relatedProductPath: "/",
    relatedProductLabel: "See how automatic game-blocking is set up, step by step",
    body: [
      {
        type: "p",
        text: "You can't fully stop a child installing games on your own Android phone without locking yourself out of the Play Store too. What you can do is make every new game useless the moment it lands: CrenelView runs on your phone and automatically blocks any newly installed app that Android files under its Games category, so a game your child installs closes the instant it's opened.",
      },
      { type: "h2", text: "Why does my child keep installing games on my phone?" },
      {
        type: "p",
        text: "Because it's one tap. The Play Store sits on your home screen, the search bar is right there, and every game listing has a big green Install button that doesn't ask for anything. A seven-year-old who can't read the app's name can still recognise the icon from an ad inside the last game and press the button. Twenty minutes later there are three new icons on your screen and a storage-full warning. Nobody did anything wrong. The phone is just built to make installing easy, and it doesn't know who's holding it.",
      },
      { type: "h2", text: "Can I turn off installing apps on my phone completely?" },
      {
        type: "p",
        text: "Not in a way that leaves the phone usable for you. Android doesn't have a switch that says \"no new apps\" for a parent's own phone. You could uninstall the Play Store, but then you can't update your banking or messaging apps either. Play Store does let you require your fingerprint or password before a purchase, and that's worth switching on - but it only kicks in when money is involved. Most of the games your child finds don't cost anything to install, so that setting never fires. The realistic goal isn't stopping the install. It's making sure the install doesn't matter.",
      },
      {
        type: "cta",
        text: "Turn on automatic game-blocking once, and every new game is closed before it can start.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How do I block games my child installs before I even notice them?" },
      {
        type: "p",
        text: "CrenelView watches for new installs. When a new app arrives and your phone's own system lists it as a game, CrenelView adds it to your blocked list on its own - you don't have to spot it first. From then on, opening that game does the same thing as opening any other blocked app: it closes straight away and the phone goes back to the home screen. There's no password screen for your child to try codes on, because there's nothing to type into. The game just doesn't stay open. You can also tick any app yourself from the list, so games that were already on the phone before you set things up are covered too. One thing to be clear about: this is for games specifically, decided by how Android itself categorises the app. A new video app or a drawing app isn't picked up automatically. Those you tick by hand.",
      },
      { type: "h2", text: "What about the games I actually want her to play?" },
      {
        type: "p",
        text: "Blocked doesn't have to mean blocked forever. CrenelView has a timed unlock called \"Allow for a while\". Tap 10, 15, 30 or 60 minutes and everything on your blocked list opens for exactly that long, then blocks itself again without you having to remember. It suits the way most handovers actually go: \"You can have it till the food is ready.\" When the timer runs out, the app is the thing saying no, not you. If you'd rather block only at certain hours - say nothing after 8pm - you can set a schedule instead, and the same schedule applies to your whole list including any games that get added later.",
      },
      { type: "h2", text: "Doesn't screen pinning already do this?" },
      {
        type: "p",
        text: "Android's built-in screen pinning is the closest thing the phone has on its own, and it's worth trying if you haven't. It holds one app on screen and stops the child leaving it. But it does nothing about the Play Store if that's the app you pinned, it only covers one app at a time, and it's widely reported as easy for a child to get out of once they've seen the swipe-and-hold trick. It also doesn't know what a game is. CrenelView is doing a different job: it isn't holding your child inside one app, it's making a whole list of apps refuse to open, and it keeps adding new games to that list on its own. Your calls, camera and everything else keep working as normal while it runs.",
      },
      {
        type: "p",
        text: "Give it a week and check what's changed. The new icons might still appear now and then. They just won't do anything.",
      },
      {
        type: "cta",
        text: "Set your blocked list once. New games get added to it by themselves.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does my child keep installing games on my phone?",
        answer:
          "Because it's one tap. The Play Store sits on your home screen, the search bar is right there, and every game listing has a big green Install button that doesn't ask for anything. A seven-year-old who can't read the app's name can still recognise the icon from an ad inside the last game and press the button. Twenty minutes later there are three new icons on your screen and a storage-full warning. Nobody did anything wrong. The phone is just built to make installing easy, and it doesn't know who's holding it.",
      },
      {
        question: "Can I turn off installing apps on my phone completely?",
        answer:
          "Not in a way that leaves the phone usable for you. Android doesn't have a switch that says \"no new apps\" for a parent's own phone. You could uninstall the Play Store, but then you can't update your banking or messaging apps either. Play Store does let you require your fingerprint or password before a purchase, and that's worth switching on - but it only kicks in when money is involved. Most of the games your child finds don't cost anything to install, so that setting never fires. The realistic goal isn't stopping the install. It's making sure the install doesn't matter.",
      },
      {
        question: "How do I block games my child installs before I even notice them?",
        answer:
          "CrenelView watches for new installs. When a new app arrives and your phone's own system lists it as a game, CrenelView adds it to your blocked list on its own - you don't have to spot it first. From then on, opening that game does the same thing as opening any other blocked app: it closes straight away and the phone goes back to the home screen. There's no password screen for your child to try codes on, because there's nothing to type into. The game just doesn't stay open. You can also tick any app yourself from the list, so games that were already on the phone before you set things up are covered too. One thing to be clear about: this is for games specifically, decided by how Android itself categorises the app. A new video app or a drawing app isn't picked up automatically. Those you tick by hand.",
      },
      {
        question: "What about the games I actually want her to play?",
        answer:
          "Blocked doesn't have to mean blocked forever. CrenelView has a timed unlock called \"Allow for a while\". Tap 10, 15, 30 or 60 minutes and everything on your blocked list opens for exactly that long, then blocks itself again without you having to remember. It suits the way most handovers actually go: \"You can have it till the food is ready.\" When the timer runs out, the app is the thing saying no, not you. If you'd rather block only at certain hours - say nothing after 8pm - you can set a schedule instead, and the same schedule applies to your whole list including any games that get added later.",
      },
      {
        question: "Doesn't screen pinning already do this?",
        answer:
          "Android's built-in screen pinning is the closest thing the phone has on its own, and it's worth trying if you haven't. It holds one app on screen and stops the child leaving it. But it does nothing about the Play Store if that's the app you pinned, it only covers one app at a time, and it's widely reported as easy for a child to get out of once they've seen the swipe-and-hold trick. It also doesn't know what a game is. CrenelView is doing a different job: it isn't holding your child inside one app, it's making a whole list of apps refuse to open, and it keeps adding new games to that list on its own. Your calls, camera and everything else keep working as normal while it runs.",
      },
    ],
  },
  {
    slug: "app-lock-kaise-lagaye-for-kids",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/app-lock-kaise-lagaye-for-kids.jpg",
    title: "App Lock Kaise Lagaye for Kids on Android",
    metaTitle: "App Lock Kaise Lagaye for Kids",
    description:
      "Searching app lock kaise lagaye for kids? Here's why blocking an app works better than a password lock, and how to set one up on your phone in minutes.",
    date: "2026-09-11",
    dateModified: "2026-09-11",
    readTime: "4 min read",
    excerpt:
      "Searching app lock kaise lagaye for kids? Here's why blocking an app works better than a password lock, and how to set one up on your phone in minutes.",
    relatedProductPath: "/",
    relatedProductLabel: "See how blocking and the parent PIN work, step by step",
    body: [
      {
        type: "p",
        text: "When people search \"app lock kaise lagaye for kids,\" they usually want one thing: stop a game or video app from opening again the second they look away. A password-style app lock is not actually the best fix for that. Blocking the app so it closes on its own works better, especially on a phone the whole family shares.",
      },
      { type: "h2", text: "What's the difference between an app lock and blocking an app for a child?" },
      {
        type: "p",
        text: "An app lock usually means a password screen sits in front of an app, so whoever opens it has to enter a code first. Blocking works differently: the app never opens at all. It closes the second it's tapped and the phone goes straight back to the home screen, so there's no code for a curious seven year old to watch you type and memorize.",
      },
      { type: "h2", text: "How do I set up an app lock for kids on my Android phone?" },
      {
        type: "p",
        text: "On CrenelView, you pick which apps to block from a list already on your phone. Once an app is ticked, it closes immediately if your child taps it. Setup takes a couple of minutes, and a parent PIN sits behind the settings, so your child can't just untick the app you blocked five minutes ago.",
      },
      {
        type: "cta",
        text: "Set up your first app block list in about two minutes.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can I lock apps only at certain times, like bedtime, instead of all day?" },
      {
        type: "p",
        text: "Yes, but it's one setting for your whole list, not a switch per app. You choose either \"always blocked\" or a scheduled window, such as 8pm to 7am, and every app on your list follows that same rule. If you want YouTube blocked all day and a game blocked only at night, that needs two different lists, because the schedule applies to everything ticked at once.",
      },
      { type: "h2", text: "What if my child needs the phone urgently, like a video call with a grandparent?" },
      {
        type: "p",
        text: "CrenelView has a timed unlock called \"Allow for a while.\" Tap 10, 15, 30 or 60 minutes and the whole blocked list opens for exactly that long, then locks itself back down on its own. So the phone comes back to normal for the length of the call, not for the rest of the day.",
      },
      { type: "h2", text: "What happens when my child downloads a new game before I've added an app lock?" },
      {
        type: "p",
        text: "If your phone's own system recognizes a newly installed app as a game, CrenelView adds it to the block list automatically. That covers a lot of what actually shows up after a bored kid spends ten minutes in the Play Store. It won't catch every kind of app this way, though. Anything that isn't a game still needs you to add it by hand once you notice it.",
      },
      {
        type: "p",
        text: "Set your block list once, before the next handover, not in the middle of the argument.",
      },
      {
        type: "cta",
        text: "Turn on your block list before you hand over the phone next.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What's the difference between an app lock and blocking an app for a child?",
        answer:
          "An app lock usually means a password screen sits in front of an app, so whoever opens it has to enter a code first. Blocking works differently: the app never opens at all. It closes the second it's tapped and the phone goes straight back to the home screen, so there's no code for a curious seven year old to watch you type and memorize.",
      },
      {
        question: "How do I set up an app lock for kids on my Android phone?",
        answer:
          "On CrenelView, you pick which apps to block from a list already on your phone. Once an app is ticked, it closes immediately if your child taps it. Setup takes a couple of minutes, and a parent PIN sits behind the settings, so your child can't just untick the app you blocked five minutes ago.",
      },
      {
        question: "Can I lock apps only at certain times, like bedtime, instead of all day?",
        answer:
          "Yes, but it's one setting for your whole list, not a switch per app. You choose either always blocked or a scheduled window, such as 8pm to 7am, and every app on your list follows that same rule. If you want YouTube blocked all day and a game blocked only at night, that needs two different lists, because the schedule applies to everything ticked at once.",
      },
      {
        question: "What if my child needs the phone urgently, like a video call with a grandparent?",
        answer:
          "CrenelView has a timed unlock called Allow for a while. Tap 10, 15, 30 or 60 minutes and the whole blocked list opens for exactly that long, then locks itself back down on its own. So the phone comes back to normal for the length of the call, not for the rest of the day.",
      },
      {
        question: "What happens when my child downloads a new game before I've added an app lock?",
        answer:
          "If your phone's own system recognizes a newly installed app as a game, CrenelView adds it to the block list automatically. That covers a lot of what actually shows up after a bored kid spends ten minutes in the Play Store. It won't catch every kind of app this way, though. Anything that isn't a game still needs you to add it by hand once you notice it.",
      },
    ],
  },
  {
    slug: "why-your-child-cries-when-you-say-no-to-the-phone",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/why-your-child-cries-when-you-say-no-to-the-phone.jpg",
    title: "Why Your Child Cries Every Time You Say No to the Phone",
    metaTitle: "Child Crying Over the Phone? Why It Happens",
    description:
      "Your child crying every time you say no to the phone is common, not a parenting failure. Here's why it happens and how to end sessions without a fight.",
    date: "2026-09-11",
    dateModified: "2026-09-11",
    readTime: "7 min read",
    excerpt:
      "Your child crying every time you say no to the phone is common, not a parenting failure. Here's why it happens and how to end sessions without a fight.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how scheduled blocking and Allow for a while work, step by step",
    body: [
      {
        type: "p",
        text: "Your child probably isn't crying about the video itself. They're crying because the ending was sudden and, as far as they can tell, negotiable - if they cry hard enough, the ending might move. That's not a discipline failure on your part. It's just how an ending that depends on your mood, rather than a fixed point, plays out for a three to twelve year old.",
      },
      { type: "h2", text: "Why does my child cry every time I say no to the phone?" },
      {
        type: "p",
        text: "Because the ending felt random. A four year old doesn't track time the way you do - fifteen minutes into a video, there's no internal signal that it's about to be over, so when you say the word \"no,\" it lands like it came out of nowhere. Add the very real difference between watching something colorful and fast-moving and staring at a plain room a second later, and the crash isn't really about the app. It's the drop itself. Older kids in the same age range cry less about the sensory drop and more about the unfairness of it - why now, why this exact minute, why not five more. Both are real reasons. Neither means anything is wrong with your child, or with how you're parenting.",
      },
      { type: "h2", text: "Is this normal, or have I done something wrong?" },
      {
        type: "p",
        text: "Normal, mostly. Impulse control is one of the last things to develop in a child's brain, and a three year old and a ten year old are both still working on it, just at different points. A three year old who screams when the phone is taken away is behaving exactly the way a three year old's brain is built to behave in that moment. A ten year old sulking through dinner is doing the older-kid version of the same thing: reacting to a limit rather than calmly accepting it. If your child is one of the rare kids who hands the phone back without a word, that says more about their temperament than about your parenting. Most don't, and that's fine.",
      },
      { type: "h2", text: "Does giving five more minutes actually help?" },
      {
        type: "p",
        text: "Usually it makes the next ending worse, not better. Once a child learns that \"no\" can turn into \"okay, five more minutes\" if they push, they've learned the actual rule: the stated time isn't the real time, crying is. That's a hard lesson to unlearn once it's in place, and it means every handover becomes a small negotiation instead of a known fact. A fixed, predictable ending, even one your child dislikes, is easier to eventually accept than an ending that moves depending on how upset they get.",
      },
      {
        type: "cta",
        text: "See exactly how a scheduled block window works, in less than two minutes.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What changes if the app says no instead of me?" },
      {
        type: "p",
        text: "One thing that changes the dynamic completely: the phone can be the one enforcing the end, not you. When an app closes on its own because it's scheduled to, or because the block just turned back on, your child isn't negotiating with you anymore. There's no one to cry at who might change their mind. CrenelView works this way - you choose which apps are blocked and, if you want, a schedule for when, and the app closes itself once the time is up. You're not the one taking anything away in the moment. You already decided earlier, when nobody was upset.",
      },
      { type: "h2", text: "How do I end a session without a fight every time?" },
      {
        type: "p",
        text: "Set the ending before the session starts, not during it. If your child knows going in that the phone works until 7pm and then it doesn't, that's a fact they can plan around, the same way they plan around a TV show ending. A scheduled block window does this without you having to remember to say anything at all - it just happens. For the moments you genuinely want to hand the phone over for a specific reason, like a long car ride or a call with a grandparent, a timed unlock works better than \"just five more minutes,\" because it has a real end built in rather than one you have to enforce yourself. CrenelView's \"Allow for a while\" does exactly that: pick 10, 15, 30 or 60 minutes, and it closes back down on its own once the time's up.",
      },
      { type: "h2", text: "What do I do in the moment, once the crying has already started?" },
      {
        type: "p",
        text: "Don't relitigate the decision while your child is upset. Explaining the reasoning again, or offering a shortened compromise, usually reads to a crying child as an opening, not comfort - it signals the ending might still move if they push a little harder. A short, calm acknowledgment, something like \"I know, you wanted to keep watching,\" without reopening the negotiation, tends to end faster than a longer explanation. And if the app closed on its own rather than you closing it, you get to be the person who's sympathetic about it instead of the person who took it away. That's a much easier position to be in.",
      },
      {
        type: "p",
        text: "The fight gets easier the moment the ending stops depending on you.",
      },
      {
        type: "cta",
        text: "Set the ending once, before the next request for the phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does my child cry every time I say no to the phone?",
        answer:
          "Because the ending felt random. A four year old doesn't track time the way you do, so when you say no, it lands like it came out of nowhere. The sudden shift from something colorful and fast-moving to a plain room adds to the crash. Older kids cry less about that sensory drop and more about the unfairness of the timing. Neither reaction means anything is wrong with your child or your parenting.",
      },
      {
        question: "Is this normal, or have I done something wrong?",
        answer:
          "It's normal. Impulse control is one of the last things to develop in a child's brain, so reacting strongly to a limit is expected at every age from three to twelve, just in different forms. A child who hands the phone back calmly is the exception, not the standard you should expect.",
      },
      {
        question: "Does giving five more minutes actually help?",
        answer:
          "Usually it makes things worse over time. Once a child learns that crying can move the stated ending, they learn that the real rule is crying, not the clock. A fixed, predictable ending is easier to eventually accept than one that moves depending on how upset a child gets.",
      },
      {
        question: "What changes if the app says no instead of me?",
        answer:
          "When an app closes itself on a schedule rather than a parent closing it in the moment, the child has no one to negotiate with. CrenelView lets a parent choose which apps are blocked and set a schedule in advance, so the app enforces the ending rather than the parent doing it live.",
      },
      {
        question: "How do I end a session without a fight every time?",
        answer:
          "Set the ending before the session starts rather than during it, so it's a known fact the child can plan around. For planned exceptions, like a long car ride, a timed unlock with a real end point, such as CrenelView's Allow for a while feature, works better than an open-ended five more minutes.",
      },
      {
        question: "What do I do in the moment, once the crying has already started?",
        answer:
          "Avoid reopening the decision while a child is upset, since re-explaining or offering a shortened compromise can read as an opening rather than comfort. A short, calm acknowledgment without renegotiating tends to end the moment faster than a longer explanation.",
      },
    ],
  },
  {
    slug: "what-to-check-before-handing-your-child-your-phone",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/what-to-check-before-handing-your-child-your-phone.jpg",
    title: "What to Check Before Handing Your Child Your Phone",
    metaTitle: "What to Check Before Handing Over Your Phone",
    description:
      "Before you hand your child your phone, there are four quick things worth checking. Here's the short pre-handover checklist that actually prevents surprises.",
    date: "2026-09-14",
    dateModified: "2026-09-14",
    readTime: "4 min read",
    excerpt:
      "Before you hand your child your phone, there are four quick things worth checking. Here's the short pre-handover checklist that actually prevents surprises.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See the block list, schedule and PIN in the app",
    body: [
      {
        type: "p",
        text: "Before you hand your phone over, there are really only four things worth checking, and none of them take more than a few seconds once you've done it a couple of times. Skipping this step is usually what leads to the \"wait, how did they get back into that game\" moment ten minutes later - not a failure of the app, just a handover that happened before the setup did.",
      },
      { type: "h2", text: "Is my block list actually turned on?" },
      {
        type: "p",
        text: "A block list that's set up but toggled off does nothing. It happens more than you'd think - you turned it off last week to test something, or an update reset a toggle, and you've been handing over an unprotected phone without realizing it. Open CrenelView for two seconds before you hand the phone over and check the block is active, not just that the apps are still ticked.",
      },
      { type: "h2", text: "Does the schedule match what I'm about to allow right now?" },
      {
        type: "p",
        text: "If you've set a scheduled block window, say 8pm to 7am, check what time it actually is before handing the phone over outside those hours. A block list with no schedule attached is blocked all the time, which is simple but means every use needs its own decision from you. A scheduled one runs itself, but only if the hours actually match what you want to happen right now.",
      },
      { type: "h2", text: "Have I set a PIN so my child can't just switch it off?" },
      {
        type: "p",
        text: "A block list without a PIN behind it lasts exactly as long as it takes a child to find the settings screen and untick it. Most don't think to look, until the day they do. Setting a PIN once, at setup, means the block stays a block even if your child pokes around inside the app itself.",
      },
      {
        type: "cta",
        text: "Turn this into a two-minute setup, done once, not every handover.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Do I actually need a timed unlock instead of handing the phone over open-ended?" },
      {
        type: "p",
        text: "If the reason you're handing over the phone has a natural end, a long car ride, a call with a grandparent, decide the length before you hand it over rather than after. CrenelView's \"Allow for a while\" lets you pick 10, 15, 30 or 60 minutes up front, and it locks itself back down once the time's up, so you're not the one who has to notice and end it.",
      },
      { type: "h2", text: "Are there any new apps I haven't added to the block list yet?" },
      {
        type: "p",
        text: "If your phone recognizes a newly installed app as a game, CrenelView usually adds it to the block list automatically. Anything else, a chat app, a browser, a photo editor, still needs to be added by hand. Worth a quick glance at what's been installed since you last checked, especially after your child has been anywhere near the Play Store on their own.",
      },
      {
        type: "p",
        text: "Four checks, a few seconds each, and the handover stops being the risky part.",
      },
      {
        type: "cta",
        text: "Check your block list once, then hand over the phone without thinking twice.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is my block list actually turned on?",
        answer:
          "A block list that's set up but toggled off does nothing. Updates or earlier testing can leave it switched off without you noticing. Check that the block is active, not just that the apps are still ticked, before handing the phone over.",
      },
      {
        question: "Does the schedule match what I'm about to allow right now?",
        answer:
          "A scheduled block window, such as 8pm to 7am, only helps if the current time actually falls inside it. A list with no schedule is blocked at all times instead, which is simpler but means every use still needs a decision from you.",
      },
      {
        question: "Have I set a PIN so my child can't just switch it off?",
        answer:
          "Without a PIN, a block list lasts only as long as it takes a child to find the settings screen and untick it. Setting a PIN once at setup keeps the block in place even if a child pokes around inside the app.",
      },
      {
        question: "Do I actually need a timed unlock instead of handing the phone over open-ended?",
        answer:
          "If the handover has a natural end, like a car ride or a video call, deciding the length upfront works better than an open-ended handover. CrenelView's Allow for a while feature opens the blocked list for 10, 15, 30 or 60 minutes and locks itself back down automatically.",
      },
      {
        question: "Are there any new apps I haven't added to the block list yet?",
        answer:
          "CrenelView automatically adds newly installed apps that the phone recognizes as games. Anything else, like a chat app or browser, needs to be added by hand, so it's worth checking what's been installed recently.",
      },
    ],
  },
  {
    slug: "giving-child-phone-to-keep-quiet-screen-addiction",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/giving-child-phone-to-keep-quiet-screen-addiction.jpg",
    title: "Why Handing Your Child the Phone to Keep Them Quiet Backfires Later",
    metaTitle: "Giving Your Child the Phone to Stay Quiet?",
    description:
      "Handing your child the phone to stop the noise feels harmless once. Here's why it becomes screen addiction over time, and what to do differently.",
    date: "2026-09-14",
    dateModified: "2026-09-14",
    readTime: "6 min read",
    excerpt:
      "Handing your child the phone to stop the noise feels harmless once. Here's why it becomes screen addiction over time, and what to do differently.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how scheduled blocking removes the daily decision",
    body: [
      {
        type: "p",
        text: "Handing your child your phone to stop a meltdown at a restaurant, or to get five quiet minutes to cook dinner, isn't a parenting failure. It's one of the most common things Indian parents do, and it works, in the moment. The problem isn't the one time. It's what happens when the phone becomes the answer to every hard moment, not just this one.",
      },
      { type: "h2", text: "Why do so many parents hand over the phone just to stop the crying or noise?" },
      {
        type: "p",
        text: "Because it works instantly, and almost nothing else does. A toddler screaming in a restaurant, a five year old refusing to sit still on a long train journey, a two hour wait at a relative's house - a phone solves the immediate problem in about ten seconds, and there's genuinely no other tool that reliable in that exact moment. Most Indian households also don't have a separate device just for the child, so it's the parent's own phone that becomes the fix, every time the same situation comes up. None of that is a mistake in isolation. It becomes one only through repetition.",
      },
      { type: "h2", text: "Is handing over the phone every time really a bad habit, or is it fine occasionally?" },
      {
        type: "p",
        text: "Occasionally is fine. The actual issue is when the phone becomes the automatic answer to any friction at all - boredom, tiredness, a sibling fight, a long queue - rather than one tool among several. A child who gets the phone sometimes, for specific reasons, learns that boredom is something you sit through. A child who gets it every time there's friction learns that discomfort has exactly one solution, and stops building any other way to handle it.",
      },
      { type: "h2", text: "What does screen addiction actually look like in a young child?" },
      {
        type: "p",
        text: "It's rarely about the number of hours. The clearer signs sit in how a child reacts to the screen not being there: a meltdown that's bigger than the situation calls for, asking for the phone the moment there's nothing else to do, or treating every handover as a negotiation rather than a request. If taking the phone away has become the hardest part of your day, harder than anything about the content itself, that's usually the real signal, not a stopwatch.",
      },
      {
        type: "cta",
        text: "Set one automatic boundary today instead of deciding it fresh every time.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Why does it get harder to say no the longer this goes on?" },
      {
        type: "p",
        text: "Because the child hasn't just learned to like the phone, they've learned that it's the reliable fix for being upset. Every time handing it over works, that link gets a little stronger, and every time it's refused, the reaction gets a little bigger, because there's no backup coping method sitting underneath it. This is why parents who've been doing this for a couple of years often describe the fights as getting worse, not better, even though nothing about their own behavior has changed. The habit is doing exactly what habits do: getting more automatic with repetition.",
      },
      { type: "h2", text: "What can I do differently starting today, if I've already been doing this?" },
      {
        type: "p",
        text: "Not a sudden, total ban - that rarely holds, and it turns every day into a fight you're unlikely to win consistently. A more workable first step is making the limit automatic instead of a decision you have to make and defend every single time. A scheduled block, where the phone's video and game apps close on their own after a set time each evening, takes you out of the loop as the one saying no in the moment. CrenelView works this way: you set the apps and the hours once, and the phone enforces it after that, instead of you renegotiating the same boundary every day.",
      },
      { type: "h2", text: "Is it too late if my child already reacts badly to any \"no\"?" },
      {
        type: "p",
        text: "No. The reaction gets worse the longer the phone stays the only coping tool, but that doesn't mean the pattern can't change, it just means the first week or two of a new limit will likely be harder than it would have been a year ago. Consistency matters more than the exact rule you pick. A boundary that holds every day, even a generous one, teaches something a stricter rule that gets abandoned after three days of crying never will.",
      },
      {
        type: "p",
        text: "Pick one boundary, make it automatic, and hold it for a week before judging whether it worked.",
      },
      {
        type: "cta",
        text: "Make the limit automatic, starting with tonight's handover.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why do so many parents hand over the phone just to stop the crying or noise?",
        answer:
          "Because it works instantly and almost nothing else does in that exact moment. Most Indian households don't have a separate device for the child, so the parent's own phone becomes the fix every time the same situation comes up. That isn't a mistake in isolation, it becomes one through repetition.",
      },
      {
        question: "Is handing over the phone every time really a bad habit, or is it fine occasionally?",
        answer:
          "Occasionally is fine. The issue is when the phone becomes the automatic answer to any friction at all, rather than one option among several, since that teaches a child that discomfort has exactly one solution.",
      },
      {
        question: "What does screen addiction actually look like in a young child?",
        answer:
          "It shows up less in total hours and more in how a child reacts when the screen isn't there: outsized meltdowns, asking for the phone the moment there's nothing else to do, or treating every handover as a negotiation.",
      },
      {
        question: "Why does it get harder to say no the longer this goes on?",
        answer:
          "Every time handing over the phone works, the link between being upset and getting the phone gets stronger, and every refusal produces a bigger reaction because there's no other coping method underneath it.",
      },
      {
        question: "What can I do differently starting today, if I've already been doing this?",
        answer:
          "A sudden total ban rarely holds. A more workable step is making the limit automatic, such as a scheduled block that closes video and game apps at a set time each evening, so the boundary doesn't need to be renegotiated every day.",
      },
      {
        question: "Is it too late if my child already reacts badly to any no?",
        answer:
          "No. The pattern can still change, though the first week or two of a new limit will likely be harder than it would have been earlier. Consistency in holding the boundary matters more than exactly how strict it is.",
      },
    ],
  },
  {
    slug: "why-blocked-app-reopens-xiaomi-realme-oppo-vivo",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/why-blocked-app-reopens-xiaomi-realme-oppo-vivo.jpg",
    title: "Why a Blocked App Might Reopen on Its Own on an Android Phone",
    metaTitle: "Why a Blocked App Reopens on Xiaomi or Oppo",
    description:
      "If an app you blocked keeps reopening, it's usually battery optimization on Xiaomi, Realme, Oppo, or Vivo, not a broken block. Here's why, and how to fix it.",
    date: "2026-09-14",
    dateModified: "2026-09-14",
    readTime: "5 min read",
    excerpt:
      "If an app you blocked keeps reopening, it's usually battery optimization on Xiaomi, Realme, Oppo, or Vivo, not a broken block. Here's why, and how to fix it.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See the built-in check that tells you if blocking is active",
    shortAnswer: [
      "Usually battery optimization killing the block in the background, not a broken app",
      "Xiaomi (MIUI), Realme, Oppo, and Vivo are the most aggressive about this",
      "Fix is normally two settings: battery optimization exemption and autostart",
      "A phone update can quietly reset both without telling you",
    ],
    body: [
      {
        type: "p",
        text: "If an app you blocked is back within a day, the block probably didn't fail. Most Android phones, especially from Xiaomi, Realme, Oppo, and Vivo, aggressively kill background apps to save battery, and any blocking tool that isn't specifically exempted from that can get shut down without you knowing.",
      },
      { type: "h2", text: "Why does a blocked app work for a while and then start opening again?" },
      {
        type: "p",
        text: "Because Android phones, especially ones running heavily customized software, treat a blocking tool's background service as something safe to kill once the phone decides it needs to save power. Nothing about the block itself failed. The process that enforces it just got shut down quietly, usually after the phone's been idle for a while or the battery dropped below a certain point. From the outside it looks identical to the block simply not working, which is why most parents assume the app is broken rather than the phone's own power management doing exactly what it was built to do.",
      },
      { type: "h2", text: "Which phones are most likely to do this?" },
      {
        type: "p",
        text: "Xiaomi's MIUI, Realme's software, Oppo's ColorOS, and Vivo's Funtouch OS are the most aggressive about it, because each brand runs its own battery management layer on top of Android that goes further than stock Android does. Samsung and stock Android phones, like Pixel, do less of this by default, though even those can still kill a background app under a strict battery saver mode. If the same block keeps failing on one phone in the house but never on another, the phone's brand is usually the actual variable, not the app.",
      },
      { type: "h2", text: "How do I check whether this is actually happening on my phone?" },
      {
        type: "p",
        text: "Open your phone's battery settings and look for the app under something like \"battery optimization,\" \"app battery usage,\" or \"background restrictions,\" depending on the brand. If it isn't already set to \"unrestricted\" or \"no restrictions,\" that's the likely cause. A separate setting, usually called \"autostart\" or \"auto-launch\" and tucked into app permissions or a \"manage apps\" section, needs to be turned on too, since some phones kill an app on restart unless it's specifically allowed to start itself back up. Checking this by hand every few weeks is tedious, which is exactly the gap a built-in self-check closes: CrenelView shows directly on its own screen whether blocking is actually active on your specific phone, instead of leaving you to dig through settings to find out.",
      },
      {
        type: "cta",
        text: "See at a glance whether your block is actually active right now.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How do I fix it once I know which setting is the problem?" },
      {
        type: "p",
        text: "Set battery optimization for the app to \"don't optimize\" or \"unrestricted,\" and turn autostart on if your phone has that setting. On most phones both live inside Settings, then Apps, then the specific app, though the exact wording and menu depth varies by brand. Restart the phone once after changing both, since some manufacturers only apply the new permission cleanly after a restart rather than immediately.",
      },
      { type: "h2", text: "Is there a way to know for sure the block is still active, without checking settings every time?" },
      {
        type: "p",
        text: "Worth looking for a blocking tool that tells you this directly rather than assuming your settings will hold forever, since a routine phone update can quietly reset both permissions without any notice. CrenelView runs that check inside the app itself and tells you plainly whether blocking is active on your specific phone right now, rather than leaving you to find out only when your child is already back in a game that should have stayed closed.",
      },
      {
        type: "p",
        text: "Check autostart and battery optimization once after any phone update, since that's usually when a working block quietly stops.",
      },
      {
        type: "cta",
        text: "Get a block that tells you when a phone setting breaks it.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does a blocked app work for a while and then start opening again?",
        answer:
          "Android phones, especially with heavily customized software, treat a blocking tool's background service as safe to kill once the phone wants to save power. The block itself didn't fail, the process enforcing it was shut down quietly, which looks identical to the block simply not working.",
      },
      {
        question: "Which phones are most likely to do this?",
        answer:
          "Xiaomi's MIUI, Realme's software, Oppo's ColorOS, and Vivo's Funtouch OS are the most aggressive, since each runs its own battery management layer beyond stock Android. Samsung and stock Android phones do less of this by default.",
      },
      {
        question: "How do I check whether this is actually happening on my phone?",
        answer:
          "Check the app's battery optimization setting, usually under battery or app battery usage, and set it to unrestricted. Also check for an autostart or auto-launch permission, since some phones kill an app on restart unless it's allowed to start itself back up.",
      },
      {
        question: "How do I fix it once I know which setting is the problem?",
        answer:
          "Set battery optimization to don't optimize or unrestricted, turn on autostart if the phone has that setting, both usually found under Settings, then Apps, then the specific app, and restart the phone once afterward.",
      },
      {
        question: "Is there a way to know for sure the block is still active, without checking settings every time?",
        answer:
          "A blocking tool that checks this directly is worth choosing, since a routine phone update can reset both permissions without notice. CrenelView runs this check inside the app and shows plainly whether blocking is active on the specific phone.",
      },
    ],
  },
  {
    slug: "fantasy-sports-apps-real-money-risk-kids-phone",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/fantasy-sports-apps-real-money-risk-kids-phone.jpg",
    title: "Why Fantasy Sports Apps Like Dream11 Are a Real Risk on a Child's Phone",
    metaTitle: "Why Fantasy Sports Apps Are Risky for Kids",
    description:
      "Dream11 and similar fantasy sports apps involve real money, not just screen time. Here's why that matters on a shared phone, and how to block them properly.",
    date: "2026-09-15",
    dateModified: "2026-09-15",
    readTime: "5 min read",
    excerpt:
      "Dream11 and similar fantasy sports apps involve real money, not just screen time. Here's why that matters on a shared phone, and how to block them properly.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how to block a specific app by name",
    body: [
      {
        type: "p",
        text: "A fantasy sports app is a different kind of risk than a game or a video app, because it isn't really about screen time at all. If a child opens an app like Dream11 while it's already logged into a parent's account, they aren't just watching something they shouldn't. They can potentially join a paid contest or use money sitting in an in-app wallet, without anyone finding out until later.",
      },
      { type: "h2", text: "Why is a fantasy sports app a different kind of risk than a game?" },
      {
        type: "p",
        text: "Because real money moves inside it. These apps operate as skill-based contests rather than straightforward gambling under Indian law, which is why they're legal and heavily advertised, but the mechanics still involve depositing money, either directly or through an in-app wallet, and entering paid contests with it. A game your child shouldn't be playing is a content problem. An app where money can move is a financial one, and it behaves completely differently if a child gets into it by accident.",
      },
      { type: "h2", text: "Isn't there an age requirement to sign up?" },
      {
        type: "p",
        text: "On paper, yes. Most fantasy sports platforms in India require a user to be 18 or older to create an account. In practice, that requirement only matters at sign-up. If the account already belongs to a parent and is already logged in on the phone, a child doesn't need to pass any age check at all, they're simply using an account that's already past that gate. The real risk almost never looks like a child signing up for their own account. It looks like a child using yours, already open.",
      },
      { type: "h2", text: "What does it actually look like when this goes wrong?" },
      {
        type: "p",
        text: "Usually something like this: the phone gets handed over for a video or a game, the child taps around while looking for something else, finds the fantasy sports app already sitting logged in from earlier, and joins a contest or two out of curiosity, sometimes without fully realizing money was involved. The parent finds out later from a notification or a bank statement, not in the moment, which is what makes it feel worse than a typical screen time slip. Nobody made a deliberate decision. A tap happened somewhere an account was already open.",
      },
      { type: "h2", text: "Does turning off notifications or hiding the app icon help?" },
      {
        type: "p",
        text: "Not really. Hiding an icon from the home screen doesn't stop the app from working, it just changes where a curious child looks for it, and most kids find an app in the full app drawer within a few minutes if they're determined. Turning off notifications stops a parent from seeing activity as it happens, but does nothing to prevent the activity itself. Neither approach touches the actual problem, which is that the app can still be opened and used at all.",
      },
      {
        type: "cta",
        text: "Block a fantasy sports app by name in under a minute.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How do I stop the app from opening entirely?" },
      {
        type: "p",
        text: "Block it. CrenelView lets you pick specific apps already installed on your phone, including a fantasy sports app by name, and once it's on the list, it closes the moment anyone taps it, the same way any other blocked app would. That removes the accidental-tap scenario completely, since there's no version of \"found it while looking for something else\" that ends in the app actually opening.",
      },
      { type: "h2", text: "Will a new fantasy sports app get added to my block list automatically?" },
      {
        type: "p",
        text: "Usually not on its own. Blocking tools that auto-add new installs, including this one, generally do it by checking whether the Play Store lists an app under the Games category, and most fantasy sports and real-money apps are listed under Sports instead, so they slip past that automatic check. Worth adding these by name deliberately rather than assuming they'll be swept in the way a new game would be, especially once a child has started following cricket or other sports closely, since that's usually when these apps start showing up.",
      },
      {
        type: "p",
        text: "Check for real-money apps the same way you'd check for a new game, since the Play Store won't flag it the way it flags one.",
      },
      {
        type: "cta",
        text: "Add real-money apps to your block list before the next handover.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why is a fantasy sports app a different kind of risk than a game?",
        answer:
          "Real money moves inside these apps through deposits or an in-app wallet used to enter paid contests. A game a child shouldn't be playing is a content problem; an app where money can move is a financial one, and it behaves differently if a child gets into it by accident.",
      },
      {
        question: "Isn't there an age requirement to sign up?",
        answer:
          "Most fantasy sports platforms in India require users to be 18 or older to create an account, but that check only applies at sign-up. If a parent's account is already logged in on the phone, a child using it bypasses that check entirely.",
      },
      {
        question: "What does it actually look like when this goes wrong?",
        answer:
          "A child handed the phone for something else finds a fantasy sports app already logged in, taps around, and joins a contest without fully realizing money was involved. The parent usually finds out later from a notification or bank statement, not in the moment.",
      },
      {
        question: "Does turning off notifications or hiding the app icon help?",
        answer:
          "Not really. Hiding an icon doesn't stop the app from working, and most children find it in the app drawer anyway. Turning off notifications only hides activity from the parent; it doesn't prevent the activity itself.",
      },
      {
        question: "How do I stop the app from opening entirely?",
        answer:
          "Block it directly. CrenelView lets a parent pick a specific installed app, including a fantasy sports app by name, and it closes the moment anyone taps it, removing the accidental-access scenario entirely.",
      },
      {
        question: "Will a new fantasy sports app get added to my block list automatically?",
        answer:
          "Usually not automatically, since most auto-block-new-installs features check for the Play Store's Games category, and fantasy sports apps are typically listed under Sports instead. These need to be added to a block list by name deliberately.",
      },
    ],
  },
  {
    slug: "screen-pinning-vs-app-blocker-shared-phone",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/screen-pinning-vs-app-blocker-shared-phone.jpg",
    title: "Screen Pinning vs an App Blocker: What Actually Covers a Shared Phone",
    metaTitle: "Screen Pinning vs an App Blocker",
    description:
      "Screen pinning is free and useful for a two-minute handover, but it isn't standing protection. Here's what it actually covers, and when you need more.",
    date: "2026-09-15",
    dateModified: "2026-09-15",
    readTime: "4 min read",
    excerpt:
      "Screen pinning is free and useful for a two-minute handover, but it isn't standing protection. Here's what it actually covers, and when you need more.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how a standing block list works",
    body: [
      {
        type: "p",
        text: "Screen pinning is free, already on your phone, and works well for exactly one situation: handing the phone over for a minute or two while you're standing right there. It isn't built to hold up for anything longer or unsupervised, and that's not a flaw, it's just not what it was designed for.",
      },
      { type: "h2", text: "What is screen pinning, exactly?" },
      {
        type: "p",
        text: "It's a built-in Android feature, available since Android 5.0, that locks your phone to whichever single app is open until you unpin it. Once pinned, the home button, recent apps, and notifications are disabled, so whoever's holding the phone can only interact with that one app, nothing else. Turning it on takes a couple of taps in the recent apps view, and turning it off needs your PIN or pattern, the same one that unlocks your phone.",
      },
      { type: "h2", text: "What is screen pinning actually good for?" },
      {
        type: "p",
        text: "Handing your phone to a toddler to watch one video while you finish cooking. Letting a child play a single game for a few minutes on a car ride while you're in the front seat. Showing a photo or a form to someone and knowing they can't accidentally swipe into your messages while you're both looking at the screen. It's genuinely useful for short, supervised handovers where the goal is just keeping one person inside one app for a few minutes, nothing more elaborate than that.",
      },
      { type: "h2", text: "Where does it fall short for a longer or unsupervised handover?" },
      {
        type: "p",
        text: "Two places. First, it has to be turned on manually, every single time, which means it only protects the handovers you remember to set up, not the ones that happen quickly or when you're distracted. Second, once a child has watched you enter your PIN to unpin it a couple of times, they've usually memorized it, and at that point pinning stops being a barrier at all, since anyone who can unpin it can also just exit whenever they want. It was built for a single supervised session, not for standing protection that holds up across a whole day.",
      },
      {
        type: "cta",
        text: "See what standing protection looks like, not just a one-session pin.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So when do I actually need something more than screen pinning?" },
      {
        type: "p",
        text: "When the handover is longer than a couple of minutes, when you're not in the room the whole time, or when the same limit needs to apply every day without you setting it up fresh each time, bedtime, mealtimes, a long commute. That's a different job: not locking someone into one app for one session, but keeping certain apps closed by default, every time, until you decide otherwise. CrenelView is built for that version of the problem: you choose which apps stay blocked, set a schedule if you want one, and it holds without you doing anything each time the phone changes hands.",
      },
      { type: "h2", text: "Can I use both together?" },
      {
        type: "p",
        text: "Yes, and it's a reasonable combination. Keep the apps you don't want opened at all on a standing block list, so that part doesn't depend on you remembering anything. Save screen pinning for the occasional moment outside that pattern, a relative wants to see a video, someone needs to use your phone briefly, where you want them locked to exactly one app for exactly one short session, with you standing right there the whole time.",
      },
      {
        type: "p",
        text: "Use the block list for every day. Save pinning for the one-off moments you're standing right next to.",
      },
      {
        type: "cta",
        text: "Set a block list that holds every day, not just this one handover.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is screen pinning, exactly?",
        answer:
          "A built-in Android feature that locks the phone to a single open app until it's unpinned with the device PIN or pattern, disabling the home button, recent apps, and notifications while active.",
      },
      {
        question: "What is screen pinning actually good for?",
        answer:
          "Short, supervised handovers, such as letting a child watch one video or play one game for a few minutes while a parent is right there, or showing something to someone without exposing the rest of the phone.",
      },
      {
        question: "Where does it fall short for a longer or unsupervised handover?",
        answer:
          "It has to be turned on manually every time, so it only protects handovers a parent remembers to set up, and a child who has watched the unpin PIN entered a few times can usually exit it on their own after that.",
      },
      {
        question: "So when do I actually need something more than screen pinning?",
        answer:
          "When a handover runs longer than a couple of minutes, happens unsupervised, or needs to repeat daily without being set up fresh each time. CrenelView addresses that by letting a parent set which apps stay blocked and on what schedule, holding automatically without repeating the setup.",
      },
      {
        question: "Can I use both together?",
        answer:
          "Yes. A standing block list can cover apps that should never open, while screen pinning still works well for the occasional one-off moment someone else needs the phone briefly under direct supervision.",
      },
    ],
  },
  {
    slug: "who-crenelview-is-built-for",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/who-crenelview-is-built-for.jpg",
    title: "Who CrenelView Is Built For, and Who Should Look Elsewhere",
    metaTitle: "Who CrenelView Is Built For",
    description:
      "CrenelView blocks apps on one shared Android phone with a single schedule for the whole list. Here's exactly who that fits, and who it doesn't.",
    date: "2026-09-16",
    dateModified: "2026-09-16",
    readTime: "4 min read",
    excerpt:
      "CrenelView blocks apps on one shared Android phone with a single schedule for the whole list. Here's exactly who that fits, and who it doesn't.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See the full setup walkthrough",
    shortAnswer: [
      "Built for one Android phone shared between a parent and a young child who has no device of their own",
      "One schedule covers the whole block list; there's no separate timer per app",
      "Setup requires turning on Android's Accessibility Service by hand, once",
      "Not built for a child's own phone or tablet, and not available on iPhone",
    ],
    body: [
      {
        type: "p",
        text: "CrenelView is built for one specific situation: a young child who doesn't have a phone of their own, using a parent's Android phone instead. That's a narrower job than most parental-control apps try to do, on purpose, and it means the app is a clear fit for some households and the wrong tool for others.",
      },
      { type: "h2", text: "Is this the right tool if my child has a phone or tablet of their own?" },
      {
        type: "p",
        text: "No. CrenelView only runs on and protects the single phone it's installed on. There's no remote control from a second device, no syncing to a child's own phone or tablet, and no dashboard a parent checks from elsewhere. If a child already has their own device, what's needed is something built for exactly that, most likely Google's own Family Link, which assumes a child's device and account from the start. CrenelView assumes the opposite: no child device, no child account, one shared phone.",
      },
      { type: "h2", text: "Can I set different time limits for different apps?" },
      {
        type: "p",
        text: "No. Blocking mode is one setting for the entire block list, not per app: every ticked app is either blocked all the time, or blocked only during hours a parent sets, like 20:00 to 07:00. There's no way to give a video app a shorter daily allowance than a game, or vice versa. That's a real limitation if what's wanted is a granular, per-app budget. It's also, for a lot of households, a reasonable trade: one rule to set and remember, instead of separate timers to track for every app.",
      },
      { type: "h2", text: "Why does setup ask me to turn on an accessibility service?" },
      {
        type: "p",
        text: "Because that's the actual mechanism that makes closing a blocked app possible. Android doesn't allow an app to detect when another app opens and close it again without the accessibility service switched on explicitly, by the phone's owner, in Settings. It's a standard, documented Android capability, not a hidden or unusual permission, and it's a one-time step during setup, not something repeated. It's also the reason a self-check exists inside the app afterward: since this is a manual switch, it's worth being able to confirm it's still on rather than assuming it stayed that way through every phone update.",
      },
      {
        type: "cta",
        text: "See the full setup, including the one manual step, before installing.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does it work on iPhone?" },
      {
        type: "p",
        text: "No. CrenelView is an Android app, distributed through the Google Play Store. There's no iPhone or iOS version.",
      },
      { type: "h2", text: "So who is it actually right for?" },
      {
        type: "p",
        text: "A parent whose young child doesn't have a phone of their own and uses the parent's phone instead, which is a common setup, not an edge case, especially for children too young to have their own device. For that specific situation, CrenelView's narrower scope is the point: one phone, one block list, one schedule, set up once and left alone rather than checked from anywhere else.",
      },
      { type: "h2", text: "Why is there no account, cloud dashboard, or remote control?" },
      {
        type: "p",
        text: "Because none of that data ever leaves the phone. CrenelView has no INTERNET permission at all, which means it's not technically capable of sending anything off the device, no account, no server, no sync. That's the direct trade-off for being single-device-only: everything the app can see stays exactly where it is.",
      },
      {
        type: "p",
        text: "CrenelView isn't trying to cover every household's setup. It's built narrowly for one: a shared Android phone, one schedule, nothing leaving the device.",
      },
      {
        type: "cta",
        text: "If this is your setup, this is built for it.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is CrenelView the right tool if my child has a phone or tablet of their own?",
        answer:
          "No. CrenelView only protects the single phone it's installed on, with no remote control or syncing to a second device. A child with their own phone needs a tool built for that, such as Google's Family Link.",
      },
      {
        question: "Can I set different time limits for different apps in CrenelView?",
        answer:
          "No. One schedule applies to the entire block list rather than per app: every blocked app is either always blocked or blocked only during set hours, with no way to give individual apps different limits.",
      },
      {
        question: "Why does CrenelView ask me to turn on an accessibility service?",
        answer:
          "Android's Accessibility Service is the mechanism that lets an app detect when a blocked app opens and close it again. It's a standard Android capability that has to be switched on manually in Settings, once, during setup.",
      },
      {
        question: "Does CrenelView work on iPhone?",
        answer:
          "No. CrenelView is an Android app distributed through Google Play, with no iPhone or iOS version.",
      },
      {
        question: "Who is CrenelView actually right for?",
        answer:
          "A parent whose young child doesn't have a phone of their own and shares the parent's Android phone instead, where one block list and one schedule is enough.",
      },
      {
        question: "Why does CrenelView have no account, cloud dashboard, or remote control?",
        answer:
          "CrenelView has no INTERNET permission at all, so it cannot send data off the device. That is the direct trade-off for being a single-device, on-device only app.",
      },
    ],
  },
  {
    slug: "relative-hands-child-their-phone",
    tags: ["Family & Relatives"],
    ogImage: "/blog-images/relative-hands-child-their-phone.jpg",
    title: "What to Do When a Relative Hands Your Child Their Own Phone",
    metaTitle: "When a Relative Hands Your Child Their Phone",
    description:
      "A grandparent's or relative's phone rarely has the boundaries yours does. Here's why it happens so often, and what to do instead at the next gathering.",
    date: "2026-09-16",
    dateModified: "2026-09-16",
    readTime: "4 min read",
    excerpt:
      "A grandparent's or relative's phone rarely has the boundaries yours does. Here's why it happens so often, and what to do instead at the next gathering.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how to set up a block list once, on your own phone",
    body: [
      {
        type: "p",
        text: "A grandparent's or relative's phone at a family gathering usually has none of the boundaries you've set on your own. It isn't anyone's fault exactly, it's just a phone that was never set up with your child in mind, handed over in a moment of affection or to buy five minutes of quiet.",
      },
      { type: "h2", text: "Why does this even happen so often?" },
      {
        type: "p",
        text: "Because it's rarely a decision, it's a reflex. A child is bored at a gathering, a relative wants to make them happy or just wants a few minutes of peace, and a phone is the fastest way to do both. Nobody sits down beforehand and thinks through what's on that phone, what's logged in, or what the child might tap into. It happens at almost every family gathering with young kids around, and it's less about anyone doing something wrong and more about nobody having thought to do anything differently.",
      },
      { type: "h2", text: "Is it actually a real risk, or am I overreacting?" },
      {
        type: "p",
        text: "It depends on the phone and the relative, but it's worth taking seriously rather than waving it off as \"just a few minutes.\" A phone that isn't yours usually has social media and messaging apps already logged in, sometimes payment apps too, and none of the limits you've built into your own routine. A few minutes on your own phone, set up the way you want it, is a very different situation from a few minutes on a phone where nothing has been thought through at all.",
      },
      { type: "h2", text: "What can I actually control here, since it isn't my phone?" },
      {
        type: "p",
        text: "Less than you'd think, and that's worth saying plainly rather than glossing over. A blocking tool, CrenelView included, only protects the phone it's installed on. There's no way to reach into a relative's phone from your own and apply your rules to it. No blocking tool offers remote control over a device it isn't installed on, this one included. Anything that actually helps in this situation is about the conversation and the alternative you offer, not a setting you can flip from a distance.",
      },
      {
        type: "cta",
        text: "Set your own phone up once, so it's always the safe option to hand over.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What can I ask a relative to do, without it being awkward?" },
      {
        type: "p",
        text: "Keep it specific and small rather than asking someone to change their whole phone. \"Can you open this one video for her, and I'll take it from there\" works better than a general request to be careful. With relatives you see often, a quick conversation ahead of time, \"she tends to wander into things she shouldn't if the phone's just handed over, so maybe we watch something together instead,\" tends to land better than a request made in the moment, when everyone's already distracted by the gathering itself.",
      },
      { type: "h2", text: "What can I hand my child instead during the visit?" },
      {
        type: "p",
        text: "Your own phone, already set up the way you want it, is usually the simplest answer, since there's nothing extra on it to worry about handing over. A show downloaded ahead of time, a specific game your child already knows, or just a small task, helping carry snacks, sitting with a cousin, covers a lot of the moments that would otherwise end in someone else's unmanaged phone. The goal isn't to prevent every single handover everywhere. It's making sure the easiest option in the moment is one you've already thought through.",
      },
      {
        type: "p",
        text: "Before the next gathering, decide what you'll hand over instead, so it's ready before anyone else's phone becomes the easy option.",
      },
      {
        type: "cta",
        text: "Make your phone the easy, already-blocked choice at the next gathering.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does this even happen so often?",
        answer:
          "It's rarely a decision, it's a reflex: a bored child, a relative wanting to help or get a moment of peace, and a phone being the fastest way to do both, with nobody thinking through what's on that phone beforehand.",
      },
      {
        question: "Is it actually a real risk, or am I overreacting?",
        answer:
          "It depends on the phone and the relative, but it's worth taking seriously, since an unmanaged phone usually has social media, messaging, and sometimes payment apps already logged in, with none of the limits a parent has built into their own routine.",
      },
      {
        question: "What can I actually control here, since it isn't my phone?",
        answer:
          "Less than expected. A blocking tool, CrenelView included, only protects the phone it's installed on, with no way to apply the same rules to a phone it isn't on. What helps here is the conversation and the alternative offered, not a setting.",
      },
      {
        question: "What can I ask a relative to do, without it being awkward?",
        answer:
          "Keep the ask specific and small, like opening one particular video rather than asking someone to change their whole phone, and raise it ahead of time with relatives seen often rather than in the moment.",
      },
      {
        question: "What can I hand my child instead during the visit?",
        answer:
          "A parent's own already set-up phone, a show downloaded ahead of time, a familiar game, or a small task all work as ready alternatives that don't depend on someone else's unmanaged phone.",
      },
    ],
  },
  {
    slug: "how-to-tell-parental-control-app-from-spyware",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/how-to-tell-parental-control-app-from-spyware.jpg",
    title: "How to Tell a Real Parental Control App From Spyware",
    metaTitle: "How to Spot Spyware vs Parental Control Apps",
    description:
      "Not every app calling itself parental control is safe. Here's how to check permissions and spot the difference before installing one.",
    date: "2026-09-17",
    dateModified: "2026-09-17",
    readTime: "4 min read",
    excerpt:
      "Not every app calling itself parental control is safe. Here's how to check permissions and spot the difference before installing one.",
    relatedProductPath: "/",
    relatedProductLabel: "Check CrenelView's own permissions and privacy details",
    shortAnswer: [
      "Legitimate tools run visibly: an icon, a settings screen, a plainly listed app",
      "Several sensitive permissions bundled together (calls, SMS, camera, microphone, location) is a red flag, not a feature",
      "Check whether the app needs internet access at all, in the phone's own permissions settings",
      "A privacy policy and a visible Play Store listing are a starting point, not the whole answer",
    ],
    body: [
      {
        type: "p",
        text: "Not every app that calls itself \"parental control\" is actually built for a parent to use openly. Some are designed to run hidden, without the phone's own owner easily finding them, and that difference matters more than any feature list.",
      },
      { type: "h2", text: "What makes an app \"spyware\" instead of legitimate parental control?" },
      {
        type: "p",
        text: "The difference isn't the intent behind the marketing, it's whether the app operates openly on the phone it's installed on. A legitimate blocking tool shows up as an app with an icon, a settings screen, and visible effects, an app closes, a phone returns to the home screen, and anyone using that phone can tell something changed. A covert monitoring app is built the opposite way: no visible icon, disguised under a generic name, running in the background collecting information without anyone holding the phone being able to tell. That distinction, hidden versus visible, is closer to how security researchers actually classify these apps than the label on the app store listing.",
      },
      { type: "h2", text: "What permissions should make me pause before installing?" },
      {
        type: "p",
        text: "Any single permission can have a legitimate reason. The pattern worth noticing is several sensitive permissions requested together for a tool that's supposedly about screen time or app access: call log access, SMS reading, camera, microphone, and precise location, bundled into one install. A tool built to block or schedule apps doesn't need to read your messages or turn on your microphone to do that job. If an app's permission list reads more like a wiretap than a blocking tool, that's worth stopping on before installing, not after.",
      },
      { type: "h2", text: "Does the app need an internet connection to work?" },
      {
        type: "p",
        text: "This is one of the most useful, checkable questions, and most people never ask it. If an app sends anything to a website checked from another device, a remote report, or an alert emailed out, it needs internet access and somewhere for that data to go, which means the data leaves the phone and sits on a server somewhere. That's not automatically dangerous, but it is a real exposure point, since breaches happen to servers, not to phones sitting in someone's pocket. An app with no internet permission at all is not technically capable of sending anything anywhere, which is checkable directly in the phone's own settings, not something to take on faith from a marketing page. CrenelView is built this way specifically: no internet permission, so there's nothing to breach, because there's no channel for anything to leave the phone.",
      },
      {
        type: "cta",
        text: "Check the permissions yourself before installing anything on your child's phone.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can I check this myself, or do I have to trust the marketing?" },
      {
        type: "p",
        text: "You can check it yourself, and it takes about a minute. On Android, go to Settings, then Apps, tap the app in question, and open its Permissions list. Look for anything unusual for what the app claims to do, and specifically check whether internet or network access is listed at all. The Play Store's own Data Safety section, visible on any app's store listing, also states what data an app says it collects and whether it's shared, though that's self-reported by the developer, so it's a starting point rather than a guarantee.",
      },
      { type: "h2", text: "Why do some apps hide their icon or disguise themselves?" },
      {
        type: "p",
        text: "Because being visible is the whole risk for that kind of app: if the phone's owner can see it, they can uninstall it. Security researchers have flagged a real, documented category of apps that market themselves as parental control but are built to run hidden, without an icon, under a disguised name, collecting far more than screen time data. Multiple apps in that category have had serious data breaches over the past few years, exposing exactly the kind of information they were collecting. A legitimate tool doesn't need to hide from the person whose phone it's on, since it's the phone's owner installing it themselves, not sneaking it onto someone else's device.",
      },
      {
        type: "p",
        text: "Before installing anything that touches your child's screen time, spend the one minute checking its permissions list. It tells you more than the app description does.",
      },
      {
        type: "cta",
        text: "A tool with no internet permission has nothing to leak. See how that works.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What makes an app spyware instead of legitimate parental control?",
        answer:
          "The difference is whether the app operates openly on the phone it's installed on. A legitimate tool has a visible icon and settings screen with visible effects; a covert monitoring app hides its icon and runs in the background without anyone holding the phone being able to tell.",
      },
      {
        question: "What permissions should make me pause before installing a parental control app?",
        answer:
          "Several sensitive permissions bundled together for a screen-time tool, such as call logs, SMS reading, camera, microphone, and precise location, is a pattern worth stopping on. A blocking tool doesn't need to read messages or access the microphone to close an app.",
      },
      {
        question: "Does a parental control app need an internet connection to work?",
        answer:
          "Only if it sends data to a remote server or report, which means that data leaves the phone and sits on a server that could be breached. An app with no internet permission at all cannot send anything anywhere, which is checkable directly in the phone's settings.",
      },
      {
        question: "Can I check a parental control app's safety myself?",
        answer:
          "Yes. On Android, check Settings, then Apps, then the app's Permissions list, looking specifically for internet or network access. The Play Store's Data Safety section is a useful starting point too, though it's self-reported by the developer.",
      },
      {
        question: "Why do some parental control apps hide their icon or disguise themselves?",
        answer:
          "Being visible is the main risk for that kind of app, since a visible app can be uninstalled by the phone's owner. Security researchers have documented a category of apps that market themselves as parental control but run hidden and have had serious data breaches.",
      },
    ],
  },
  {
    slug: "screen-time-by-age-2026-guidelines",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/screen-time-by-age-2026-guidelines.jpg",
    title: "How Much Screen Time Is Right for Your Child? What Changed in 2026",
    metaTitle: "Screen Time by Age: 2026 Guidelines",
    description:
      "The AAP retired its old two-hour screen time rule in 2026. Here's the actual guidance by age now, and what changed for kids six and older.",
    date: "2026-09-17",
    dateModified: "2026-09-17",
    readTime: "5 min read",
    excerpt:
      "The AAP retired its old two-hour screen time rule in 2026. Here's the actual guidance by age now, and what changed for kids six and older.",
    relatedProductPath: "/",
    relatedProductLabel: "See how a scheduled evening cutoff works",
    body: [
      {
        type: "p",
        text: "For years, \"two hours or less\" was the number every parent had heard, whether or not they followed it. In early 2026, the American Academy of Pediatrics quietly retired that blanket rule for children six and older, replacing a single number with a framework built around quality and context instead. The guidance for the youngest children hasn't changed. What changed is what \"the right amount\" means for everyone older than that.",
      },
      {
        type: "table",
        headers: ["Age", "Current guidance"],
        rows: [
          ["Under 18 months", "Screens only for video calls with family (AAP); WHO recommends no sedentary screen time before age 1"],
          ["18-24 months", "Introduce only high-quality content slowly, ideally watched together with a parent"],
          ["2-5 years", "About 1 hour a day of high-quality, co-viewed content (AAP); WHO: no more than 1 hour, less is better"],
          ["6 years and up", "No single hour number since 2026 - a consistent, family-specific plan built around content quality, calm, what it's crowding out, and ongoing conversation (AAP's \"5 C's\" framework)"],
        ],
      },
      { type: "h2", text: "What's the actual guidance for a child under 2?" },
      {
        type: "p",
        text: "Almost nothing has changed here, and the guidance is still the strictest of any age band. The AAP recommends no screen time at all for children under 18 months, with the single exception of video calls with family. The World Health Organization goes further for the youngest infants, recommending no sedentary screen time before age 1. Between 18 and 24 months, both bodies suggest introducing only high-quality content slowly, and ideally watched together with a parent rather than handed over alone.",
      },
      { type: "h2", text: "What about ages 2 to 5?" },
      {
        type: "p",
        text: "Also largely unchanged: around 1 hour a day of high-quality, co-viewed content is still the benchmark from the AAP, and the WHO's own recommendation lines up closely, no more than 1 hour, with the general principle that less is better within that limit. This is the age band where the content itself matters most, since a slow-paced educational video and a fast-cut algorithmic feed aren't remotely the same thing even at the same number of minutes.",
      },
      { type: "h2", text: "Why did the old \"2 hours a day\" rule for older kids just change?" },
      {
        type: "p",
        text: "Because a single number never captured what actually mattered. In its updated 2026 policy, the AAP moved away from a fixed daily limit for children six and older and replaced it with a framework built around five questions, referred to as the \"5 C's\": the Child, since every child and family is different, the Content, since what's being watched or played matters more than the raw minute count, whether the activity keeps things Calm or agitates a child, what it's Crowding Out, sleep, physical activity, time with friends, and ongoing Communication between parent and child about what they're using and why. The two-hour rule had been the reference point for over a decade, but pediatricians had increasingly found it too blunt to reflect how differently screen time actually plays out between one child watching an educational video with a parent and another scrolling an algorithmic feed alone for the same two hours.",
      },
      {
        type: "cta",
        text: "Hold a consistent evening cutoff automatically, without tracking minutes.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So what should I actually go by now, since there's no number for older kids?" },
      {
        type: "p",
        text: "The one piece of guidance that shows up consistently across sources, and holds regardless of age, is keeping screens out of the bedroom and switched off for the hour before bed. Beyond that, the practical shift is toward a household rule that's consistent rather than a minute count that's precise: a fixed window, say screens off by 9pm, matters more day to day than whether today's total was 95 minutes or 100. A scheduled block that closes video and game apps at a set time each evening is one straightforward way to hold that kind of rule without re-deciding it every night, which is closer to what the updated guidance is actually asking for than a strict hour count ever was.",
      },
      { type: "h2", text: "Does this mean I don't need any limit at all for older kids?" },
      {
        type: "p",
        text: "No, the shift is away from one universal number, not away from having a limit at all. Pediatric guidance still recommends a consistent, family-specific plan, sometimes called a Family Media Plan, built around the same questions the 5 C's raise: what's being watched, whether it's crowding out sleep or activity, and whether the household is actually talking about it. The number just stopped being the headline.",
      },
      {
        type: "p",
        text: "Pick one consistent boundary that fits your evenings, screens off by a set time works for most households, rather than trying to track an exact minute count every day.",
      },
      {
        type: "cta",
        text: "Set screens-off time once, and let it run itself every evening.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What's the actual screen time guidance for a child under 2?",
        answer:
          "The AAP recommends no screen time at all under 18 months except video calls with family, and the WHO recommends no sedentary screen time before age 1. Between 18 and 24 months, both suggest introducing only high-quality content slowly and with a parent.",
      },
      {
        question: "What is the screen time guidance for ages 2 to 5?",
        answer:
          "Around 1 hour a day of high-quality, co-viewed content remains the AAP benchmark, in line with the WHO's own recommendation of no more than 1 hour, with less being better within that limit.",
      },
      {
        question: "Why did the old two-hours-a-day rule for older kids change?",
        answer:
          "In its 2026 policy update, the AAP replaced the fixed daily limit for children six and older with a framework called the 5 C's: Child, Content, Calm, Crowding Out, and Communication, because a single number didn't reflect how differently screen time plays out depending on what's being watched.",
      },
      {
        question: "What should parents go by now that there's no fixed number for older kids?",
        answer:
          "A consistent household rule, such as screens off by a set time each evening and out of the bedroom overnight, matters more than tracking an exact minute count.",
      },
      {
        question: "Does the 2026 update mean there's no limit at all for older kids?",
        answer:
          "No. The shift is away from one universal number, not away from having limits. A consistent, family-specific plan built around the 5 C's questions is still recommended.",
      },
    ],
  },
  {
    slug: "is-bgmi-safe-for-kids-shared-phone",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/is-bgmi-safe-for-kids-shared-phone.jpg",
    title: "Is BGMI Safe for Kids? What Its Built-In Protections Actually Cover",
    metaTitle: "Is BGMI Safe for Kids on a Shared Phone?",
    description:
      "BGMI carries an 18+ age rating and has a 3-hour play limit for under-18 accounts. Here's why neither protection applies if your child plays on your own account.",
    date: "2026-09-17",
    dateModified: "2026-09-17",
    readTime: "4 min read",
    excerpt:
      "BGMI carries an 18+ age rating and has a 3-hour play limit for under-18 accounts. Here's why neither protection applies if your child plays on your own account.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking an already-installed app works",
    body: [
      {
        type: "p",
        text: "BGMI carries an 18+ age rating in India, not a children's game, and Krafton has built real safety features into it for players under 18. Almost none of that applies, though, if a child is playing on a phone signed into a parent's own adult account instead of their own.",
      },
      { type: "h2", text: "Is BGMI actually age-appropriate for kids?" },
      {
        type: "p",
        text: "No. BGMI carries an 18+ age rating under India's age-rating system, and Krafton requires anyone registering an account to complete OTP verification tied to a parent or guardian's phone number if they're under 18. That's a genuinely stricter gate than most mobile games bother with, on paper.",
      },
      { type: "h2", text: "Doesn't Krafton already have built-in protections for players under 18?" },
      {
        type: "p",
        text: "It does, and they're worth knowing about even outside the blocking question. Accounts registered as belonging to someone under 18 get a playtime warning after 2 hours and a hard lock after 3 hours of continuous play in a day, with the game staying locked until after midnight. It's a real, functioning limit, built by the developer specifically for younger players.",
      },
      { type: "h2", text: "Why doesn't that help if my child plays on my account instead of their own?" },
      {
        type: "p",
        text: "Because none of it is tied to who's physically holding the phone, it's tied to which account is logged in. If a child plays on a phone signed into a parent's own account, an account that was registered as an adult, the 3-hour lock and the under-18 OTP gate simply don't apply, since as far as Krafton's system is concerned, an adult is playing. This is the same pattern that shows up with almost every age-gated app on a shared phone: the check happens once, at account creation, not every time the phone changes hands.",
      },
      {
        type: "cta",
        text: "Block it directly instead of relying on an account-based age check.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What about in-game purchases, since it's free to download?" },
      {
        type: "p",
        text: "BGMI's purchases are cosmetic only by regulation, skins, outfits, a seasonal Battle Pass, paid for with an in-game currency called UC. Nothing about that requires real money knowledge or strategy to trigger, a few taps inside the app is usually enough if a payment method is already saved to the Play Store account. Checking whether your Play Store account requires a password or biometric confirmation for every purchase, not just the first one, is worth doing regardless of whether you block the app itself.",
      },
      { type: "h2", text: "How do I actually stop the app from opening on my phone?" },
      {
        type: "p",
        text: "Block it directly, the same way as any other app. Unlike some real-money apps that get filed under a different Play Store category, BGMI and similar battle royale games are categorized as Games, so a tool that auto-adds new game installs, CrenelView included, picks it up on its own without you having to add it by name. If it's already installed, ticking it in your block list closes it the moment it's tapped, same as any other blocked app.",
      },
      {
        type: "p",
        text: "The age gate and the 3-hour limit are real, they just only work on an account actually registered to your child, not on yours.",
      },
      {
        type: "cta",
        text: "Add BGMI or any installed game to your block list in under a minute.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is BGMI actually age-appropriate for kids?",
        answer:
          "No. BGMI carries an 18+ age rating under India's age-rating system, and registering an account requires OTP verification tied to a parent or guardian's phone number for anyone under 18.",
      },
      {
        question: "Doesn't Krafton already have built-in protections for players under 18?",
        answer:
          "Yes. Accounts registered as under 18 get a playtime warning after 2 hours and a hard lock after 3 hours of continuous daily play, staying locked until after midnight.",
      },
      {
        question: "Why doesn't that help if my child plays on my account instead of their own?",
        answer:
          "Because the protections are tied to which account is logged in, not who's holding the phone. On a parent's own adult-registered account, the age gate and 3-hour lock don't apply at all.",
      },
      {
        question: "What about in-game purchases, since BGMI is free to download?",
        answer:
          "Purchases are cosmetic only by regulation, paid for with an in-game currency called UC, and can be triggered with a few taps if a payment method is already saved to the Play Store account.",
      },
      {
        question: "How do I actually stop BGMI from opening on my phone?",
        answer:
          "Block it directly. BGMI is categorized as a Game on the Play Store, so a blocking tool that auto-adds new game installs picks it up automatically; if already installed, adding it to a block list closes it the moment it's tapped.",
      },
    ],
  },
  {
    slug: "share-one-phone-between-two-kids",
    title: "How to Share One Phone Between Two Kids Fairly",
    metaTitle: "How to Share One Phone Between Two Kids Fairly",
    tags: ["Family & Relatives"],
    ogImage: "/blog-images/share-one-phone-between-two-kids.jpg",
    description:
      "One phone and two kids means someone's always waiting. Here's why the fighting happens, and how to make turns feel fair without tracking every minute.",
    date: "2026-09-18",
    dateModified: "2026-09-18",
    readTime: "4 min read",
    excerpt:
      "One phone and two kids means someone's always waiting. Here's why the fighting happens, and how to make turns feel fair without tracking every minute.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how a fixed timer works in the app",
    body: [
      {
        type: "p",
        text: "One phone and two or more kids means someone's always waiting, and the block list, schedule, and everything else in a tool like CrenelView applies to whoever's holding the phone at that moment, not to a specific child.",
      },
      { type: "h2", text: "Why does sharing one phone between siblings cause so much fighting?" },
      {
        type: "p",
        text: "Because there's no natural stopping point built into a turn the way there is with, say, a board game's set number of rounds. One child's turn tends to expand to fill whatever time nobody interrupts it, and the sibling waiting has no way to know if it's been three minutes or fifteen. Most of the fighting isn't really about the phone itself, it's about the uncertainty of not knowing when a turn will end.",
      },
      { type: "h2", text: "Does CrenelView have separate settings for each child?" },
      {
        type: "p",
        text: "No, and this is worth knowing plainly rather than discovering it later. CrenelView has one block list and one schedule for the phone itself, not per person. Whoever is holding the phone gets whatever that phone's current settings allow, whether that's a five-year-old or a ten-year-old. If two children need genuinely different rules, that's a real limitation this specific tool doesn't solve.",
      },
      { type: "h2", text: "How do I make turns feel fair without a stopwatch every time?" },
      {
        type: "p",
        text: "A visible, fixed length works better than judging by feel in the moment, since \"a few more minutes\" means something different to the parent holding the stopwatch than it does to the child watching the clock. A kitchen timer, a phone timer set by the parent, or a specific number of levels or episodes agreed on beforehand all give both kids the same visible countdown, rather than one child's turn quietly running long because nobody was tracking it closely.",
      },
      {
        type: "cta",
        text: "Set a fixed length for a turn, once, and let it hold every time.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What if one kid needs longer for something legitimate, like a homework app, than the other?" },
      {
        type: "p",
        text: "Separate the reason from the turn-taking system rather than trying to build one rule that covers everything. A homework app that's genuinely needed for schoolwork is a different category from entertainment turn-taking, and it's reasonable to treat it differently, used before or after the turn-taking rotation, rather than competing for the same block of time.",
      },
      { type: "h2", text: "Does the \"Allow for a while\" timer help with turn-taking specifically?" },
      {
        type: "p",
        text: "It can, used as a shared visible countdown rather than a private one. Setting it for a fixed length, 15 or 30 minutes, and letting both kids see that the same clock applies to whoever's turn it currently is, at least removes the \"how much longer\" guessing game, even though it doesn't solve who goes first or track whose turn is next, since that part still has to be a household system, not a phone setting.",
      },
      {
        type: "p",
        text: "The phone can hold a length. It can't hold a turn order, that part still has to be a family rule you set once and stick to.",
      },
      {
        type: "cta",
        text: "Hold a consistent turn length automatically, without watching the clock yourself.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does sharing one phone between siblings cause so much fighting?",
        answer:
          "There's no natural stopping point to a turn, so one child's turn tends to expand while the waiting sibling has no way to know how long it's actually been. Most of the fighting is about that uncertainty, not the phone itself.",
      },
      {
        question: "Does CrenelView have separate settings for each child?",
        answer:
          "No. CrenelView applies one block list and schedule to the phone itself, not per person, so whoever is holding the phone gets the same settings regardless of which child it is.",
      },
      {
        question: "How do I make turns feel fair without a stopwatch every time?",
        answer:
          "A visible, fixed length, such as a timer or an agreed number of levels or episodes, gives both children the same countdown rather than relying on a parent's in-the-moment judgment of how long a turn has run.",
      },
      {
        question: "What if one kid needs longer for something legitimate, like a homework app?",
        answer:
          "Treat schoolwork apps as a separate category from entertainment turn-taking, used before or after the rotation, rather than trying to build one rule that covers both.",
      },
      {
        question: "Does the Allow for a while timer help with sibling turn-taking?",
        answer:
          "Used as a shared, visible countdown it removes the how-much-longer guessing game, though it doesn't determine turn order, which still has to be a household rule rather than a phone setting.",
      },
    ],
  },
  {
    slug: "why-reels-shorts-harder-to-stop-than-video",
    title: "Why Reels and Shorts Are Harder to Stop Than a Video",
    metaTitle: "Why Reels and Shorts Are Hard to Stop",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/why-reels-shorts-harder-to-stop-than-video.jpg",
    description:
      "A single video has an ending. Reels and Shorts don't. Here's why short-form feeds are harder for a child to stop, and what actually works instead.",
    date: "2026-09-18",
    dateModified: "2026-09-18",
    readTime: "4 min read",
    excerpt:
      "A single video has an ending. Reels and Shorts don't. Here's why short-form feeds are harder for a child to stop, and what actually works instead.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how a timed unlock works",
    body: [
      {
        type: "p",
        text: "A single video has an ending, credits roll, a screen changes, and there's a moment to hand the phone back. Reels and Shorts don't have that moment built in at all, one clip ends and the next starts before anyone's had a chance to decide whether to stop.",
      },
      { type: "h2", text: "Why does a child resist stopping Reels or Shorts more than a regular video?" },
      {
        type: "p",
        text: "Because there's no natural pause. A regular video ends, and even a young child recognizes that as a stopping point, whether they like it or not. A short-form feed is built to remove that moment entirely, one clip flows into the next automatically, with nothing marking where one thing ended and another began. Asking a child to stop mid-scroll is asking them to invent their own stopping point in a format specifically built not to have one.",
      },
      { type: "h2", text: "Why is it designed that way?" },
      {
        type: "p",
        text: "Because it works, for the platform. Autoplay and an endless, algorithmically chosen feed are standard design choices across every major short-form video product, not a flaw specific to one app, and they're built to maximize how long someone keeps watching, not to give a natural exit point. That's true for adults too, but a child has far less built-in resistance to a feed engineered specifically to keep pulling them into one more clip.",
      },
      { type: "h2", text: "Can I just block Reels or Shorts specifically, but leave the rest of the app open?" },
      {
        type: "p",
        text: "Not really, and it's worth being honest about that limitation rather than implying it's possible. Blocking works at the level of the whole app, not a feature inside it. There's no way to let YouTube open for a subscribed channel's videos while keeping its Shorts tab closed, or let Instagram open for messages while blocking Reels specifically, since both live inside the same app.",
      },
      {
        type: "cta",
        text: "Block the whole app on a schedule when one feature inside it is the real problem.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So what actually works instead?" },
      {
        type: "p",
        text: "Treat the whole app as the unit that gets blocked or allowed, rather than trying to carve out one feature from it. If Reels or Shorts specifically are the problem, blocking the entire app, on a schedule or all the time, is the only version of that control that actually exists right now. For a child who genuinely needs an app for a specific reason, a school YouTube channel, for instance, a scheduled window that opens the whole app at a set time and closes it again afterward is closer to what's actually achievable than trying to filter what happens inside it.",
      },
      { type: "h2", text: "Does a timed unlock help with this specific problem?" },
      {
        type: "p",
        text: "A little, since it at least puts a hard stop on the session even if the feed itself never signals one. Setting a fixed length upfront, rather than trying to interrupt a scroll once it's already started, means the ending doesn't depend on anyone noticing it's time to stop. CrenelView's timed unlock works this way: pick a length, and the app closes itself when it's up, regardless of what's on screen at that moment.",
      },
      {
        type: "p",
        text: "The feed was built to never end on its own. The stop has to come from outside it, a schedule or a timer, since it won't come from the content itself.",
      },
      {
        type: "cta",
        text: "Set a hard stop that doesn't depend on the feed ever pausing itself.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does a child resist stopping Reels or Shorts more than a regular video?",
        answer:
          "A regular video ends and is recognized as a stopping point, but a short-form feed removes that moment entirely, with clips flowing automatically and nothing marking where one ended and the next began.",
      },
      {
        question: "Why are Reels and Shorts designed without a stopping point?",
        answer:
          "Autoplay and an endless, algorithmic feed are standard across short-form video products, built to maximize watch time rather than provide a natural exit point.",
      },
      {
        question: "Can I block Reels or Shorts specifically but leave the rest of the app open?",
        answer:
          "No. Blocking works at the whole-app level, not a feature inside it, so a specific tab or feed within an app can't be blocked while the rest of the app stays open.",
      },
      {
        question: "What actually works instead of trying to block just Reels or Shorts?",
        answer:
          "Treating the whole app as the unit that gets blocked or allowed, either on a schedule or all the time, since filtering one feature inside an app isn't currently possible.",
      },
      {
        question: "Does a timed unlock help with the endless-scroll problem?",
        answer:
          "Somewhat. Setting a fixed length upfront and letting the app close automatically when time is up puts a hard stop on a session regardless of what the feed itself is doing.",
      },
    ],
  },
  {
    slug: "karnataka-social-media-ban-under-16-status",
    title: "Karnataka's Social Media Ban for Under-16s: What's Actually in Effect Right Now",
    metaTitle: "Karnataka Social Media Ban: What's in Effect",
    tags: [],
    ogImage: "/blog-images/karnataka-social-media-ban-under-16-status.jpg",
    description:
      "Karnataka announced a social media ban for under-16s in March 2026. Seven months later, here's exactly what's law, what isn't, and what parents can do now.",
    date: "2026-09-19",
    dateModified: "2026-09-19",
    readTime: "4 min read",
    excerpt:
      "Karnataka announced a social media ban for under-16s in March 2026. Seven months later, here's exactly what's law, what isn't, and what parents can do now.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how a scheduled block works today",
    shortAnswer: [
      "Announced in March 2026 as a state budget-speech policy intent, not a passed law",
      "No bill has been tabled in the Karnataka legislature as of this writing",
      "No enforcement mechanism, age-verification method, or start date has been set",
      "Nothing currently requires any parent or platform to do anything differently yet",
    ],
    body: [
      {
        type: "p",
        text: "In March 2026, Karnataka's chief minister announced during the state budget speech that social media use would be banned for children under 16. Seven months on, that announcement is still exactly that, an announcement, with no bill passed, no enforcement rules written, and no date on which anything actually changes.",
      },
      { type: "h2", text: "What did Karnataka actually announce in March 2026?" },
      {
        type: "p",
        text: "Chief Minister Siddaramaiah said, while presenting the 2026-27 state budget, that social media use would be prohibited for children under 16, making Karnataka the first Indian state to propose this kind of restriction. He clarified afterward that the ban was aimed at social media use specifically, not phone ownership, children could still have a phone, just not access to social platforms on it. No enforcement details were shared at the time, and the government said a program for how it would actually work would be announced later.",
      },
      { type: "h2", text: "Has it become law yet?" },
      {
        type: "p",
        text: "No. A policy intent announced in a budget speech isn't legislation by itself, a bill still has to be drafted and passed through the state legislature, and legal analysts covering the announcement noted in March that no timeline for that had been confirmed. Searching for updates as of September 2026 turns up no report of a bill having actually been tabled since.",
      },
      { type: "h2", text: "How would something like this even be enforced?" },
      {
        type: "p",
        text: "That's the open question digital rights groups raised from the start. Age verification for social media is technically difficult to do reliably, and India's Digital Personal Data Protection Act already touches children's data at the central government level, which raises a real question about whether a single state has the authority to regulate a national platform's access rules on its own. The Internet Freedom Foundation specifically flagged this jurisdiction question, along with concerns that any age-verification system built to enforce a ban would itself create new privacy risks.",
      },
      {
        type: "cta",
        text: "A household rule can start today, before any law does.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So is there anything I need to do right now, as a parent?" },
      {
        type: "p",
        text: "Nothing has legally changed yet, so there's no compliance step to take. But the worry behind the proposed ban, a child spending unsupervised time on social apps, isn't something a parent has to wait for state legislation to act on at home. A scheduled block on specific apps, during specific hours, does at the household level today what the state is still trying to work out how to do at the platform level. That's true regardless of whether the ban eventually passes, gets narrowed, or stalls entirely.",
      },
      { type: "h2", text: "Will this actually happen, or could it change?" },
      {
        type: "p",
        text: "Genuinely uncertain. Similar age-based bans in other countries have faced legal challenges, been narrowed in scope, or taken far longer to implement than initially announced, and Karnataka's proposal is still at the stage where the basic legal mechanism hasn't been settled. Worth checking back on this rather than assuming either that it will definitely take effect as first described, or that it will quietly disappear.",
      },
      {
        type: "p",
        text: "A law can take months or years to actually apply. A household rule can start today.",
      },
      {
        type: "cta",
        text: "Set a household rule now, whatever the law ends up saying.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What did Karnataka actually announce in March 2026?",
        answer:
          "A policy intent to ban social media use, not phone ownership, for children under 16, announced during the state budget speech, with no enforcement details shared at the time.",
      },
      {
        question: "Has Karnataka's social media ban for under-16s become law?",
        answer:
          "No. It remains a policy announcement; a bill still needs to be drafted and passed by the state legislature, and no confirmed timeline for that exists as of this writing.",
      },
      {
        question: "How would Karnataka's proposed social media ban be enforced?",
        answer:
          "This is unresolved. Reliable age verification is technically difficult, and there's an open jurisdiction question over whether a state can regulate access to national platforms given India's central data protection law.",
      },
      {
        question: "Do parents need to do anything right now because of this ban?",
        answer:
          "No legal requirement exists yet. A household-level scheduled block on specific apps already addresses the underlying concern regardless of the law's eventual status.",
      },
      {
        question: "Will Karnataka's social media ban actually happen?",
        answer:
          "Genuinely uncertain. Comparable age-based bans elsewhere have faced legal challenges or taken far longer than initially announced, and the basic legal mechanism here hasn't been settled yet.",
      },
    ],
  },
  {
    slug: "roblox-face-scan-age-check-shared-phone",
    title: "Roblox's Face-Scan Age Check: What It Misses on a Shared Phone",
    metaTitle: "Roblox Face-Scan Age Check: What It Misses",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/roblox-face-scan-age-check-shared-phone.jpg",
    description:
      "Roblox's 2026 face-scan age check verifies an account, not a person. Here's what happens when a child logs into a parent's or sibling's account instead.",
    date: "2026-09-19",
    dateModified: "2026-09-19",
    readTime: "4 min read",
    excerpt:
      "Roblox's 2026 face-scan age check verifies an account, not a person. Here's what happens when a child logs into a parent's or sibling's account instead.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how scheduled blocking works regardless of account",
    body: [
      {
        type: "p",
        text: "Since January 2026, anyone who wants to chat on Roblox has to pass a face-scan age check first. It's a real, meaningful change, but it checks the account, not the person holding the phone, and that distinction matters a lot on a phone more than one person uses.",
      },
      { type: "h2", text: "What actually changed with Roblox's age check in January 2026?" },
      {
        type: "p",
        text: "Roblox now requires every user, worldwide, to complete an age check before chat features work at all, text, voice, or direct messages. Playing games without chatting still works with no check. The rollout followed real pressure, lawsuits and government scrutiny over adults contacting children through the platform's chat, and it sorts every account into one of six age bands, under 9, 9 to 12, 13 to 15, 16 to 17, 18 to 20, and 21 and up, with chat permissions that differ by band.",
      },
      { type: "h2", text: "How does the age check actually work?" },
      {
        type: "p",
        text: "The default method is a short selfie-style video, processed by a third-party vendor, that estimates age from facial features in a few seconds. A government ID is available as an alternative for users 13 and older, and a parent can verify a child's age directly through parental controls instead, which skips the face scan entirely. Whichever method is used, the result gets attached to the account, not repeated for every session.",
      },
      { type: "h2", text: "Does my child need to redo the check every time, or does it stick to the account?" },
      {
        type: "p",
        text: "It sticks to the account. Once an account passes the check and lands in an age band, that band applies to whoever is logged into that account going forward. Roblox has no way to tell who is physically holding the phone at any given moment, only which account is active.",
      },
      {
        type: "cta",
        text: "Schedule Roblox to stay closed outside the hours you choose, regardless of the account.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What happens if my child logs into my account, or an older sibling's, instead of their own?" },
      {
        type: "p",
        text: "They inherit whatever age band that account was sorted into, chat permissions included. A parent's or an older sibling's account, verified as an adult or an older teen, gives a much younger child the same chat access as whoever the account actually belongs to, which is precisely the kind of contact the age check was built to prevent. On a phone where the same Roblox account gets used by more than one person, the age check is really only checking the account's history, not who's playing right now.",
      },
      { type: "h2", text: "What can I actually do about it on a shared phone?" },
      {
        type: "p",
        text: "Roblox's own controls can cap total daily hours on an account, which is worth setting regardless, but they can't schedule when that time is used, a capped hour can still get spent right in the middle of homework time. Blocking the app entirely during specific hours closes that gap: CrenelView lets you schedule Roblox, or any other app, to stay closed outside the hours you choose, which holds regardless of which account is logged in or what age band it's sorted into. For the account-sharing problem specifically, the more direct fix is making sure your child has their own Roblox account with their own actual age on it, rather than one that inherited someone else's verification.",
      },
      {
        type: "p",
        text: "The face scan checks the account. The account doesn't know whose hands are actually on the phone.",
      },
      {
        type: "cta",
        text: "Set the hours once, on the app itself, no matter which account is signed in.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What changed with Roblox's age check in January 2026?",
        answer:
          "Roblox began requiring every user worldwide to complete an age check before chat features work, sorting accounts into six age bands with different chat permissions each. Playing without chatting still requires no check.",
      },
      {
        question: "How does Roblox's face-scan age check work?",
        answer:
          "The default method is a short selfie-style video estimating age; government ID or parental verification are alternatives, and the result is attached to the account rather than checked each session.",
      },
      {
        question: "Does Roblox's age check repeat every session or stick to the account?",
        answer:
          "It sticks to the account permanently once completed, so whoever is logged into that account inherits its age band, since Roblox can't detect who is physically holding the phone.",
      },
      {
        question: "What happens if a child logs into a parent's or sibling's Roblox account?",
        answer:
          "The child inherits that account's age band and chat permissions, which can expose them to contact the age-check system was specifically built to prevent.",
      },
      {
        question: "What can a parent do about this on a shared phone?",
        answer:
          "Roblox's own hour caps can't schedule when time is used, so a scheduled app block that closes Roblox outside chosen hours helps regardless of account, alongside setting up a correctly-aged account for the child directly.",
      },
    ],
  },
  {
    slug: "inappropriate-ads-in-kids-games-apps",
    title: "Why Does My Child's Game Show Ads I Don't Want Them Seeing",
    metaTitle: "Why Kids' Games Show Inappropriate Ads?",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/inappropriate-ads-in-kids-games-apps.jpg",
    description:
      "A gambling or dating ad showing up in an ordinary free game isn't rare, and it's not something you did wrong. Here's why it happens and what to do about it.",
    date: "2026-09-21",
    dateModified: "2026-09-21",
    readTime: "4 min read",
    excerpt:
      "A gambling or dating ad inside an ordinary free game isn't rare, and it isn't your fault. Why it happens, and the one lever that actually works.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how to block a specific app on your list",
    body: [
      {
        type: "p",
        text: "A free game showing a gambling or dating ad mid-level isn't rare or a sign you downloaded something sketchy. It happens inside perfectly ordinary, popular apps too, because of how mobile advertising actually works, not because of anything specific you did wrong.",
      },
      { type: "h2", text: "Why do free games and video apps show inappropriate ads to a child at all?" },
      {
        type: "p",
        text: "Because most free apps don't choose their ads directly, an automated ad network does, picking from thousands of advertisers in real time based on bids, not on who's likely to be watching. Academic research testing thousands of apps has found a meaningful share of ad content violating child-audience rules, and separate reporting has documented gambling, dating, and horror-game ads showing up inside apps used by children as young as four. The app developer often has limited visibility into which specific ad actually gets served at any given moment.",
      },
      { type: "h2", text: "Isn't there a rule against this in apps made for kids?" },
      {
        type: "p",
        text: "There is, on paper. Both the Play Store and the App Store prohibit age-inappropriate ads in apps listed under their kids or family categories. The gap is enforcement: ad networks serve creative dynamically and at massive scale, and a rule against showing the wrong ad doesn't automatically stop it from happening in practice, which is exactly why watchdog groups have continued raising this as an active problem rather than a solved one.",
      },
      { type: "h2", text: "Does it only happen in obviously low-quality or shady apps?" },
      {
        type: "p",
        text: "No, and that's part of what makes it hard to predict. Plenty of well-known, mainstream games and video apps run the same kind of programmatic advertising, and none of it is specific to a particular app category. An app doesn't have to look sketchy, or even be aimed at children specifically, for an inappropriate ad to show up inside it.",
      },
      {
        type: "cta",
        text: "Close a specific app that keeps showing what you don't want your child seeing.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can I turn the ads off, or filter what kind of ad shows?" },
      {
        type: "p",
        text: "Not from the parent's side, generally. Ad content is controlled by the app and its ad network, not by anything on the phone itself, so there's no phone setting that filters what category of ad an app is allowed to show. Some apps offer an ad-free paid tier, which removes the issue for that one app specifically, but that's a per-app purchase decision, not a phone-wide control.",
      },
      { type: "h2", text: "So what can I actually do about it?" },
      {
        type: "p",
        text: "If a specific app keeps showing something you don't want your child seeing, blocking that app is really the only lever that works reliably, since there's no way to allow the app but filter its ads. CrenelView lets you close a specific app on your list any time, which at least removes an app you've already flagged as a repeat problem, even though it can't screen ads inside an app you're still allowing. Reporting the specific ad through the app itself, most apps have a report or flag option on an ad, is worth doing too, since that's the one channel that can actually change what gets served to everyone, not just your own phone.",
      },
      {
        type: "p",
        text: "There's no filter for this. There's only which apps stay open and which ones don't.",
      },
      {
        type: "cta",
        text: "Block the app when the ads inside it aren't something you can control.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why do free games and video apps show inappropriate ads to a child at all?",
        answer:
          "Most free apps don't choose their ads directly, an automated ad network does, picking from thousands of advertisers in real time. Research testing thousands of apps has found a meaningful share of ad content violating child-audience rules, with gambling, dating, and horror-game ads documented inside apps used by children as young as four.",
      },
      {
        question: "Isn't there a rule against this in apps made for kids?",
        answer:
          "Both the Play Store and the App Store prohibit age-inappropriate ads in kids and family category apps, but enforcement is the gap. Ad networks serve creative dynamically at massive scale, so a rule against the wrong ad doesn't automatically stop it happening in practice.",
      },
      {
        question: "Does inappropriate advertising only happen in obviously low-quality or shady apps?",
        answer:
          "No. Plenty of well-known, mainstream games and video apps run the same kind of programmatic advertising, and an app doesn't have to look sketchy, or be aimed at children specifically, for an inappropriate ad to appear inside it.",
      },
      {
        question: "Can I turn the ads off or filter what kind of ad shows?",
        answer:
          "Not generally from the parent's side, since ad content is controlled by the app and its ad network rather than anything on the phone. Some apps offer an ad-free paid tier for that one app specifically, but there's no phone-wide control.",
      },
      {
        question: "What can I actually do about inappropriate ads in an app?",
        answer:
          "Blocking the app is the only reliable lever, since there's no way to allow an app while filtering its ads. CrenelView can close a specific app any time it's flagged as a repeat problem, and reporting the ad through the app itself is worth doing too, since that can change what gets served more broadly.",
      },
    ],
  },
  {
    slug: "stop-accidental-calls-child-phone-handover",
    title: "How to Stop Accidental Calls When You Hand Your Child the Phone",
    metaTitle: "Stop Accidental Calls During Phone Handover",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/stop-accidental-calls-child-phone-handover.jpg",
    description:
      "A bored child tapping around can end in an accidental call before you notice. Here's why blocking apps doesn't stop it, and what actually does.",
    date: "2026-09-21",
    dateModified: "2026-09-21",
    readTime: "4 min read",
    excerpt:
      "A bored child tapping around can end in an accidental call before you notice. Why blocking apps doesn't stop it, and what actually does.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how scheduled app blocking works",
    body: [
      {
        type: "p",
        text: "Handing your phone over for a video usually goes fine. It's the five minutes after, when a bored child starts tapping around, that ends in a call to a relative or a contact nobody meant to dial.",
      },
      { type: "h2", text: "Why does this happen so easily?" },
      {
        type: "p",
        text: "Because the phone dialer and contacts list are core phone functions, not something buried behind a password or a specific app icon a child has to find. A locked screen's emergency-call shortcut, the recent calls list, or a contacts app sitting on the home screen are all just a tap or two away, and a young child exploring the screen doesn't distinguish between an app meant for them and one that isn't.",
      },
      { type: "h2", text: "Does blocking apps stop this?" },
      {
        type: "p",
        text: "Not directly. Calling and messaging are handled by the phone's own dialer and messaging apps, core phone functions rather than something a blocking tool is built to close the way it closes a game or a video app. CrenelView blocks apps on a list you choose, it doesn't touch the phone's calling function, and it's worth being upfront about that rather than implying otherwise.",
      },
      { type: "h2", text: "What can Android itself do about it?" },
      {
        type: "p",
        text: "Screen pinning is genuinely useful here, more than almost anywhere else it gets recommended. Pinning the phone to one specific app, the video or game your child is actually using, removes access to the home button, recent apps, and everything else, including the dialer and contacts, for as long as the pin is active. It has to be turned on each time, but for a specific handover where nothing but one app should be reachable, it does exactly that.",
      },
      {
        type: "cta",
        text: "See how CrenelView handles app blocking, separate from what happens with calls.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Is there a faster option for a short handover?" },
      {
        type: "p",
        text: "Airplane mode is blunt but effective if the video or game being used doesn't need an internet connection, an already-downloaded video, for instance. It stops calls, messages, and data all at once with one toggle, though it also cuts off anything in the handed-over app that does need a connection, so it only works for offline use.",
      },
      { type: "h2", text: "So what's actually the most reliable fix?" },
      {
        type: "p",
        text: "Screen pinning for a short, in-your-presence handover, since it locks out the dialer along with everything else. Once the handover is over and it's about apps staying closed until you decide otherwise, rather than a single supervised session, that's the part CrenelView actually handles: closing specific apps on a schedule, separate from what happens with calling in the moment.",
      },
      {
        type: "p",
        text: "Screen pinning locks the dialer away for one session. A block list keeps an app closed every time after that. Neither one does the other's job.",
      },
      {
        type: "cta",
        text: "Set up your app block list for everything after the handover is over.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does this happen so easily?",
        answer:
          "The phone dialer and contacts list are core phone functions, not hidden behind a password or a specific app icon. A locked screen's emergency-call shortcut, the recent calls list, or a visible contacts app are all just a tap or two away for a child exploring the screen.",
      },
      {
        question: "Does blocking apps stop accidental calls?",
        answer:
          "Not directly. Calling and messaging are core phone functions handled outside of what a blocking tool closes. CrenelView blocks apps on a chosen list; it doesn't touch the phone's calling function.",
      },
      {
        question: "What can Android itself do about accidental calls?",
        answer:
          "Screen pinning locks the phone to one specific app, removing access to the home button, recent apps, the dialer, and contacts for as long as it's active, making it genuinely useful for a short, supervised handover.",
      },
      {
        question: "Is there a faster option for a short handover?",
        answer:
          "Airplane mode stops calls, messages, and data at once with a single toggle, though it only works if the app being used doesn't need an internet connection.",
      },
      {
        question: "What's the most reliable fix for accidental calls during a handover?",
        answer:
          "Screen pinning for the handover itself, since it locks out the dialer along with everything else, and a scheduled app block like CrenelView for keeping specific apps closed afterward, since the two solve different parts of the problem.",
      },
    ],
  },
  {
    slug: "research-kids-screen-time-india-studies",
    title: "What Indian Research Actually Shows About Kids and Screen Time",
    metaTitle: "What Research Shows on Kids' Screen Time",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/research-kids-screen-time-india-studies.jpg",
    description:
      "Two real studies on Indian children and screen time, not opinion. Here's what an AIIMS study and a 70,000-parent survey actually found.",
    date: "2026-09-21",
    dateModified: "2026-09-21",
    readTime: "5 min read",
    excerpt:
      "Two real studies on Indian children and screen time, not opinion. What an AIIMS study and a 70,000-parent survey actually found.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how an automatic schedule works",
    body: [
      {
        type: "p",
        text: "Most advice about kids and screens is opinion, dressed up as authority. Two actual studies, one from AIIMS on rural Haryana and one a 70,000-respondent LocalCircles survey, give a clearer picture of what's actually happening in Indian households, and it's worth looking at the numbers directly rather than the advice built on top of them.",
      },
      { type: "h2", text: "How common is heavy screen use in young Indian children, according to actual data?" },
      {
        type: "p",
        text: "A 2026 study published in the Indian Journal of Medical Research, run by AIIMS Delhi's Centre for Community Medicine across four villages in rural Haryana, found that just over half of children aged 2 to 4, 50.5 percent, already exceeded the World Health Organization's threshold for excessive screen time, more than 60 minutes a day for that age group. One in four children in the study had started regular screen exposure before their second birthday. Smartphones were the most common device, used by three in four children in the study, ahead of television.",
      },
      { type: "h2", text: "Why do parents say they hand over the phone in the first place?" },
      {
        type: "p",
        text: "The same study found screens were used as a deliberate parenting tool by roughly half of parents surveyed, most often to calm a child, around 46 percent, or to keep them occupied, around 28 percent. A separate, much larger survey by LocalCircles in November 2024, covering more than 70,000 respondents across 368 urban districts, described the same pattern in plain terms: it is not unusual to see young parents hand over their mobile phones to their young children, some less than five years old, to keep them occupied.",
      },
      { type: "h2", text: "Do parents actually try to limit it, or just let it happen?" },
      {
        type: "p",
        text: "Mostly, they try. In the AIIMS study, 92 percent of parents said they believed screen time could be harmful to their child, and 90 percent said they had already attempted to restrict it. That's a striking gap: nearly everyone recognizes the concern and has tried to act on it, yet just over half of children in the same study were still past the recommended threshold. The gap between intention and outcome shows up more than any single number does, restriction as a one-off decision, repeated in the moment, doesn't hold as reliably as most parents trying it probably expect.",
      },
      {
        type: "cta",
        text: "See what a boundary that holds automatically looks like.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What did parents report noticing in their child's behavior?" },
      {
        type: "p",
        text: "In the LocalCircles survey, parents who said their child spends heavy time on screens were asked what changes they'd noticed. The most common answers were aggression, reported by 58 percent, impatience and lethargy, each around 49 percent, and low mood, around 42 percent. It's worth being precise about what this data does and doesn't show: this is what parents reported noticing, not a clinical study establishing that screen time directly causes these behaviors. The finding is real and worth taking seriously either way, since it reflects what a large number of parents are actually observing in their own households, whatever the underlying explanation turns out to be.",
      },
      { type: "h2", text: "What does this actually mean for a household deciding what to do?" },
      {
        type: "p",
        text: "Mostly, that the intention isn't the missing piece, the follow-through is. Ninety percent of parents in the AIIMS study had already tried to cut back, and most children in that same study were still over the threshold. That gap is exactly why a boundary that holds automatically, a schedule that closes specific apps at a set time rather than a decision repeated every evening, tends to work better than willpower applied fresh each day. CrenelView is built around that specific idea, not because the research says to buy an app, but because the research is fairly clear about which part of this actually breaks down.",
      },
      {
        type: "p",
        text: "The numbers say most parents already know, and have already tried. The part that's missing isn't awareness.",
      },
      {
        type: "cta",
        text: "Make the boundary automatic, since willpower alone is what the data says breaks down.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How common is heavy screen use in young Indian children, according to actual data?",
        answer:
          "A 2026 AIIMS study published in the Indian Journal of Medical Research, covering children aged 2 to 4 in rural Haryana, found 50.5 percent already exceeded the WHO's threshold for excessive screen time. One in four had started regular screen exposure before age 2, and smartphones were the most common device, ahead of television.",
      },
      {
        question: "Why do parents say they hand over the phone in the first place?",
        answer:
          "The AIIMS study found screens used as a parenting tool by roughly half of parents, most often to calm a child (around 46 percent) or keep them occupied (around 28 percent). A November 2024 LocalCircles survey of over 70,000 respondents described the same pattern: it is common for young parents to hand over their phone to a child under five to keep them occupied.",
      },
      {
        question: "Do parents actually try to limit screen time, or just let it happen?",
        answer:
          "Mostly they try. In the AIIMS study, 92 percent of parents believed screen time could be harmful and 90 percent had already attempted to restrict it, yet just over half of children in the same study were still past the recommended threshold, showing a gap between intention and outcome.",
      },
      {
        question: "What did parents report noticing in their child's behavior around heavy screen use?",
        answer:
          "In the LocalCircles survey, parents reported noticing aggression (58 percent), impatience and lethargy (around 49 percent each), and low mood (around 42 percent). This reflects what parents observed, not a clinical study proving screen time directly causes these behaviors.",
      },
      {
        question: "What does this research actually mean for a household deciding what to do?",
        answer:
          "That intention isn't the missing piece, follow-through is. Since most parents already try to restrict screen time without lasting success, a boundary that holds automatically, such as a schedule that closes apps at a set time, tends to work better than a decision repeated fresh every evening.",
      },
    ],
  },
  {
    slug: "threads-parental-controls-shared-phone-india",
    title: "Meta's New Threads Parental Controls: Do They Apply on a Shared Phone",
    metaTitle: "Threads Parental Controls on a Shared Phone",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/threads-parental-controls-shared-phone-india.jpg",
    description:
      "Meta rolled out Threads parental controls in India on 18 September 2026, built for a teen's own account. Here's why that doesn't cover a shared family phone.",
    date: "2026-09-22",
    dateModified: "2026-09-22",
    readTime: "4 min read",
    excerpt:
      "Meta's new Threads parental controls are built for a teen's own supervised account, not a young child using a parent's already-logged-in app.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking a specific app works regardless of account",
    shortAnswer: [
      "Rolled out in India and Asia-Pacific starting 18 September 2026, via Meta's Family Centre",
      "Built for a teen's own registered Teen Account, not a phone a parent and child share",
      "Lets a parent see weekly usage, set daily limits, and turn on sleep mode for a supervised account",
      "Does nothing if a young child uses Threads through the parent's own logged-in account instead",
    ],
    body: [
      {
        type: "p",
        text: "Meta announced on 18 September 2026 that parental supervision is coming to Threads across Asia-Pacific, India included, through its existing Family Centre. It's a real, meaningful update, but it's built around a teenager's own supervised account, not a young child using Threads through a parent's own login.",
      },
      { type: "h2", text: "What did Meta actually announce on 18 September 2026?" },
      {
        type: "p",
        text: "Starting that week, parents in the region can use Meta's Family Centre to see how much time a teen has spent on Threads over the past seven days, set a daily usage limit, restrict access during specific hours, and turn on a sleep mode that blocks the app overnight. Meta also said it will now report child-safety matters directly to India's Cybercrime Coordination Centre, alongside the product changes.",
      },
      { type: "h2", text: "Who is this actually built for?" },
      {
        type: "p",
        text: "A teenager under 16 who has their own Threads account, set up as a Teen Account with Meta's built-in protections, supervised through a parent's Family Centre. The whole system runs on that link between a specific teen's account and a specific parent's supervision settings, the same structure Meta already uses across Instagram and Messenger.",
      },
      { type: "h2", text: "What happens if my child isn't old enough for their own Threads account?" },
      {
        type: "p",
        text: "Then none of this applies, because there's no account for it to attach to. Threads, like most of Meta's platforms, has its own minimum age for creating an account, well above the age range a shared-phone household is usually managing. A young child using Threads at all on a shared phone is almost always doing it through a parent's own already-logged-in app, not through an account of their own, and Meta's new supervision tools have nothing to say about that situation.",
      },
      {
        type: "cta",
        text: "Block an app entirely when the question isn't which account is logged in.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this mean Threads is safe to leave open on my phone?" },
      {
        type: "p",
        text: "Not necessarily, since a parent's own Threads feed and messages are built for an adult account, not filtered for a child looking over their shoulder or tapping around during a handover. The new parental controls solve a specific, different problem, a teen's own account needing limits, not the question of whether the app should be reachable at all on a phone a much younger child also uses.",
      },
      { type: "h2", text: "What can I actually do about it on my own phone?" },
      {
        type: "p",
        text: "If Threads isn't something you want open at all during a handover, blocking it is the more direct fix, since it removes the question of which account is logged in entirely. CrenelView lets you add Threads, or any other app, to a block list that closes it the moment it's tapped, which works the same way regardless of whether Meta ever extends its teen-account supervision to cover this specific situation.",
      },
      {
        type: "p",
        text: "Meta's new tools supervise a teen's own account. They don't do anything about an app that's simply open on someone else's phone.",
      },
      {
        type: "cta",
        text: "Add Threads, or any app, to a block list that works regardless of the account.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What did Meta actually announce on 18 September 2026?",
        answer:
          "Parents in India and Asia-Pacific can now use Meta's Family Centre to see a teen's weekly Threads usage, set daily limits, restrict access during set hours, and enable a sleep mode, alongside a commitment to report child-safety matters directly to India's Cybercrime Coordination Centre.",
      },
      {
        question: "Who is Meta's new Threads parental supervision actually built for?",
        answer:
          "A teenager under 16 with their own Threads account set up as a supervised Teen Account, linked to a parent's Family Centre, the same structure Meta already uses across Instagram and Messenger.",
      },
      {
        question: "What happens if my child isn't old enough for their own Threads account?",
        answer:
          "None of the new supervision applies, since it attaches to a specific teen account. A young child using Threads on a shared phone is almost always doing so through a parent's own already-logged-in app, which the new tools don't address.",
      },
      {
        question: "Does this mean Threads is safe to leave open on a shared phone?",
        answer:
          "Not necessarily. A parent's own Threads feed and messages are built for an adult account, not filtered for a child, and the new controls solve a different problem than whether the app should be reachable at all.",
      },
      {
        question: "What can a parent do about Threads on a shared phone?",
        answer:
          "Blocking the app entirely removes the question of which account is logged in. CrenelView can add Threads, or any app, to a block list that closes it the moment it's tapped, regardless of account.",
      },
    ],
  },
  {
    slug: "should-screen-time-be-earned-or-scheduled",
    title: "Should Screen Time Be a Reward for Chores, or Just Part of the Day",
    metaTitle: "Should Screen Time Be Earned or Scheduled?",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/should-screen-time-be-earned-or-scheduled.jpg",
    description:
      "Some apps let kids earn screen time through chores. Here's what child development experts say actually happens when screen time becomes the reward.",
    date: "2026-09-22",
    dateModified: "2026-09-22",
    readTime: "4 min read",
    excerpt:
      "Some apps let a child earn screen time through chores. What child development experts say actually happens when screen time becomes the prize.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how a predictable schedule works",
    body: [
      {
        type: "p",
        text: "Some apps let a child earn extra screen time by finishing chores or lessons, turning it into a kind of currency. It's an appealing idea on the surface, and it's also something child development experts have started pushing back on, not because rewards are bad, but because of what happens when screen time specifically becomes the prize.",
      },
      { type: "h2", text: "Why do so many apps and parents tie screen time to chores or good behavior?" },
      {
        type: "p",
        text: "Because it feels like it solves two problems at once, getting chores done and controlling screen time, with one system. If a child wants the phone badly enough, the logic goes, they'll do what's asked to earn it. It's a natural extension of how a lot of parenting advice already works, sticker charts, allowance tied to tasks, so applying the same structure to screen time doesn't feel like a stretch.",
      },
      { type: "h2", text: "What actually happens when screen time becomes a reward?" },
      {
        type: "p",
        text: "According to child development experts covering this in detail in 2026, the issue is what the reward does to how a child experiences everything around it. Highly stimulating screen activities, fast-paced games, social media, short videos, trigger a real dopamine response, and when that specific activity consistently becomes the prize for finishing something else, the brain learns exactly what's worth working for. Homework, chores, and family time can start to feel like obstacles standing between a child and the thing they actually want, rather than things worth doing on their own.",
      },
      { type: "h2", text: "Is there a real downside to earning screen time through tasks?" },
      {
        type: "p",
        text: "The pattern experts describe is less about any single instance and more about what builds up over time. A child used to earning screen time through tasks can start treating the tasks themselves as meaningless, just the toll paid to reach the actual goal, which can work against the exact behaviors a parent was hoping to encourage in the first place. It doesn't mean a child using this system is being difficult on purpose. The reward system in the brain is doing precisely what it's built to do, learning what's worth chasing.",
      },
      {
        type: "cta",
        text: "Set a predictable window once, instead of negotiating screen time task by task.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So does that mean rewards are always the wrong idea?" },
      {
        type: "p",
        text: "Not rewards generally, just tying them specifically to screen time. The alternative experts point to isn't removing structure, it's building structure around a predictable routine instead of a negotiated prize: a consistent daily window, roughly the same start and stop time most days, rather than an amount that has to be earned fresh each time through a task or a behavior.",
      },
      { type: "h2", text: "What does a predictable schedule actually look like instead?" },
      {
        type: "p",
        text: "A fixed window that doesn't move depending on what happened that day, screens available from a set time to another set time, rather than a quota unlocked by chores or good behavior. That's a different design entirely from a task-based system: nothing to earn, nothing to negotiate, just a boundary that holds the same way every day. A scheduled block, closing specific apps outside chosen hours, is one way to hold that kind of predictable routine without deciding it fresh, or re-negotiating it, every single day.",
      },
      {
        type: "p",
        text: "The screen time doesn't have to be earned to be limited. It just has to be predictable.",
      },
      {
        type: "cta",
        text: "Hold the same schedule every day, without needing a reward system to enforce it.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why do so many apps and parents tie screen time to chores or good behavior?",
        answer:
          "It feels like it solves two problems at once, getting chores done and controlling screen time, with one system, and it's a natural extension of familiar parenting tools like sticker charts or chore-based allowance.",
      },
      {
        question: "What actually happens when screen time becomes a reward?",
        answer:
          "Highly stimulating screen activities trigger a real dopamine response, and when that becomes the prize for finishing something else, the brain learns what's worth working for, which can make homework, chores, and family time feel like obstacles rather than things worth doing on their own.",
      },
      {
        question: "Is there a real downside to earning screen time through tasks?",
        answer:
          "Over time, a child can start treating the tasks themselves as meaningless, just the toll paid to reach the reward, which can work against the exact behaviors a parent hoped to encourage.",
      },
      {
        question: "Does this mean rewards are always the wrong approach?",
        answer:
          "Not rewards generally, just tying them specifically to screen time. The alternative is a predictable daily routine rather than an amount earned fresh through tasks or behavior.",
      },
      {
        question: "What does a predictable schedule look like instead of an earned system?",
        answer:
          "A fixed window that stays the same regardless of what happened that day, held through something like a scheduled app block, rather than a quota unlocked by chores or good behavior.",
      },
    ],
  },
  {
    slug: "monitoring-vs-blocking-parental-apps",
    title: "Monitoring vs Blocking: Two Different Ways Parental Apps Actually Work",
    metaTitle: "Monitoring vs Blocking Parental Apps",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/monitoring-vs-blocking-parental-apps.jpg",
    description:
      "Some parental apps read what your child sees and alert you. Others just close the app entirely. Here's the real difference, and which one fits your situation.",
    date: "2026-09-22",
    dateModified: "2026-09-22",
    readTime: "4 min read",
    excerpt:
      "Some parental apps read what a child sees and alert you. Others just close the app entirely. The real difference, and which one fits your situation.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See exactly what CrenelView does and doesn't do",
    body: [
      {
        type: "p",
        text: "Parental apps in this category generally do one of two different things, and it's worth knowing which is which before picking one. Some read what a child is doing, messages, browsing, calls, and alert a parent to anything concerning. Others simply close specific apps outright, without reading what's inside them at all.",
      },
      { type: "h2", text: "What does a monitoring app actually do?" },
      {
        type: "p",
        text: "It reads activity on the phone, texts, social media posts, sometimes call content or tone, and flags anything that looks concerning to a parent, cyberbullying, self-harm language, contact from a stranger. To do that, it generally needs an ongoing internet connection to send what it finds somewhere a parent can see it, and often an account tied to the child's own device. The value is visibility: a parent finds out about a specific concerning message or interaction, not just that an app was used.",
      },
      { type: "h2", text: "What does a blocking app actually do, and how is that different?" },
      {
        type: "p",
        text: "It closes a specific app the moment someone taps it, without reading anything inside that app at all. There's nothing to flag, because there's nothing being read, an app is either allowed to open or it isn't, on whatever schedule a parent sets. The trade-off is the mirror image of monitoring's: no visibility into what a child said or saw inside an allowed app, just control over which apps are reachable in the first place.",
      },
      { type: "h2", text: "Why would someone choose one approach over the other?" },
      {
        type: "p",
        text: "It depends on the actual situation. Monitoring makes more sense for an older child with their own device and their own social accounts, where the real risk is what happens inside apps that are otherwise appropriate to use. Blocking makes more sense for a younger child using a parent's own phone, where the apps themselves, not what's said inside them, are usually the actual concern, a game that's too old for them, a video app eating the whole afternoon.",
      },
      {
        type: "cta",
        text: "See what a blocking-only approach looks like, with nothing read or sent anywhere.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does CrenelView monitor anything at all?" },
      {
        type: "p",
        text: "No, and that's a deliberate design choice, not an oversight. CrenelView doesn't read messages, browsing history, or call content, and it has no internet permission at all, so it isn't technically capable of sending anything it saw to anyone, even if it wanted to. It only ever does one thing: close an app on the list when it's tapped, on whatever schedule is set. That's a genuinely different job from monitoring, not a lighter version of it.",
      },
      { type: "h2", text: "Can the two approaches work together?" },
      {
        type: "p",
        text: "In principle, yes, for a household where they solve different real problems, an older child's phone with monitoring for what happens inside their own accounts, and a younger child's use of a shared phone handled with blocking instead. They're not competing for the same job, so much as covering different situations that come up in the same house.",
      },
      {
        type: "p",
        text: "Monitoring answers what happened. Blocking answers whether it could happen at all. Most households only actually need one of those, depending on the age in question.",
      },
      {
        type: "cta",
        text: "Choose blocking when the concern is which apps open, not what's said inside them.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What does a monitoring app actually do?",
        answer:
          "It reads activity on the phone, texts, social media posts, sometimes call content or tone, and flags anything concerning to a parent. It generally needs an ongoing internet connection and often an account tied to the child's own device, and the value is visibility into specific interactions.",
      },
      {
        question: "What does a blocking app actually do, and how is that different?",
        answer:
          "It closes a specific app the moment someone taps it, without reading anything inside that app. An app is either allowed to open or it isn't, with no visibility into what happens inside an allowed app, only control over which apps are reachable.",
      },
      {
        question: "Why would someone choose monitoring or blocking over the other?",
        answer:
          "Monitoring fits an older child with their own device and accounts, where the risk is what happens inside otherwise-appropriate apps. Blocking fits a younger child on a shared phone, where the apps themselves are usually the actual concern.",
      },
      {
        question: "Does CrenelView monitor anything at all?",
        answer:
          "No. CrenelView doesn't read messages, browsing history, or call content, and has no internet permission, so it can't send anything it saw to anyone. It only closes apps on a chosen list according to a set schedule.",
      },
      {
        question: "Can monitoring and blocking approaches work together?",
        answer:
          "In principle yes, in a household where each covers a different situation, such as monitoring for an older child's own device and blocking for a younger child's use of a shared phone.",
      },
    ],
  },
  {
    slug: "samsung-kids-mode-vs-blocking-apps",
    title: "Samsung Kids Mode vs Blocking Specific Apps: What's the Real Difference",
    metaTitle: "Samsung Kids Mode vs Blocking Apps",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/samsung-kids-mode-vs-blocking-apps.jpg",
    description:
      "Samsung Kids Mode walls off the whole phone into a separate space. Blocking specific apps leaves the phone as it is. Here's how the two actually differ.",
    date: "2026-09-23",
    dateModified: "2026-09-23",
    readTime: "4 min read",
    excerpt:
      "Samsung Kids Mode walls off the whole phone into a separate space. Blocking specific apps leaves the phone as it is. How the two actually differ.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking specific apps works on any Android phone",
    body: [
      {
        type: "p",
        text: "Samsung Kids Mode is a genuinely different design from blocking specific apps, not a competing version of the same idea. It replaces the whole phone with a separate, walled-garden interface, rather than leaving the normal phone in place and closing off a few apps within it.",
      },
      { type: "h2", text: "What is Samsung Kids Mode, and how does it actually work?" },
      {
        type: "p",
        text: "It's a free, built-in feature on Galaxy phones that swaps the entire interface for a separate \"Kids Home\" when switched on, a bright, simplified home screen with only apps a parent has specifically added. A parent sets a PIN once, picks which apps are allowed inside, roughly favoring pre-approved, curated titles, drawing, stories, kid-friendly games, and a child stays inside that sandboxed environment until the PIN is entered to exit.",
      },
      { type: "h2", text: "How is that different from blocking specific apps on the normal phone?" },
      {
        type: "p",
        text: "It flips the underlying model. Samsung Kids Mode is an allowlist: only apps specifically added work at all, everything else on the phone is unreachable by default inside that mode. Blocking a specific app on the normal phone works the opposite way, an allowlist by exception: the whole phone stays as it normally is, and only the apps a parent specifically blocks get closed. One assumes nothing should be reachable unless added; the other assumes everything should stay reachable except what's specifically removed.",
      },
      { type: "h2", text: "Does Samsung Kids Mode work on every Android phone?" },
      {
        type: "p",
        text: "No, only on Samsung's own Galaxy devices, since it's built into Samsung's software specifically. Xiaomi, Realme, Oppo, and Vivo, all widely used in India, don't have an equivalent built-in feature, which is exactly the kind of OEM difference that already shows up in how differently these phones handle background app blocking generally.",
      },
      {
        type: "cta",
        text: "Block specific apps on any Android phone, not just Samsung's.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can more than one child share Samsung Kids Mode safely?" },
      {
        type: "p",
        text: "Only loosely. Samsung Kids Mode doesn't currently support separate profiles for different children on the same device, the same PIN and the same set of allowed apps applies to whichever child is using it. For a single child on a shared phone that's not an issue, but it doesn't distinguish between an eight year old and a four year old using the same phone at different times.",
      },
      { type: "h2", text: "So which approach actually fits a shared-phone household better?" },
      {
        type: "p",
        text: "It depends on how much of the phone needs to stay usable for the parent too. Kids Mode works well when the phone is being handed over specifically and fully for a child's use, a dedicated session in the sandbox. Blocking specific apps, the way CrenelView works, fits better when the same phone needs to go back to being a normal, fully-featured adult phone the moment the handover ends, without switching modes, since nothing about the rest of the phone was ever walled off in the first place.",
      },
      {
        type: "p",
        text: "One replaces the whole phone for a session. The other just closes a few doors on the same one. Which fits depends on how the phone gets used in between.",
      },
      {
        type: "cta",
        text: "Keep the rest of your phone exactly as it is, and just close the apps that matter.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is Samsung Kids Mode, and how does it actually work?",
        answer:
          "A free, built-in Galaxy phone feature that swaps the whole interface for a separate \"Kids Home\" with only apps a parent specifically added, protected by a PIN a child needs to exit.",
      },
      {
        question: "How is Samsung Kids Mode different from blocking specific apps?",
        answer:
          "Kids Mode is an allowlist, only added apps work at all inside it, while blocking specific apps on a normal phone leaves everything reachable except what's specifically closed, the opposite model.",
      },
      {
        question: "Does Samsung Kids Mode work on every Android phone?",
        answer:
          "No. It only works on Samsung's own Galaxy devices; Xiaomi, Realme, Oppo, and Vivo, widely used in India, have no equivalent built-in feature.",
      },
      {
        question: "Can more than one child share Samsung Kids Mode safely?",
        answer:
          "Only loosely, since it doesn't currently support separate profiles per child, using the same PIN and allowed-app list regardless of which child is using the device.",
      },
      {
        question: "Which approach fits a shared-phone household better, Kids Mode or app blocking?",
        answer:
          "Kids Mode suits a dedicated handover session; blocking specific apps, as CrenelView does, suits a phone that needs to return to full normal use for the parent immediately after.",
      },
    ],
  },
  {
    slug: "dpdp-act-children-data-apps-india",
    title: "Does India's Data Protection Law Already Cover Apps Your Child Uses",
    metaTitle: "Does India's DPDP Act Cover Apps Kids Use?",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/dpdp-act-children-data-apps-india.jpg",
    description:
      "India's DPDP Act sets strict rules for children's data, but the children's-data provisions aren't fully in force yet. Here's what's actually enforceable now.",
    date: "2026-09-23",
    dateModified: "2026-09-23",
    readTime: "4 min read",
    excerpt:
      "India's DPDP Act sets strict rules for children's data, but those provisions aren't fully in force yet. Here's what's actually enforceable now.",
    relatedProductPath: "/",
    relatedProductLabel: "See CrenelView's actual permissions and privacy details",
    relatedSlugs: ["who-crenelview-is-built-for", "monitoring-vs-blocking-parental-apps"],
    shortAnswer: [
      "The DPDP Act's children's-data protections are not fully in force yet, as of this writing",
      "Once fully in effect, it will require verifiable parental consent before an app processes a child's data",
      "It will also ban tracking, behavioral monitoring, and targeted ads aimed at children",
      "An app that never collects any data in the first place isn't waiting on any of this to apply",
    ],
    body: [
      {
        type: "p",
        text: "India's Digital Personal Data Protection Act treats anyone under 18 as a child, a stricter line than most countries draw, and it sets out real rules for apps that collect a child's data. Whether those rules are actually enforceable today, right now, is a separate and less commonly understood question.",
      },
      { type: "h2", text: "What does India's DPDP Act actually say about children's data?" },
      {
        type: "p",
        text: "Any platform processing a child's personal data, anyone under 18, needs verifiable consent from a parent or guardian first. The Act also explicitly bans tracking, behavioral monitoring, and targeted advertising aimed at children outright, regardless of consent. Penalties for getting this wrong run into the hundreds of crores, among the stricter child-data regimes in the world, well beyond what the US or EU require.",
      },
      { type: "h2", text: "Is this already the law today, or is it still coming into effect?" },
      {
        type: "p",
        text: "Still coming into effect, and this matters more than the headline rules themselves. The Act commences in stages rather than all at once. As of the most recent tracking available, only the first stage, setting up the Data Protection Board itself, is actually in force. The substantive rules, including the children's-data protections, remain dormant, with full enforcement expected around May 2027. An app that isn't yet built around verifiable parental consent isn't necessarily breaking the law today, though that's expected to change.",
      },
      { type: "h2", text: "What counts as \"processing a child's data\" under this law?" },
      {
        type: "p",
        text: "Broadly, anything a platform collects, stores, or acts on that identifies or relates to a specific child, usage patterns, location, contact details, behavior used to personalize content or ads. The law is aimed squarely at platforms that build a profile of a child over time, whether for recommending content, targeting ads, or simply tracking what they do.",
      },
      {
        type: "cta",
        text: "An app that collects nothing isn't waiting on any consent framework to catch up.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So does this mean an app I already use is currently breaking the law?" },
      {
        type: "p",
        text: "Not necessarily, and it's worth being precise here rather than alarmist. Since the children's-data provisions haven't fully commenced yet, an app collecting a child's data today isn't automatically violating DPDP specifically, though it may still raise other concerns. What's genuinely useful to know as a parent is less about legal enforcement today and more about which apps are already built the way this law is heading, and which ones will need to change significantly once it does.",
      },
      { type: "h2", text: "Does this matter for an app that doesn't collect any data at all?" },
      {
        type: "p",
        text: "Less than for one that does, by design. CrenelView has no internet permission at all, so it isn't technically capable of collecting, storing, or sending any data anywhere, which means there's no consent framework to build, no tracking to stop, because none of that is happening in the first place. That's not a claim about complying with DPDP specifically, it's simply true regardless of which regulation is in force at any given moment.",
      },
      {
        type: "p",
        text: "The law is catching up to a real problem. An app with nothing to collect was never part of that problem to begin with.",
      },
      {
        type: "cta",
        text: "See why there's no data collection to regulate in the first place.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What does India's DPDP Act actually say about children's data?",
        answer:
          "Any platform processing a child's (anyone under 18) personal data needs verifiable parental or guardian consent first, and the Act bans tracking, behavioral monitoring, and targeted advertising aimed at children outright, with penalties running into the hundreds of crores.",
      },
      {
        question: "Is the DPDP Act's children's-data protection already the law today?",
        answer:
          "Not fully. The Act commences in stages, and as of the most recent tracking, only the Data Protection Board's establishment is in force; the substantive children's-data rules remain dormant, with full enforcement expected around May 2027.",
      },
      {
        question: "What counts as \"processing a child's data\" under the DPDP Act?",
        answer:
          "Broadly, anything a platform collects, stores, or acts on that identifies or relates to a specific child, including usage patterns, location, contact details, or behavior used to personalize content or ads.",
      },
      {
        question: "Does this mean an app I already use is breaking the law right now?",
        answer:
          "Not necessarily, since the children's-data provisions haven't fully commenced yet. It's more useful to know which apps are already built the way the law is heading than to treat today's status as settled enforcement.",
      },
      {
        question: "Does the DPDP Act matter for an app that doesn't collect any data at all?",
        answer:
          "Less directly. CrenelView has no internet permission and can't collect, store, or send data anywhere, so there's no consent framework or tracking question that applies to it regardless of the law's implementation timeline.",
      },
    ],
  },
  {
    slug: "google-family-link-supervision-change-shared-phone",
    title: "Google Fixed a Real Gap in Family Link. Does It Change Anything for a Shared Phone",
    metaTitle: "Google's Family Link Fix and Shared Phones",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/google-family-link-supervision-change-shared-phone.jpg",
    description:
      "Google closed a real loophole in Family Link after backlash in January 2026. Here's what changed, and why it still doesn't cover a child using a parent's own phone.",
    date: "2026-09-23",
    dateModified: "2026-09-23",
    readTime: "4 min read",
    excerpt:
      "Google closed a real loophole in Family Link after backlash in January 2026. Here's what changed, and why it still doesn't cover a child using a parent's own phone.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking works without a child needing their own account",
    relatedSlugs: ["who-crenelview-is-built-for", "screen-pinning-vs-app-blocker-shared-phone"],
    shortAnswer: [
      "Until early 2026, a child on Family Link could remove supervision alone at age 13, no parent approval needed",
      "Google reversed this after public backlash, now requiring parental approval to stop supervision until age 18",
      "The fix only matters for a child who already has their own supervised Google account",
      "It has nothing to do with a young child simply using a parent's own phone, since there's no child account there to supervise",
    ],
    body: [
      {
        type: "p",
        text: "For years, a child supervised through Google's Family Link could remove that supervision entirely on their own the moment they turned 13, no parent approval required. That quietly changed in January 2026, after a public backlash most parents never heard about.",
      },
      { type: "h2", text: "What was the actual gap in Family Link before this year?" },
      {
        type: "p",
        text: "Family Link's supervision had an exit built in at age 13, aligned with the US COPPA threshold: once a child turned 13, they could choose to end parental supervision on their own account, and Google would send them an email explaining how, framed as something they'd \"graduate\" into. The parent supervising that same account got a separate, differently worded email, one that didn't actually explain their child could remove supervision without asking them first.",
      },
      { type: "h2", text: "Why did this become a real controversy?" },
      {
        type: "p",
        text: "Because parents discovered it by accident, not from Google explaining it upfront. A widely shared account from one parent, who found her son had received instructions on ending her oversight while she'd received no warning that was even possible, set off a wave of public criticism from other parents and child-safety advocates in January 2026. The core objection wasn't really about the age 13 threshold itself, it was that Google was communicating directly with a child about loosening safeguards, without the parent in that conversation at all.",
      },
      { type: "h2", text: "What did Google actually change?" },
      {
        type: "p",
        text: "Within days of the backlash, Google reversed the policy globally. A child now needs explicit parental approval to stop Family Link supervision, and that requirement holds until they turn 18, not just 13. Both the parent and the child get notified if supervision is ever turned off, and it can no longer happen unilaterally from the child's side.",
      },
      {
        type: "cta",
        text: "See what protects a child who doesn't have an account of their own.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this fix apply to every family using Family Link?" },
      {
        type: "p",
        text: "Only to families already set up the way Family Link expects: a child with their own Google account, supervised by a parent's account, both linked together from the start. The fix closes a real gap in that specific setup. It doesn't extend Family Link's reach to a situation that was never part of that setup in the first place.",
      },
      { type: "h2", text: "Does any of this matter if my child doesn't have their own Google account?" },
      {
        type: "p",
        text: "Not really, since there's no supervised account for the fix to protect. A young child using a parent's own phone, rather than a device and Google account of their own, was never inside Family Link's supervision model to begin with, whether the opt-out gap existed or not. This is the same pattern that shows up across most of Google's, and most competitors', child-safety tools: they're built around a child's own account, and a household without one is outside the system entirely, improvements included.",
      },
      {
        type: "p",
        text: "Google fixed a real problem, for the families it was built for. It's still a different problem than the one a shared phone actually has.",
      },
      {
        type: "cta",
        text: "Block specific apps directly, without needing a child's own account at all.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What was the actual gap in Family Link before this year?",
        answer:
          "A child could end parental supervision on their own account unilaterally at age 13, with Google emailing them instructions framed as a \"graduation,\" while the supervising parent's own email didn't clearly explain that this could happen without their approval.",
      },
      {
        question: "Why did this become a real controversy?",
        answer:
          "Parents discovered the gap by accident rather than from Google explaining it, and a widely shared account of one parent's experience triggered public criticism in January 2026 over Google communicating directly with a child about removing safeguards.",
      },
      {
        question: "What did Google actually change about Family Link supervision?",
        answer:
          "Within days of the backlash, Google reversed the policy globally so a child now needs explicit parental approval to stop supervision, with that requirement holding until age 18 rather than 13.",
      },
      {
        question: "Does the Family Link fix apply to every family?",
        answer:
          "Only to families already set up with a child's own Google account supervised by a parent's account; it closes a gap in that specific setup rather than extending to situations outside it.",
      },
      {
        question: "Does this matter if my child doesn't have their own Google account?",
        answer:
          "Not really, since a young child using a parent's own phone was never inside Family Link's supervision model in the first place, whether the opt-out gap existed or not.",
      },
    ],
  },
  {
    slug: "should-you-block-whatsapp-school-groups",
    title: "Should You Block WhatsApp Too, With School Groups Using It",
    metaTitle: "Should You Block WhatsApp for School Groups?",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/should-you-block-whatsapp-school-groups.jpg",
    description:
      "WhatsApp often carries school updates and a child's handover risk on the same app. Here's how to block it without missing something that actually matters.",
    date: "2026-09-24",
    dateModified: "2026-09-24",
    readTime: "4 min read",
    excerpt:
      "WhatsApp often carries school updates and a child's handover risk on the same app. Here's how to block it without missing something that actually matters.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how to set a block window down to the hour",
    relatedSlugs: ["what-to-check-before-handing-your-child-your-phone", "screen-pinning-vs-app-blocker-shared-phone"],
    body: [
      {
        type: "p",
        text: "WhatsApp isn't just another app to block. It's often the same app carrying school announcements, homework updates, and a class group a parent genuinely needs to keep checking, which makes the decision to block it a different kind of call than blocking a game.",
      },
      { type: "h2", text: "Why is WhatsApp a harder call than other apps to block?" },
      {
        type: "p",
        text: "Because it usually serves two completely different purposes on the same phone: a parent's own communication, including school groups, and something a child might open and scroll through during a handover, media shared in a group chat, other conversations, messages meant for adults. Blocking it solves the second problem but also removes the first, since there's no way to allow WhatsApp for messages but close it for browsing within the same app.",
      },
      { type: "h2", text: "What actually happens if I block WhatsApp during school hours?" },
      {
        type: "p",
        text: "Messages still arrive and sit unread, they don't disappear, but nothing shows or opens until the block lifts or is paused. For a parent checking school updates only once or twice a day anyway, that's often not a real loss. For a household that relies on WhatsApp for something time-sensitive during the day, a same-day pickup change, an urgent note from a teacher, a scheduled block covering those hours can genuinely mean missing something that mattered in the moment it mattered.",
      },
      { type: "h2", text: "Is there a way to keep school messages coming through without leaving WhatsApp fully open?" },
      {
        type: "p",
        text: "Not from inside the blocking itself, since a block works at the level of the whole app, not a specific chat or group within it. The workaround is really a scheduling choice: blocking WhatsApp only during the specific handover window, rather than for a whole day, or choosing not to block it at all and relying on other tools, like putting the phone out of reach during that window, instead.",
      },
      {
        type: "cta",
        text: "Set the block window to match the actual handover, not the whole day.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What about blocking it just for the child's handover time, not all day?" },
      {
        type: "p",
        text: "That's usually the more practical middle ground for a household that genuinely needs WhatsApp open the rest of the day. A short, specific window, the ten or fifteen minutes the phone is actually in a child's hands, rather than blocking the whole day, limits the actual risk, a child scrolling through chats during that window, without cutting off school messages for hours at a stretch.",
      },
      { type: "h2", text: "So what's actually the more workable approach?" },
      {
        type: "p",
        text: "Match the block to the actual risk window rather than the whole day. If the concern is specifically a child having the phone unsupervised for a while, a scheduled block that only covers the hours that actually happens is closer to solving the real problem than an all-day block that also cuts off something a parent needs. CrenelView lets that window be as narrow or as wide as actually fits a household's day, rather than an all-or-nothing choice.",
      },
      {
        type: "p",
        text: "The risk isn't WhatsApp itself, it's the specific hours the phone is out of your hands. Block those hours, not the whole day.",
      },
      {
        type: "cta",
        text: "Choose exactly which hours WhatsApp stays closed, and leave the rest of the day alone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why is WhatsApp a harder call than other apps to block?",
        answer:
          "It usually serves two purposes on the same phone, a parent's own communication including school groups, and something a child might scroll through during a handover, and blocking it solves one problem while removing the other, since there's no way to allow it for messages but close it for browsing.",
      },
      {
        question: "What actually happens if I block WhatsApp during school hours?",
        answer:
          "Messages still arrive and sit unread rather than disappearing, but nothing shows until the block lifts, which can mean missing something time-sensitive if the household relies on WhatsApp for same-day updates.",
      },
      {
        question: "Can I keep school messages coming through without leaving WhatsApp fully open?",
        answer:
          "Not from inside the blocking itself, since it works at the whole-app level, not per chat. The real workaround is scheduling the block only during the specific handover window rather than all day.",
      },
      {
        question: "Should I block WhatsApp just for the handover time, not all day?",
        answer:
          "That's usually the more practical middle ground for a household that needs WhatsApp open the rest of the day, limiting the actual risk window without cutting off messages for hours at a stretch.",
      },
      {
        question: "What's the most workable approach to blocking WhatsApp around school groups?",
        answer:
          "Match the block to the actual risk window, the hours the phone is out of a parent's hands, rather than blocking the whole day, since a scheduled block can be set as narrow or wide as the household actually needs.",
      },
    ],
  },
  {
    slug: "web-filter-doesnt-cover-second-browser",
    title: "Why a Website Restriction Doesn't Follow Your Child to a Second Browser",
    metaTitle: "Why a Web Filter Misses a Second Browser",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/web-filter-doesnt-cover-second-browser.jpg",
    description:
      "A website restriction set up in one browser only applies inside that browser. Here's why a second browser slips past it, and a more direct fix for a shared phone.",
    date: "2026-09-24",
    dateModified: "2026-09-24",
    readTime: "4 min read",
    excerpt:
      "A website restriction set up in one browser only applies inside that browser. Here's why a second browser slips past it, and a more direct fix for a shared phone.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking a browser app works",
    relatedSlugs: ["screen-pinning-vs-app-blocker-shared-phone", "samsung-kids-mode-vs-blocking-apps"],
    shortAnswer: [
      "A website restriction set up in one browser, like Chrome, only applies inside that one browser",
      "Many Android phones come with a second browser already installed, or one tap away",
      "Switching to a different browser isn't a technical trick, just a different icon",
      "Blocking every browser app outright sidesteps the problem for a phone a young child doesn't need a browser on at all",
    ],
    body: [
      {
        type: "p",
        text: "A website restriction switched on inside one browser only applies inside that one browser. It's a narrower promise than it sounds, and it's easy to assume \"the web is filtered\" once it's been set up once, in whichever browser happened to be open at the time.",
      },
      { type: "h2", text: "Why doesn't a website restriction set up in one browser cover the whole phone?" },
      {
        type: "p",
        text: "Because a browser is just another app, and a restriction built into one app has no reach into a different one. Google's own documentation for Family Link is direct about this: its Chrome website-restriction setting applies in Chrome and in some Android apps, not across every browser on the device. A phone with Chrome restricted and a second browser installed isn't a phone with every browser covered, it's a phone with one restricted app and one completely open one.",
      },
      { type: "h2", text: "How would a child even find a second browser?" },
      {
        type: "p",
        text: "Often without looking for one. Many Android phones, especially from Xiaomi, Samsung, Oppo, and Vivo, come with their own browser pre-installed alongside Chrome, an icon that's simply been sitting on the phone the whole time. Installing an additional browser from the Play Store, if that's even needed, takes a couple of taps and no particular technical knowledge, a classmate mentioning a different browser is usually all it takes.",
      },
      { type: "h2", text: "Does this mean browser-restriction software doesn't work?" },
      {
        type: "p",
        text: "Not that it doesn't work, more that it works exactly as scoped, restricting the browsers it's actually set up to cover, and nothing outside that. Software built for this specifically can extend that restriction across more browsers than a single browser's own setting does. The underlying limitation, a restriction only covers what it was actually applied to, doesn't disappear, it just moves to a smaller and smaller list of uncovered apps as more get covered.",
      },
      {
        type: "cta",
        text: "Close every browser on the phone, instead of restricting what each one shows.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What's a more direct fix for a shared phone, then?" },
      {
        type: "p",
        text: "Block the browser apps outright rather than trying to restrict what loads inside each one. That sidesteps the whole cat-and-mouse problem, since there's nothing to restrict inside an app that never opens at all. CrenelView works this way: pick which apps, including any browser, stay closed, and it doesn't matter how many different browsers are on the phone, since none of them open until you decide otherwise.",
      },
      { type: "h2", text: "Does a young child actually need browser access at all during a handover?" },
      {
        type: "p",
        text: "Usually not. A specific video app, a specific game, a specific downloaded show, almost none of what a young child actually uses during a supervised handover requires a browser at all. Blocking every browser on the phone, rather than trying to restrict what one of them shows, tends to fit that reality better than an approach built for an older child who's actually expected to browse the open web.",
      },
      {
        type: "p",
        text: "A restriction has to cover every door to mean anything. Blocking the doors a young child doesn't need open in the first place is often the simpler fix.",
      },
      {
        type: "cta",
        text: "Block every browser on the phone at once, not one restricted app at a time.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why doesn't a website restriction set up in one browser cover the whole phone?",
        answer:
          "A browser is just another app, and a restriction built into one app has no reach into a different one. Google's own Family Link documentation confirms its Chrome website-restriction setting applies in Chrome and in some Android apps, not across every browser on the device.",
      },
      {
        question: "How would a child even find a second browser?",
        answer:
          "Often without looking, since many Android phones, especially from Xiaomi, Samsung, Oppo, and Vivo, come with their own browser pre-installed alongside Chrome. Installing another from the Play Store takes a couple of taps.",
      },
      {
        question: "Does this mean browser-restriction software doesn't work?",
        answer:
          "It works exactly as scoped, filtering the browsers it's set up to cover and nothing outside that; the limitation is that coverage only extends as far as it's actually applied.",
      },
      {
        question: "What's a more direct fix for a shared phone than filtering each browser?",
        answer:
          "Blocking browser apps outright rather than filtering what loads inside them, since there's nothing to filter inside an app that never opens at all.",
      },
      {
        question: "Does a young child actually need browser access during a handover?",
        answer:
          "Usually not, since a specific video app, game, or downloaded show covers most of what a young child needs, making blocking every browser a more direct fit than filtering one.",
      },
    ],
  },
  {
    slug: "is-google-gemini-safe-for-young-child",
    title: "Is Google Gemini Safe for a Young Child to Use on Your Phone",
    metaTitle: "Is Google Gemini Safe for a Young Child?",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/is-google-gemini-safe-for-young-child.jpg",
    description:
      "Google gives under-13s access to Gemini by default through Family Link. Here's what that means, and what happens on a phone with no child account at all.",
    date: "2026-09-24",
    dateModified: "2026-09-24",
    readTime: "4 min read",
    excerpt:
      "Google gives under-13s access to Gemini by default through Family Link. Here's what that means, and what happens on a phone with no child account at all.",
    relatedProductPath: "/#how-it-works",
    relatedProductLabel: "See how blocking a specific app works regardless of account",
    relatedSlugs: ["is-bgmi-safe-for-kids-shared-phone", "roblox-face-scan-age-check-shared-phone"],
    shortAnswer: [
      "Gemini officially requires age 13+, but Google separately allows under-13 access through Family Link",
      "That access is turned on by default unless a parent finds the setting and turns it off",
      "A notification about first use arrives only after the child has already used it, not before",
      "Gemini also powers Google Search's AI Overviews, which show up without opening any app at all",
    ],
    body: [
      {
        type: "p",
        text: "Google began giving children under 13 access to its Gemini AI chatbot by default, on any Android phone managed through Family Link, unless a parent specifically turns it off. On a phone that isn't managed through Family Link at all, a simpler version of the same problem shows up: a young child talking to Gemini through nothing more than the parent's own already-signed-in account.",
      },
      { type: "h2", text: "Does Gemini have an age requirement?" },
      {
        type: "p",
        text: "Officially, yes, 13 or the applicable age in your country for a personal Google account. Google carved out a specific exception below that: a parent can enable Gemini access for a child under 13 through a Family Link-supervised account, which is a deliberate, documented option, not a loophole.",
      },
      { type: "h2", text: "What does \"available by default\" actually mean for a supervised child account?" },
      {
        type: "p",
        text: "It means the setting starts on rather than off for many families, so a parent has to actively find it and turn it off if they don't want their supervised child using it, rather than opting in when they're ready. Google does send a notification the first time a child under 13 uses Gemini, but that notification arrives after the first conversation has already happened, not as a request for permission beforehand.",
      },
      { type: "h2", text: "What if my child doesn't have their own Google account and just uses my phone?" },
      {
        type: "p",
        text: "Then none of the Family Link age-gate applies at all, since there's no supervised child account for it to attach to. A young child using a parent's own phone is simply talking to Gemini through the parent's own adult account, already signed in, with no age check in the path whatsoever. This is the same pattern that shows up across almost every age-gated feature on a shared phone: the check happens at the account level, and an account that was never set up as a child's account was never going to trigger it.",
      },
      {
        type: "cta",
        text: "Block an app entirely when the question isn't which account is signed in.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this only happen if my child deliberately opens a Gemini app?" },
      {
        type: "p",
        text: "No, and this is worth knowing specifically. Gemini also generates the AI Overviews that now appear at the top of most Google Search results, and there's currently no way to turn that off for an individual account. A child searching something completely ordinary, a school topic, a question about an animal, can still land on an AI-generated summary without ever opening anything called \"Gemini\" at all.",
      },
      { type: "h2", text: "So what can I actually do about it?" },
      {
        type: "p",
        text: "If Gemini shows up as its own app on your phone, block it the same way as any other app, closing it the moment it's tapped. AI Overviews inside ordinary Search are a different, harder problem, since they're built into Search itself rather than a separate app, closer to the same category as a phone's built-in dialer: not something an app block reaches. Being aware that an AI-generated answer can appear during an entirely ordinary search is, realistically, the more useful takeaway than expecting a single setting to close every path.",
      },
      {
        type: "p",
        text: "The age gate exists. It just checks the account, and a phone shared with a parent usually doesn't have a child account for it to check.",
      },
      {
        type: "cta",
        text: "Block a specific app directly, regardless of which account is signed in.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Does Google Gemini have an age requirement?",
        answer:
          "Officially yes, 13 or the applicable age in your country for a personal Google account, though Google separately allows a parent to enable Gemini access for a child under 13 through a Family Link-supervised account.",
      },
      {
        question: "What does \"available by default\" mean for a supervised child account on Gemini?",
        answer:
          "The setting starts on rather than off for many families, so a parent has to actively turn it off if they don't want it enabled. A notification about first use arrives only after the child has already used it, not beforehand.",
      },
      {
        question: "What if my child doesn't have their own Google account and just uses my phone?",
        answer:
          "The Family Link age-gate doesn't apply at all in that case, since there's no supervised child account for it to attach to; the child is simply using the parent's own already-signed-in account with no age check involved.",
      },
      {
        question: "Does this only happen if a child deliberately opens a Gemini app?",
        answer:
          "No. Gemini also generates Google Search's AI Overviews, which can't be turned off per account, so a child can encounter an AI-generated answer during an entirely ordinary search without opening any app called Gemini.",
      },
      {
        question: "What can a parent actually do about Gemini access on a shared phone?",
        answer:
          "If Gemini appears as its own app, it can be blocked like any other app. AI Overviews inside Search itself are a harder problem, since they're built into Search rather than a separate app.",
      },
    ],
  },
  {
    slug: "diwali-break-screen-time-plan",
    title: "Why Your Screen Time Rules Fall Apart During Diwali, and What to Do Instead",
    metaTitle: "A Screen Time Plan for the Diwali Break",
    tags: ["Family & Relatives"],
    ogImage: "/blog-images/diwali-break-screen-time-plan.jpg",
    description:
      "Diwali week breaks every screen time rule. A realistic plan for the holidays that bends without collapsing, and what to check once school starts again.",
    date: "2026-09-25",
    dateModified: "2026-09-25",
    readTime: "5 min read",
    excerpt:
      "Diwali week breaks every screen time rule. A realistic plan for the holidays that bends without collapsing, and what to check once school starts again.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    body: [
      {
        type: "p",
        text: "Diwali 2026 falls on Sunday, 8 November, with the school break usually stretching a few days on either side of it. Every family with young kids already knows what that week actually looks like. Cousins visiting. Grandparents around all day. New phones out for photos and videos. And a child who is usually handed a phone at 6pm now has one in hand from breakfast onward, because everyone else is busy and the phone is the easiest way to keep her occupied. The normal rule does not survive that week. Trying to enforce it exactly as written just turns into a fight during the one week you actually wanted to enjoy with family. The better move is deciding in advance what bends and what does not.",
      },
      { type: "h2", text: "Why does screen time always slip during Diwali specifically" },
      {
        type: "p",
        text: "During most holidays a child is bored and picks up a phone out of habit. Diwali is different because the phone comes out on purpose, all week, in other people's hands too. Relatives are recording rangoli and diyas, kids are asking to see the photos, and somewhere in that the child ends up holding an unlocked phone with no supervision for ten or fifteen minutes at a stretch. It is not one long session that breaks the rule. It is fifteen short ones a day that add up to more screen time than a normal week, without anyone deciding that on purpose.",
      },
      { type: "h2", text: "Should you keep the normal schedule or just let go for the week" },
      {
        type: "p",
        text: "Neither, honestly. Dropping the rule completely for five days makes it much harder to bring back on the sixth, because the child has just had a week of proof that the rule is negotiable if enough people are around. Keeping it exactly as strict as a school week is its own kind of fight, and it is the fight you are least likely to win when three other adults in the house are also handing over their phones. The middle option that actually works: decide which part of the rule is non-negotiable (usually the late-night cutoff) and let the daytime hours flex more than usual.",
      },
      {
        type: "cta",
        text: "Set your blocked list once before the week gets busy, so it holds even when you are distracted.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What about new apps that show up from festival gift money" },
      {
        type: "p",
        text: "Diwali is also when a lot of kids get cash from relatives, and that cash often becomes a new game or app within a day or two of the holiday ending. If your phone auto-adds anything Android categorizes as a game to your blocked list, a new install is covered without you having to notice it and add it yourself. If it does not, put \"check what got installed this week\" on your own to-do list for the Monday after the break, not as a suspicion of your child, just as ordinary upkeep the same way you would check the SIM balance or the electricity bill after a long weekend.",
      },
      { type: "h2", text: "How do you handle a relative who keeps handing your child their own phone" },
      {
        type: "p",
        text: "This happens constantly during Diwali specifically because so many relatives are together in one place for several days in a row, each with their own phone, each happy to hand it over for a minute of quiet. A phone you do not control has none of your rules on it. The honest fix is not a technical one, it is a five-second heads up to the adults in the house before the week starts: \"we are trying to keep her off video apps this week, so maybe hold off handing over your phone.\" Most relatives are glad to help once they know it is a real ask and not a judgment on them.",
      },
      { type: "h2", text: "What is the one thing worth actually deciding before the holiday starts" },
      {
        type: "p",
        text: "Pick the schedule now, while nobody is asking you for the phone yet, rather than trying to improvise it in the moment with a room full of family watching. A five-minute decision made on a quiet evening before the break holds up far better than a rule you are inventing on the spot on day two of the holiday, tired, with company in the house and a child already holding the phone.",
      },
      {
        type: "p",
        text: "Diwali week is short. The habits it sets, good or bad, usually are not. A plan you actually wrote down before the holiday started is the difference between one loud week and one loud month.",
      },
      {
        type: "cta",
        text: "Lock in your Diwali-week hours in a few taps, before the house fills up with guests.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does screen time always slip during Diwali specifically",
        answer:
          "Because the phone comes out on purpose, all week, in other people's hands too. Relatives are recording rangoli and diyas, kids ask to see the photos, and the child ends up holding an unlocked phone unsupervised for ten or fifteen minutes at a stretch, several times a day. It is not one long session that breaks the rule, it is many short ones adding up without anyone deciding that on purpose.",
      },
      {
        question: "Should you keep the normal schedule or just let go for the week",
        answer:
          "Neither works well on its own. Dropping the rule for five days makes it hard to bring back afterward, and holding a school-week schedule during a family festival is a fight you are unlikely to win. Decide which part is non-negotiable, usually the late-night cutoff, and let daytime hours flex more than usual.",
      },
      {
        question: "What about new apps that show up from festival gift money",
        answer:
          "Gift money after Diwali often turns into a new game or app within a day or two. If new installs Android categorizes as games get added to your blocked list automatically, this is already covered. Either way, checking what got installed the week after the holiday is a useful habit, the same as checking any other bill after a long break.",
      },
      {
        question: "How do you handle a relative who keeps handing your child their own phone",
        answer:
          "A phone you do not control has none of your rules on it. The honest fix is a quick heads up to the adults in the house before the week starts, asking them to hold off handing over their own phone. Most relatives are glad to help once they understand it is a real request.",
      },
      {
        question: "What is the one thing worth actually deciding before the holiday starts",
        answer:
          "The schedule itself. Deciding it on a quiet evening before the break holds up far better than inventing a rule on the spot during the holiday, tired, with guests in the house and a child already holding the phone.",
      },
    ],
  },
  {
    slug: "blinkit-parental-controls-shared-phone",
    title: "Blinkit's New Parental Controls, and What They Do Not Cover on a Shared Phone",
    metaTitle: "Blinkit's Parental Controls: What They Cover",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/blinkit-parental-controls-shared-phone.jpg",
    description:
      "Blinkit now lets you hide adult products behind a PIN. Here is what that setting covers on a phone your child also uses, and what it was never built to stop.",
    date: "2026-09-25",
    dateModified: "2026-09-25",
    readTime: "4 min read",
    excerpt:
      "Blinkit now lets you hide adult products behind a PIN. Here is what that setting covers on a phone your child also uses, and what it was never built to stop.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["fantasy-sports-apps-real-money-risk-kids-phone", "threads-parental-controls-shared-phone-india"],
    body: [
      {
        type: "p",
        text: "Blinkit added a parental controls setting in 2025: a six-digit PIN that hides sexual wellness items, nicotine products, and your order history from anyone browsing the app. It is a genuinely useful, overdue feature, and it is the first quick-commerce app in India to ship anything like it. It is also, by its own design, a narrow one. It hides a specific list of product categories from view. It does not stop a child from opening Blinkit at all, and it does not stop an order from going through on anything outside that list. If your child uses your phone, it is worth knowing exactly where that line sits.",
      },
      { type: "h2", text: "What does Blinkit's parental control setting actually hide" },
      {
        type: "p",
        text: "It hides sexual wellness items, nicotine and tobacco-adjacent products, and your past order history, all behind a PIN you set in your profile. Blinkit also sends a notification if someone tries to change the setting. That is the entire scope. Groceries, snacks, toys, electronics, and every other category on the app remain fully visible and fully orderable, PIN or no PIN.",
      },
      { type: "h2", text: "Does the PIN stop my child from opening Blinkit at all" },
      {
        type: "p",
        text: "No. The PIN only gates one settings screen and the categories behind it. Opening the app itself, browsing the rest of the catalog, and adding items to a cart all still work with no PIN prompt at any point. A child handed the phone can use Blinkit normally for anything the setting was not built to hide.",
      },
      {
        type: "cta",
        text: "If you would rather Blinkit just does not open at all during certain hours, that is a different problem to solve.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Can my child still place an order on something else" },
      {
        type: "p",
        text: "Yes, if a saved card or UPI ID is already on the account. This is the part parents usually do not think about until a delivery arrives that nobody remembers ordering. A ten-year-old adding a stack of snacks or a toy to a cart and tapping confirm pay is not doing anything sneaky, they are doing exactly what the app is designed to make easy for anyone holding the phone.",
      },
      { type: "h2", text: "Should you still keep Blinkit off the phone during certain hours" },
      {
        type: "p",
        text: "That depends on how often the phone actually gets handed over unsupervised, and for how long. A five-minute handover while you finish cooking is a different risk than a child holding the phone for an hour while you are out. If it is the second one, hiding a few product categories does not change much. Blocking the app itself for the hours you actually hand the phone over removes the question completely, rather than trusting that the child never taps past the parts Blinkit chose to hide.",
      },
      { type: "h2", text: "What is actually different between hiding items and blocking the app" },
      {
        type: "p",
        text: "Blinkit's setting controls what is visible inside Blinkit. It has no opinion on whether Blinkit opens in the first place. Blocking the app is a different layer entirely: the app never opens, so there is nothing inside it to hide from or order through. The two are not competing solutions, they solve different problems, and a shared phone usually needs both, not one instead of the other.",
      },
      {
        type: "p",
        text: "Blinkit's team built a real fix for a real problem, and it is a good sign that a quick-commerce app is thinking about who else might be holding the phone. It just was not built to answer the question of whether the app should be open at all.",
      },
      {
        type: "cta",
        text: "Add Blinkit to your blocked list for the hours your child usually has the phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What does Blinkit's parental control setting actually hide",
        answer:
          "It hides sexual wellness items, nicotine and tobacco-adjacent products, and past order history, all behind a six-digit PIN set in the profile. Every other category, including groceries, snacks, toys, and electronics, stays fully visible and orderable regardless of the PIN.",
      },
      {
        question: "Does the PIN stop my child from opening Blinkit at all",
        answer:
          "No. The PIN only protects one settings screen and the categories behind it. Opening the app, browsing the rest of the catalog, and adding items to a cart all work with no PIN prompt.",
      },
      {
        question: "Can my child still place an order on something else",
        answer:
          "Yes, if a saved card or UPI ID is already linked to the account. Nothing in the parental control setting stops an order from going through on any category outside the hidden list.",
      },
      {
        question: "Should you still keep Blinkit off the phone during certain hours",
        answer:
          "It depends on how long the phone is typically handed over unsupervised. For longer stretches, hiding a few product categories does not remove the risk of unplanned orders, so blocking the app during those hours is a more complete fix.",
      },
      {
        question: "What is actually different between hiding items and blocking the app",
        answer:
          "Blinkit's setting controls what is visible inside the app once it is open. Blocking the app stops it from opening at all. They solve different problems, and a shared phone usually benefits from both rather than either one alone.",
      },
    ],
  },
  {
    slug: "indian-children-share-parent-phone-study",
    title: "How Many Indian Children Actually Share a Parent's Phone, According to Government Data",
    metaTitle: "How Many Indian Kids Use a Parent's Phone",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/indian-children-share-parent-phone-study.jpg",
    description:
      "A government study of 5,811 children, parents, and teachers found most children who go online do it on a parent's phone, not their own. Here is what it found.",
    date: "2026-09-25",
    dateModified: "2026-09-25",
    readTime: "4 min read",
    excerpt:
      "A government study of 5,811 children, parents, and teachers found most children who go online do it on a parent's phone, not their own. Here is what it found.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["research-kids-screen-time-india-studies", "parental-control-no-phone-of-their-own"],
    body: [
      {
        type: "p",
        text: "If it feels like every other family you know is handing a child their own phone rather than sharing one, a 2021 national study by the National Commission for Protection of Child Rights (NCPCR) suggests the opposite is closer to true. The study surveyed 5,811 people, children, parents and teachers together, across 60 schools in six states. Only 30.2 percent of children aged 8 to 18 owned a smartphone of their own. The rest were going online somehow anyway, and the same study found that 62.6 percent of them were doing it on a parent's phone.",
      },
      { type: "h2", text: "How many Indian children actually own a smartphone" },
      {
        type: "p",
        text: "Just under a third, 30.2 percent, across the full 8 to 18 age range the study covered. That number includes teenagers, who own phones at a much higher rate than younger children, which means the real figure for a primary-school-aged child is almost certainly lower than 30 percent. For most families with a child under twelve, a phone in that child's hand is someone else's phone.",
      },
      { type: "h2", text: "So how are the rest of them getting online" },
      {
        type: "p",
        text: "Through a parent's phone, according to the same study, 62.6 percent of the time. That lines up with what most parents already know from their own house: a child doesn't wait for their own device to want to watch something or play a game, they ask for yours, and you hand it over, usually to buy ten quiet minutes.",
      },
      {
        type: "cta",
        text: "If your phone is the one being borrowed, deciding in advance what it can open matters more than deciding what your child's phone can open.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What does that 62.6 percent actually look like day to day" },
      {
        type: "p",
        text: "It is not one long supervised session. It is the phone at dinner so everyone can eat in peace, the phone in the car, the phone during a video call with grandparents that turns into twenty minutes of a game once the call ends. Every one of those handovers happens on a device that was set up for an adult, with an adult's apps still one tap away, because there was never a separate child device to set up differently in the first place.",
      },
      { type: "h2", text: "Does this match what the parents in the study actually said" },
      {
        type: "p",
        text: "The same NCPCR research also asked parents directly, and a large share reported trying to restrict their child's screen time, which tells you this isn't a case of parents not caring. It's a case of the tools available not matching the situation. Most parental control products are still built around the assumption of a second device with its own account, which is exactly the setup most of these families do not have.",
      },
      { type: "h2", text: "What does this mean for how you actually set your phone up" },
      {
        type: "p",
        text: "It means the shared-phone case isn't a small edge case to plan around later, it's the more common situation according to the government's own numbers. If your child is one of the majority using your phone rather than their own, the useful question isn't which app to install on a device they don't have, it's what your own phone should and shouldn't open when it's in their hands.",
      },
      {
        type: "p",
        text: "The 30 percent of children with their own device get their own set of solutions built for them already. This study is a reminder that the other 70 percent, most of them going online through a parent's phone, are the more typical family, not the exception.",
      },
      {
        type: "cta",
        text: "Set up your own phone for the moments your child is holding it, not just for yourself.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How many Indian children actually own a smartphone",
        answer:
          "Just under a third, 30.2 percent, across the full 8 to 18 age range studied by the NCPCR in 2021. Since teenagers own phones at a much higher rate than younger children, the real figure for a primary-school-aged child is almost certainly lower than 30 percent.",
      },
      {
        question: "So how are the rest of them getting online",
        answer:
          "Through a parent's phone, according to the same study, 62.6 percent of the time. A child who wants to watch something or play a game typically asks for the nearest adult's phone rather than waiting for a device of their own.",
      },
      {
        question: "What does that 62.6 percent actually look like day to day",
        answer:
          "Short, frequent handovers rather than one long supervised session: the phone at dinner, in the car, or after a video call with relatives turns into extra screen time, all on a device set up for an adult with adult apps still one tap away.",
      },
      {
        question: "Does this match what the parents in the study actually said",
        answer:
          "Yes. A large share of parents in the same NCPCR research reported trying to restrict their child's screen time, suggesting the gap is not a lack of concern but a lack of tools built for a shared, rather than a personal, device.",
      },
      {
        question: "What does this mean for how you actually set your phone up",
        answer:
          "If your child is one of the majority using your phone rather than their own, the more useful question is what your own phone should and should not open while your child is holding it, rather than looking for a product built around a second device you do not have.",
      },
    ],
  },
  {
    slug: "google-play-age-verification-shared-phone",
    title: "Google Play Now Verifies Age. Does That Change Anything on Your Phone",
    metaTitle: "Google Play's Age Check on a Shared Phone",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/google-play-age-verification-shared-phone.jpg",
    description:
      "Google Play now verifies age before letting you download certain apps. Here is whose age actually gets checked, and what that means if your child uses your phone.",
    date: "2026-09-26",
    dateModified: "2026-09-26",
    readTime: "4 min read",
    excerpt:
      "Google Play now verifies age before letting you download certain apps. Here is whose age actually gets checked, and what that means if your child uses your phone.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["google-family-link-supervision-change-shared-phone", "is-bgmi-safe-for-kids-shared-phone"],
    body: [
      {
        type: "p",
        text: "Google has started asking Play Store users to verify their age in 2026, including in India, before letting them download certain age-restricted apps or see certain content in the store. It sounds like exactly the kind of gate that should stop a child from installing something they shouldn't. On a phone that belongs to the child and is signed into their own account, it mostly is. On a phone that belongs to a parent, and is signed into a parent's Google account, it checks something else entirely.",
      },
      { type: "h2", text: "What is Google Play's new age verification actually checking" },
      {
        type: "p",
        text: "It verifies the age tied to the Google account signed into the Play Store, using government ID, a credit card, a selfie-based check, or a third-party verification service, depending on what is offered in that market. Once an account is verified, the Play Store uses that verified age to decide what apps and content it will show or allow that account to download.",
      },
      { type: "h2", text: "Whose age gets verified, yours or your child's" },
      {
        type: "p",
        text: "Whoever's Google account is signed in. On a phone a parent bought, set up, and uses every day, that account belongs to the parent, and it gets verified as an adult account the moment the parent completes the check. The system has no way of knowing that a ten-year-old is holding the phone for the next twenty minutes. It only knows which account is active.",
      },
      {
        type: "cta",
        text: "An account-level check cannot tell who is holding the phone right now. A blocked list can.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "So can my child still download an age-restricted app on my phone" },
      {
        type: "p",
        text: "Yes, if your account is already verified as an adult, which for most parents it will be. Handing your phone to your child does not switch the Play Store into a different mode. It still sees the same verified adult account it always does, so it applies the same adult permissions, regardless of who is actually tapping install.",
      },
      { type: "h2", text: "What does this actually protect against, then" },
      {
        type: "p",
        text: "It is a real and useful fix for a real problem: a child using their own account on their own device to buy or download something meant for adults. It closes that gap properly. It was just never designed to solve the different problem of a child temporarily using someone else's already-verified phone, because from the Play Store's point of view, there is nothing temporary about that account. It is simply the account that is signed in.",
      },
      { type: "h2", text: "What should you actually do differently on a shared phone" },
      {
        type: "p",
        text: "Treat the phone's blocked list as the layer that matters when your child is holding your phone, and let Google's age verification do its job for your own account the rest of the time. The two are not in conflict, they are just answering different questions: one asks whether this account is old enough, the other asks whether this app should be reachable at all right now, no matter whose account is signed in.",
      },
      {
        type: "p",
        text: "Age verification at the account level is a genuine step forward for the accounts it was built for. It just was not built with a shared family phone in mind, and knowing that up front saves you from assuming a setting is protecting you when it isn't.",
      },
      {
        type: "cta",
        text: "Add anything age-sensitive to your blocked list, so it stays closed no matter which account is signed in.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is Google Play's new age verification actually checking",
        answer:
          "It verifies the age tied to the Google account signed into the Play Store, using government ID, a credit card, a selfie-based check, or a third-party verification service depending on the market. Once verified, the Play Store uses that age to decide what it will show or let that account download.",
      },
      {
        question: "Whose age gets verified, yours or your child's",
        answer:
          "Whoever's Google account is signed in. On a phone a parent set up and uses daily, that is the parent's account, verified as an adult once the parent completes the check. The system has no way to know a child is holding the phone at any given moment.",
      },
      {
        question: "So can my child still download an age-restricted app on my phone",
        answer:
          "Yes, if the account is already verified as an adult, which it will be for most parents. Handing the phone over does not change which account is active, so the same adult permissions apply no matter who is actually tapping install.",
      },
      {
        question: "What does this actually protect against, then",
        answer:
          "A child using their own account on their own device to download something meant for adults. It was not designed to catch a child temporarily using an already-verified adult's phone, since the Play Store has no concept of a temporary handover.",
      },
      {
        question: "What should you actually do differently on a shared phone",
        answer:
          "Rely on a blocked list for the moments your child is holding your phone, and let Google's account-level age verification do its separate job the rest of the time. They answer different questions and work well together rather than replacing each other.",
      },
    ],
  },
  {
    slug: "what-happens-when-monitoring-app-gets-hacked",
    title: "What Happens When a Phone Monitoring App Gets Hacked",
    metaTitle: "What Happens When a Monitoring App Is Hacked",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/what-happens-when-monitoring-app-gets-hacked.jpg",
    description:
      "At least 27 phone monitoring apps have been breached since 2017. Here is what was actually exposed, and why sending your child's data anywhere carries that risk.",
    date: "2026-09-26",
    dateModified: "2026-09-26",
    readTime: "4 min read",
    excerpt:
      "At least 27 phone monitoring apps have been breached since 2017. Here is what was actually exposed, and why sending your child's data anywhere carries that risk.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["how-to-tell-parental-control-app-from-spyware", "monitoring-vs-blocking-parental-apps"],
    body: [
      {
        type: "p",
        text: "Apps that watch a child's messages, location, and photos so a parent can view them from another screen have to store that data somewhere first. That somewhere is usually a server run by the company that made the app. A February 2026 TechCrunch investigation counted at least 27 of these monitoring companies that have been breached or leaked data since 2017, with eight shutting down entirely after it happened. This is not one bad actor. It is a pattern built into how this whole category of app works.",
      },
      { type: "h2", text: "How often do these apps actually get breached" },
      {
        type: "p",
        text: "Regularly enough that it has become its own recurring news story rather than a one-off scandal. TechCrunch's tally names companies including mSpy, Cocospy, Spyic, Spyzie, FlexiSpy, and pcTattletale among more than two dozen with a reported breach or leak on record since 2017. Some of these companies are still operating. Eight are not, having shut down after their breach became public.",
      },
      { type: "h2", text: "What kind of data is sitting on their servers when that happens" },
      {
        type: "p",
        text: "Whatever the app was built to capture: text messages, call logs, photos, GPS location history, browser history, and in some cases audio recordings or live screenshots pulled straight off the monitored phone. When one of these companies gets breached, that is what ends up exposed, not some abstract account record, the actual private content the app collected.",
      },
      {
        type: "cta",
        text: "An app that has nothing to send has nothing that can leak.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Whose data is it, the parent's or the child's" },
      {
        type: "p",
        text: "Both, and that is exactly the problem. TechCrunch's reporting describes two groups exposed in these breaches: the person who installed the app, whose payment and account details get caught up in it, and the person being monitored, whose actual messages, photos, and location were sitting on that server the whole time. If a parent installs one of these apps to watch over a child, a breach exposes the child's private information, not just the parent's.",
      },
      { type: "h2", text: "Why does this happen so often to this specific category of app" },
      {
        type: "p",
        text: "Because the entire product only works by moving a child's private activity off their phone and onto a server somewhere else, usually so a parent can check it later from a different device. That server is a target the moment it exists, and it stays a target for as long as the company keeps operating. The risk is not a bug some of these companies happened to have. It is a direct consequence of the model.",
      },
      { type: "h2", text: "What is the alternative to sending your child's data anywhere at all" },
      {
        type: "p",
        text: "An app that never collects the data in the first place has nothing sitting on a server for anyone to breach. CrenelView works entirely on the phone it is installed on, has no account or sign-in, and has no INTERNET permission at all, which is checkable in the phone's own permissions list. It cannot send a message log, a photo, or a location anywhere, not because of a policy promise, but because there is no channel for it to do that through.",
      },
      {
        type: "p",
        text: "Before installing anything that watches over your child, it is worth asking one plain question: where does the information go once it leaves the phone. If the honest answer is \"to a server,\" ask what happens to that server when, not if, it eventually gets breached.",
      },
      {
        type: "cta",
        text: "Nothing to collect means nothing to expose. See how CrenelView works entirely on your phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How often do these apps actually get breached",
        answer:
          "Regularly enough to be a recurring pattern rather than a single incident. A February 2026 TechCrunch investigation named more than two dozen phone monitoring companies, including mSpy, Cocospy, Spyic, Spyzie, FlexiSpy, and pcTattletale, with a reported breach or leak since 2017, and said eight of them have shut down since.",
      },
      {
        question: "What kind of data is sitting on their servers when that happens",
        answer:
          "Whatever the app was built to capture, including text messages, call logs, photos, GPS location history, browser history, and in some cases audio recordings or live screenshots. A breach exposes that actual content, not just account records.",
      },
      {
        question: "Whose data is it, the parent's or the child's",
        answer:
          "Both. Reporting on these breaches describes exposure of the installer's payment and account details alongside the monitored person's actual messages, photos, and location, meaning a breach exposes the child's private information along with the parent's.",
      },
      {
        question: "Why does this happen so often to this specific category of app",
        answer:
          "Because these apps work by moving a child's private activity off their phone onto a company server so a parent can check it later from a different screen. That server is a target for as long as the company operates, which is a consequence of the model, not a bug in any one app.",
      },
      {
        question: "What is the alternative to sending your child's data anywhere at all",
        answer:
          "An app that never collects the data has nothing on a server to breach. CrenelView runs entirely on the phone it is installed on, with no account and no INTERNET permission at all, so there is no channel through which any data could be sent anywhere.",
      },
    ],
  },
  {
    slug: "how-to-explain-blocked-app-to-child",
    title: "How to Explain to Your Child Why an App Is Blocked",
    metaTitle: "How to Explain a Blocked App to Your Child",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/how-to-explain-blocked-app-to-child.jpg",
    description:
      "Blocking an app is the easy part. Here is how to actually explain it to a young child so it does not feel like a punishment, at different ages.",
    date: "2026-09-26",
    dateModified: "2026-09-26",
    readTime: "4 min read",
    excerpt:
      "Blocking an app is the easy part. Here is how to actually explain it to a young child so it does not feel like a punishment, at different ages.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["why-your-child-cries-when-you-say-no-to-the-phone", "should-screen-time-be-earned-or-scheduled"],
    body: [
      {
        type: "p",
        text: "Blocking an app takes a few taps. Explaining it to a five-year-old who just watched their game disappear off the home screen takes a lot more than that. Most of the actual conflict around blocked apps has nothing to do with the block itself, it comes from a child feeling like something was taken away without a reason they can understand. A short, honest explanation, pitched at their actual age, prevents most of that conflict before it starts.",
      },
      { type: "h2", text: "Why does explaining the block even matter, why not just block it silently" },
      {
        type: "p",
        text: "Because a child who does not understand why something disappeared assumes it is arbitrary, and an arbitrary rule is the one children push back against hardest. A block they understand, even a simple one, feels like a decision. A block they do not understand feels like something happening to them. The difference shows up immediately in how much they argue about it.",
      },
      { type: "h2", text: "What age is old enough to actually understand the reason" },
      {
        type: "p",
        text: "Even a three or four year old can understand a simple version: \"this one is only for after dinner\" or \"this game is not for right now.\" You do not need to explain algorithms or attention or anything abstract. By six or seven, most children can handle a slightly fuller reason, like why a specific app is blocked rather than another one. The explanation should grow with them, not arrive all at once before they are ready for it.",
      },
      {
        type: "cta",
        text: "A clear, consistent block is easier to explain than a rule you are inventing in the moment.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What should you actually say when they ask why" },
      {
        type: "p",
        text: "Keep it short, keep it about the phone or the time, not about them. \"This one is blocked right now, we can look at it again after 6\" lands very differently from \"you play this too much.\" The first is a fact about the schedule. The second is a judgment about the child, and it is the version most likely to turn a two-minute question into a longer argument. Say the true, simple thing and stop there, rather than justifying it further than the child actually asked.",
      },
      { type: "h2", text: "What if they push back or get upset anyway" },
      {
        type: "p",
        text: "That is a completely normal reaction and not a sign the explanation failed. A child can understand a reason and still be disappointed by it, the same way an adult can understand why a flight got delayed and still be annoyed about it. Let the disappointment happen without arguing them out of it, and hold the answer steady. Changing the rule because a child is upset teaches that being upset enough is what changes the rule, which makes the next conversation harder, not easier.",
      },
      { type: "h2", text: "Should the reason change as they get older" },
      {
        type: "p",
        text: "Yes, and it should change well before you think it needs to. A reason that made sense to a five-year-old (\"it's not the right time\") starts to feel thin to a nine-year-old, who is capable of understanding something closer to the actual reason, whether that is protecting sleep, managing how much time goes to one thing, or steering clear of something not meant for their age. Revisiting the explanation occasionally, rather than repeating the same line for years, is part of what keeps it from sounding like an excuse.",
      },
      {
        type: "p",
        text: "None of this makes a child instantly happy about a blocked app. It does make the disagreement about the actual thing, the app being unavailable right now, rather than about whether the rule makes any sense at all.",
      },
      {
        type: "cta",
        text: "Set the schedule once, explain it once, and let it hold without you having to enforce it every time.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does explaining the block even matter, why not just block it silently",
        answer:
          "A child who does not understand why something disappeared assumes it is arbitrary, and an arbitrary rule is what children push back against hardest. A block they understand feels like a decision, one they do not feels like something happening to them, and that difference shows up in how much they argue about it.",
      },
      {
        question: "What age is old enough to actually understand the reason",
        answer:
          "Even a three or four year old can understand a simple version, such as \"this one is only for after dinner.\" By six or seven, most children can handle a fuller reason. The explanation should grow with the child rather than arrive all at once.",
      },
      {
        question: "What should you actually say when they ask why",
        answer:
          "Keep it short and about the phone or the time, not about the child. \"This one is blocked right now, we can look at it again after 6\" is a fact about the schedule, which lands better than a judgment about how much they play.",
      },
      {
        question: "What if they push back or get upset anyway",
        answer:
          "That is a normal reaction, not a sign the explanation failed. A child can understand a reason and still be disappointed by it. Letting the disappointment happen without changing the rule keeps future conversations easier, not harder.",
      },
      {
        question: "Should the reason change as they get older",
        answer:
          "Yes, and earlier than most parents expect. A reason that satisfies a five-year-old starts to feel thin to a nine-year-old, who can understand something closer to the actual reason behind the rule.",
      },
    ],
  },
  {
    slug: "nonsense-videos-long-term-effect-young-child",
    title: "What Does Watching Endless Low-Quality Videos Actually Do to a Young Child",
    metaTitle: "What Nonsense Videos Do to a Young Child",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/nonsense-videos-long-term-effect-young-child.jpg",
    description:
      "The research on fast, low-quality video is more specific than \"screens are bad.\" Here is what it actually says about young children, and what does not hold up.",
    date: "2026-09-26",
    dateModified: "2026-09-26",
    readTime: "5 min read",
    excerpt:
      "The research on fast, low-quality video is more specific than \"screens are bad.\" Here is what it actually says about young children, and what does not hold up.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["why-reels-shorts-harder-to-stop-than-video", "screen-time-by-age-2026-guidelines"],
    body: [
      {
        type: "p",
        text: "Every parent has seen the version of this: a child staring at an endless string of loud, fast, plotless clips, one sliding into the next before the last one even finished, none of it really about anything. It looks unhealthy, and a lot of parents worry it is doing real damage. The honest answer from the research is more specific than \"screens are bad,\" and more useful too, because it points at what actually seems to matter and what does not.",
      },
      { type: "h2", text: "What actually counts as \"nonsense\" content in this case" },
      {
        type: "p",
        text: "The research draws a real line here, and it is not about screens versus no screens. It is about content that moves fast, has no real story, and often shows things that could not happen in real life, next to slower, narrative content a child can actually follow and learn from. The American Academy of Pediatrics puts it plainly: young children \"do not understand\" fast-paced programs as well as slower ones, and recommends avoiding them along with apps full of distracting content.",
      },
      { type: "h2", text: "What happens to a child's attention right after watching it" },
      {
        type: "p",
        text: "Here the evidence is genuinely solid, just narrower than people assume. Studies that showed young children brief clips found that content full of impossible events, not fast editing on its own, left children performing worse on attention and self-control tasks immediately afterward. The effect was real but short-lived, more like a child needing a few minutes to settle before they can follow instructions than any kind of lasting change.",
      },
      {
        type: "cta",
        text: "The easiest fix for the short-term effect is controlling when the video stops, not just what is on it.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Is there real evidence of a longer-term effect, or is that overstating it" },
      {
        type: "p",
        text: "This is the part worth being honest about. The strongest, most repeated evidence is short-term and often measured right after a single viewing session in a research setting, not across months or years of a child's actual life. Longer-term studies exist and do find associations between heavy low-quality video use and weaker attention or language outcomes later, but they are observational, which means they show a pattern, not proof that the videos alone caused it. Claiming the research proves permanent brain damage from short-form video goes further than what the research actually supports.",
      },
      { type: "h2", text: "Why background, passive video seems to matter more than any single clip" },
      {
        type: "p",
        text: "The AAP's own review points to something less dramatic than \"damaged attention span\" and more concrete: video playing in the background measurably cuts down how many words a child hears an adult say, and how many back-and-forth exchanges happen between them. That crowding-out effect, less real conversation during exactly the years language develops fastest, is the mechanism with the clearest evidence behind it, not a single scary claim about the content itself.",
      },
      { type: "h2", text: "What is actually within a parent's control here" },
      {
        type: "p",
        text: "Two things the evidence points at directly: what kind of content is on, and how much of the day it replaces talking, playing, or reading instead. A slower, story-based video a child can actually follow is a genuinely different experience than an endless feed with no plot, and neither one is harmless in unlimited quantity if it is quietly replacing the conversation and play a young child's development actually depends on.",
      },
      {
        type: "p",
        text: "None of this means one loud, plotless video ruins a child's day. It means the habit of endless autoplay, day after day, is worth interrupting on purpose, because the actual mechanism at risk is time and conversation, not one clip doing lasting harm.",
      },
      {
        type: "cta",
        text: "Set a time window for video apps, so autoplay has a natural stopping point.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What actually counts as \"nonsense\" content in this case",
        answer:
          "Content that moves fast, has no real story, and often shows impossible events, as opposed to slower, narrative content a child can follow and learn from. The American Academy of Pediatrics says young children do not understand fast-paced programs as well as slower ones and recommends avoiding them.",
      },
      {
        question: "What happens to a child's attention right after watching it",
        answer:
          "Studies showing young children brief clips found that content full of impossible events, more than fast editing itself, led to worse performance on attention and self-control tasks right afterward. The effect was real but short-lived, similar to a child needing time to settle before following instructions.",
      },
      {
        question: "Is there real evidence of a longer-term effect, or is that overstating it",
        answer:
          "The strongest evidence is short-term, often measured right after one viewing session. Longer-term studies find associations between heavy low-quality video use and weaker attention or language outcomes, but they are observational, showing a pattern rather than proof the videos alone caused it.",
      },
      {
        question: "Why does background, passive video seem to matter more than any single clip",
        answer:
          "Because it measurably reduces how many words a child hears an adult say and how many back-and-forth exchanges happen between them, according to AAP-reviewed research. That crowding-out of real conversation during key language-development years has the clearest evidence behind it.",
      },
      {
        question: "What is actually within a parent's control here",
        answer:
          "What kind of content is on, and how much of the day it replaces talking, playing, or reading instead. A slower, story-based video is a different experience than an endless plotless feed, and the real risk is either one quietly replacing conversation and play in large quantities.",
      },
    ],
  },
  {
    slug: "family-link-website-blocking-chrome-only",
    title: "Family Link's Website Blocking Only Works in One Browser, and It Is Not the Only One on the Phone",
    metaTitle: "Family Link's Website Blocking Only Works in Chrome",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/family-link-website-blocking-chrome-only.jpg",
    description:
      "Family Link's site blocking and SafeSearch only apply inside Chrome. Here is what that means if your child opens any other browser on the same phone.",
    date: "2026-09-29",
    dateModified: "2026-09-29",
    readTime: "4 min read",
    excerpt:
      "Family Link's site blocking and SafeSearch only apply inside Chrome. Here is what that means if your child opens any other browser on the same phone.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["web-filter-doesnt-cover-second-browser", "google-family-link-supervision-change-shared-phone"],
    externalSources: [
      { label: "Chrome and your child's Google Account (Google Support)", url: "https://support.google.com/families/answer/7087030" },
    ],
    body: [
      {
        type: "p",
        text: "Family Link lets a parent block specific sites, turn on SafeSearch, and approve or deny websites one by one. All of it works, and works well, inside Chrome. Google's own support page for Family Link says plainly that a child \"may also have access to other browsers where Family Link settings for Chrome won't apply.\" That is not a bug report from a frustrated parent, it is Google describing its own product's actual boundary.",
      },
      { type: "h2", text: "What exactly does Family Link block, and where" },
      {
        type: "p",
        text: "Explicit-site blocking, SafeSearch enforcement, and the approve-or-block list for individual sites all live inside the Chrome app, tied to the child's signed-in managed account. Turn any of these on and Chrome behaves exactly as expected: blocked sites stay blocked, explicit content gets filtered, SafeSearch stays locked on.",
      },
      { type: "h2", text: "So what happens if my child opens a different browser" },
      {
        type: "p",
        text: "Nothing from Family Link follows them there. Samsung Internet, Firefox, Edge, a second copy of Chrome under a different profile, whatever else happens to be on the phone, none of it is aware that Family Link exists. The child does not need to disable anything or find a workaround. They just tap a different icon.",
      },
      {
        type: "cta",
        text: "Blocking the browser app itself removes this gap entirely, no matter which one your child opens.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Is this only a problem on Android, or does it happen on a computer too" },
      {
        type: "p",
        text: "Google's own documentation says the same limitation applies on Windows, Mac, and Linux computers as well: a child can switch to another browser or another profile there too, and Family Link's Chrome settings simply do not apply outside Chrome. This is not an Android quirk. It is how Family Link's website controls work everywhere they exist.",
      },
      { type: "h2", text: "Does a second browser usually appear on purpose, or is it already there" },
      {
        type: "p",
        text: "Often it is already on the phone before anyone thinks about it. Many Android phones ship with a second browser preinstalled by the manufacturer, alongside Chrome, out of the box. A child does not need to go looking for a workaround if one is already sitting on the home screen next to the one that is actually being managed.",
      },
      { type: "h2", text: "What actually closes this gap on a shared phone" },
      {
        type: "p",
        text: "Blocking the browser app itself, not just the sites inside it. A block on the app means it does not open at all during the hours you choose, so it does not matter which browser your child taps, because none of them get the chance to load a page in the first place. That sidesteps the whole question of which browser has which setting turned on.",
      },
      {
        type: "p",
        text: "Family Link's website controls do exactly what they say they do, inside Chrome. The gap is not a flaw in that promise, it is what happens the moment a phone has more than one way to reach the internet.",
      },
      {
        type: "cta",
        text: "Add every browser on the phone to your blocked list, not just the one you normally think of.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What exactly does Family Link block, and where",
        answer:
          "Explicit-site blocking, SafeSearch enforcement, and the approve-or-block list for individual sites all operate inside the Chrome app, tied to the child's signed-in managed account. Within Chrome, these controls work as expected.",
      },
      {
        question: "So what happens if my child opens a different browser",
        answer:
          "None of Family Link's settings follow them there. Any other browser on the phone, whether preinstalled by the manufacturer or added later, has no awareness that Family Link exists, so nothing is blocked or filtered inside it.",
      },
      {
        question: "Is this only a problem on Android, or does it happen on a computer too",
        answer:
          "Google's own documentation states the same limitation applies on Windows, Mac, and Linux: switching to a different browser or profile there also steps outside Family Link's Chrome-based controls.",
      },
      {
        question: "Does a second browser usually appear on purpose, or is it already there",
        answer:
          "Often it is already installed. Many Android phones ship with a second browser preloaded by the manufacturer alongside Chrome, so a child may encounter one without deliberately looking for a workaround.",
      },
      {
        question: "What actually closes this gap on a shared phone",
        answer:
          "Blocking the browser app itself rather than only the sites inside one browser. If a browser cannot open at all during certain hours, it does not matter which one a child taps, since none of them get the chance to load a page.",
      },
    ],
  },
  {
    slug: "right-age-first-smartphone-child",
    title: "What Is the Right Age to Give a Child Their Own Phone",
    metaTitle: "What Is the Right Age for a Child's First Phone",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/right-age-first-smartphone-child.jpg",
    description:
      "There is no single correct age for a child's first phone, according to pediatric guidance. Here is what actually matters more, and what to do in the meantime.",
    date: "2026-09-29",
    dateModified: "2026-09-29",
    readTime: "4 min read",
    excerpt:
      "There is no single correct age for a child's first phone, according to pediatric guidance. Here is what actually matters more, and what to do in the meantime.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["parental-control-no-phone-of-their-own", "screen-time-by-age-2026-guidelines"],
    externalSources: [
      { label: "Appropriate Age to Introduce a Mobile Device (American Academy of Pediatrics)", url: "https://www.aap.org/en/patient-care/media-and-children/center-of-excellence-on-social-media-and-youth-mental-health/qa-portal/qa-portal-library/qa-portal-library-questions/appropriate-age-to-introduce-a-mobile-device/" },
    ],
    body: [
      {
        type: "p",
        text: "Every parent asks this eventually, usually while comparing notes with other parents at school pickup. The honest answer, according to the American Academy of Pediatrics, is that there is no single correct age. Readiness varies enough between individual children that the AAP deliberately avoids naming one, and points parents toward a different, more useful question instead.",
      },
      { type: "h2", text: "Is there an actual official recommended age" },
      {
        type: "p",
        text: "No. The AAP states plainly that there is no single perfect age for a child's first phone, and frames readiness as a matter of the child's own maturity and the parent's own readiness to support healthy use, not a birthday to circle on a calendar. Most guidance in this space, from pediatric groups to teens themselves, lands on the same point: the number matters less than what happens around it.",
      },
      { type: "h2", text: "What matters more than age, according to pediatric guidance" },
      {
        type: "p",
        text: "Whether the child already handles responsibility well without one. The AAP points to concrete signs rather than a number: managing homework and other tasks without constant reminders, being able to apologize and show empathy after a conflict, and actually following the technology rules already in place at home. A child who struggles with those is not made ready by turning eleven or thirteen. A child who has those in hand is a different conversation regardless of the exact age.",
      },
      {
        type: "cta",
        text: "Until that day comes, your own phone is the one doing the job. It is worth setting up for that.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What age do most kids actually get their first phone" },
      {
        type: "p",
        text: "Survey data cited by the AAP puts the average first phone somewhere between 12 and 13 in many markets, with some studies finding an average closer to 11 and a half. None of that means a younger child getting one struggles, or an older child getting one earlier was rushed. It is a description of what typically happens, not a target to aim for.",
      },
      { type: "h2", text: "What are the concrete signs a child might actually be ready" },
      {
        type: "p",
        text: "Beyond the AAP's readiness markers, a few practical questions tend to tell you more than age does: does the child already ask before using someone else's phone rather than just taking it, do they put it down again without an argument most of the time, and do they understand, in their own words, why a rule exists rather than just that it exists. None of these have anything to do with a birthday.",
      },
      { type: "h2", text: "What should happen on the phone your child uses until then" },
      {
        type: "p",
        text: "Until a child has their own device, they are almost certainly using yours, and the same questions about readiness do not disappear just because there is no separate phone yet. A schedule for which apps are reachable and when, set on your own phone, does the job a personal device would eventually do, without needing to answer the age question early.",
      },
      {
        type: "p",
        text: "There is no perfect day this becomes true. There is a long stretch beforehand where the answer is \"not yet,\" and what happens on your phone during that stretch matters just as much as whichever age the actual handover ends up happening at.",
      },
      {
        type: "cta",
        text: "Set your own phone up for the years before that handover, not just for yourself.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is there an actual official recommended age",
        answer:
          "No. The American Academy of Pediatrics states there is no single perfect age for a child's first phone, and frames readiness around the child's maturity and the parent's readiness to support healthy use, rather than a specific birthday.",
      },
      {
        question: "What matters more than age, according to pediatric guidance",
        answer:
          "Whether the child already handles responsibility well: managing homework and tasks without constant reminders, showing empathy after a conflict, and following existing technology rules at home. These indicators matter more than the number of years a child has been alive.",
      },
      {
        question: "What age do most kids actually get their first phone",
        answer:
          "Survey data cited by the AAP places the average first phone around 12 to 13 in many markets, with some studies finding an average closer to 11 and a half. This describes a typical pattern, not a recommended target.",
      },
      {
        question: "What are the concrete signs a child might actually be ready",
        answer:
          "Whether they ask before using someone else's phone rather than taking it, put it down again without an argument most of the time, and can explain in their own words why a household rule exists rather than just that it does.",
      },
      {
        question: "What should happen on the phone your child uses until then",
        answer:
          "The same readiness questions still apply, just applied to your own phone. A schedule for which apps are reachable and when does the job a personal device would eventually do, without needing to resolve the age question early.",
      },
    ],
  },
  {
    slug: "supreme-court-social-media-minors-rules-india",
    title: "India May Get New Rules for Kids on Social Media. Here Is What Is Actually Being Proposed",
    metaTitle: "India May Get New Rules for Kids on Social Media",
    tags: [],
    ogImage: "/blog-images/supreme-court-social-media-minors-rules-india.jpg",
    description:
      "The Supreme Court has pushed the government to consider new safeguards for minors on social media. Here is exactly what is being discussed, and what is not yet true.",
    date: "2026-09-29",
    dateModified: "2026-09-29",
    readTime: "4 min read",
    excerpt:
      "The Supreme Court has pushed the government to consider new safeguards for minors on social media. Here is exactly what is being discussed, and what is not yet true.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["karnataka-social-media-ban-under-16-status", "dpdp-act-children-data-apps-india"],
    externalSources: [
      { label: "No social media for under-18s? Centre mulls new safeguards after SC raises concern (Business Today)", url: "https://www.businesstoday.in/india/story/no-social-media-for-under-18s-centre-mulls-new-safeguards-after-sc-raises-concern-558513-2026-09-29" },
    ],
    body: [
      {
        type: "p",
        text: "The Supreme Court has raised a real concern: children under 18 in India can currently open a social media account and agree to a platform's terms on their own, even though Indian law does not let a minor enter a binding contract that way. The government has told the court that something will be done about it. As of now, nothing has actually changed yet, and it is worth being precise about what is happening versus what might happen.",
      },
      { type: "h2", text: "What did the Supreme Court actually say" },
      {
        type: "p",
        text: "The court pointed out that platforms built under one country's legal framework, largely the United States, have effectively imported that framework's age rules into India, even though Indian law treats a minor's ability to agree to a contract differently. One judge on the bench specifically flagged this mismatch as something Indian regulation has not caught up to.",
      },
      { type: "h2", text: "What is the government considering doing about it" },
      {
        type: "p",
        text: "The Solicitor General told the court that the Centre would examine the issue and that \"something can be done, something should be done and will be done,\" a commitment to look at binding rules rather than the voluntary steps platforms currently rely on. The court has directed the government to look specifically at whether existing intermediary rules could be used to require this, rather than starting from scratch.",
      },
      {
        type: "cta",
        text: "Whatever the final rule looks like, it will almost certainly work at the account level, not the device level.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Is this a ban, or something else" },
      {
        type: "p",
        text: "Not a ban. The petition behind this case is specifically not asking for social media to be off-limits to minors entirely. It is asking for real safeguards: age verification, parental consent before an account is created, and changes to how the platforms themselves handle a minor user, rather than removing access altogether.",
      },
      { type: "h2", text: "Is this actually law right now" },
      {
        type: "p",
        text: "No, and that distinction matters. This is a court pushing the government to examine the question and report back, not a rule already in force. Nothing about how a child can currently sign up for a social media account has changed as a result of this specific case, at least not yet. Any actual requirement would need to come from a government rule or a further court order.",
      },
      { type: "h2", text: "What would this likely mean for a shared family phone, based on how similar rules work" },
      {
        type: "p",
        text: "Every account-based safeguard India has rolled out so far, whether it is a supervised account setup, an age check tied to a sign-in, or a parental-consent flow, checks the account that is active, not who is physically holding the device in that moment. If this eventually becomes a real requirement, it would most likely follow the same shape: a child using a parent's already-signed-in adult account would not automatically be affected by a rule built around minors creating their own accounts.",
      },
      {
        type: "p",
        text: "Nothing here is settled yet, and it is worth watching rather than acting on. What is already true, regardless of how this plays out, is that a shared phone signed into an adult's account sits outside most account-level protections built for a minor's own account, which is exactly the gap worth planning for separately.",
      },
      {
        type: "cta",
        text: "Cover the shared-phone gap yourself, rather than waiting to see how this rule turns out.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What did the Supreme Court actually say",
        answer:
          "The court noted that platforms built under a different country's legal framework, largely the United States, have effectively carried that framework's age rules into India, despite Indian law treating a minor's ability to enter into an agreement differently. A judge on the bench flagged this specific mismatch.",
      },
      {
        question: "What is the government considering doing about it",
        answer:
          "The Solicitor General told the court the Centre would examine the issue and committed to looking at binding rules rather than voluntary steps. The court asked the government to look at whether existing intermediary rules could already be used to require this.",
      },
      {
        question: "Is this a ban, or something else",
        answer:
          "Not a ban. The underlying petition specifically does not ask for social media to be off-limits to minors. It asks for safeguards such as age verification and parental consent before account creation, rather than removing access.",
      },
      {
        question: "Is this actually law right now",
        answer:
          "No. This is a court directing the government to examine the question and report back, not a rule already in force. Nothing about how a child can currently create a social media account has changed because of this case, at least not yet.",
      },
      {
        question: "What would this likely mean for a shared family phone, based on how similar rules work",
        answer:
          "Account-based safeguards rolled out in India so far check the account that is signed in, not who is holding the device. A future rule built around minors creating their own accounts would most likely follow the same pattern, leaving a child using an already-signed-in adult account largely unaffected.",
      },
    ],
  },
  {
    slug: "screen-time-before-bed-child-sleep",
    title: "Does a Phone Before Bed Actually Ruin Your Child's Sleep",
    metaTitle: "Does a Phone Before Bed Ruin Your Child's Sleep?",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/screen-time-before-bed-child-sleep.jpg",
    description:
      "It is not just blue light. Here is what pediatric guidance actually says happens when a child uses a phone right before bed, and what to change first.",
    date: "2026-09-30",
    dateModified: "2026-09-30",
    readTime: "4 min read",
    excerpt:
      "It is not just blue light. Here is what pediatric guidance actually says happens when a child uses a phone right before bed, and what to change first.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-time-by-age-2026-guidelines", "block-apps-only-at-bedtime"],
    externalSources: [
      { label: "Screen Time Affecting Sleep (American Academy of Pediatrics)", url: "https://www.aap.org/en/patient-care/media-and-children/center-of-excellence-on-social-media-and-youth-mental-health/qa-portal/qa-portal-library/qa-portal-library-questions/screen-time-affecting-sleep/" },
    ],
    body: [
      {
        type: "p",
        text: "Most parents have a version of this fight: the phone comes out for \"just a few minutes\" before bed, and forty-five minutes later a wired, wide-awake child is still holding it. According to the American Academy of Pediatrics, that is not a coincidence, and blue light is just one piece of it. Two separate things are working against sleep at the same time, and they call for different fixes.",
      },
      { type: "h2", text: "Is it actually the blue light, or something else" },
      {
        type: "p",
        text: "Both, and the AAP is specific about it. The light screens give off can disrupt melatonin, the hormone that signals it is time to sleep, but the content itself does something just as disruptive: an action-heavy game or an intense video raises a child's alertness and heart rate directly, which has nothing to do with the light and everything to do with what is actually on the screen.",
      },
      { type: "h2", text: "Does it matter what kind of video or game it is" },
      {
        type: "p",
        text: "Yes, more than most parents assume. The AAP specifically recommends choosing relaxing or funny content over anything stimulating during the evening hours, rather than treating all screen use in the last hour before bed the same way. A slow, familiar show and a fast-paced competitive game are not the same risk to a child's ability to fall asleep, even at the same volume and the same screen.",
      },
      {
        type: "cta",
        text: "A blocked list that includes games and video apps after a set time removes the guesswork.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How long before bed should the phone actually stop" },
      {
        type: "p",
        text: "The AAP's own recommendation is a full hour, not five or ten minutes. That is longer than most households actually manage, which is worth saying plainly rather than pretending it is an easy habit to keep. The reason for the length is that both effects, the light and the alertness from stimulating content, need time to wear off before the body is actually ready for sleep, not just quiet.",
      },
      { type: "h2", text: "Does it matter if the phone is out of the room versus just switched off" },
      {
        type: "p",
        text: "The AAP recommends keeping phones and screens out of the child's bedroom at night entirely, not just turned off nearby. A phone sitting on the nightstand, even off, is still something to reach for the moment a child cannot fall asleep right away, which is exactly the moment the one-hour cutoff is trying to protect.",
      },
      { type: "h2", text: "How much sleep should this actually be protecting" },
      {
        type: "p",
        text: "The AAP puts the number at 9 to 11 hours a night for children aged 6 to 13. That range, not screen time itself, is the actual target. The screen habits above are the mechanism, but the goal worth keeping in view is whether a child is consistently getting close to that number, not whether the last hour before bed was screen-free in some perfect, exact sense.",
      },
      {
        type: "p",
        text: "None of this requires a dramatic change overnight. A one-hour cutoff and the phone actually leaving the room are two specific, checkable things, and pediatric guidance says both matter more than most parents assume.",
      },
      {
        type: "cta",
        text: "Schedule your blocked list to start an hour before your child's actual bedtime.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is it actually the blue light, or something else",
        answer:
          "Both. Screen light can disrupt melatonin production, and separately, stimulating content raises a child's alertness and heart rate directly, according to the American Academy of Pediatrics. The two effects work against sleep at the same time for different reasons.",
      },
      {
        question: "Does it matter what kind of video or game it is",
        answer:
          "Yes. The AAP recommends relaxing or funny content over stimulating content in the evening, since an intense video or action-heavy game raises alertness more than a slow, familiar show does, even on the same screen for the same amount of time.",
      },
      {
        question: "How long before bed should the phone actually stop",
        answer:
          "The AAP recommends a full hour, longer than most households typically manage. Both the light effect and the alertness from stimulating content need time to wear off before a child is actually ready to fall asleep.",
      },
      {
        question: "Does it matter if the phone is out of the room versus just switched off",
        answer:
          "Yes, according to the AAP, which recommends keeping phones and screens out of the child's bedroom at night entirely. A phone nearby, even switched off, is still reachable the moment a child cannot fall asleep right away.",
      },
      {
        question: "How much sleep should this actually be protecting",
        answer:
          "The AAP recommends 9 to 11 hours a night for children aged 6 to 13. That total, not perfect screen-free evenings, is the actual goal the one-hour cutoff and bedroom rule are meant to protect.",
      },
    ],
  },
  {
    slug: "is-youtube-kids-app-actually-safer",
    title: "Is the YouTube Kids App Actually Safer Than Regular YouTube",
    metaTitle: "Is YouTube Kids Actually Safer Than YouTube?",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/is-youtube-kids-app-actually-safer.jpg",
    description:
      "Switching to YouTube Kids feels like the safe option. Here is what its own safety guidance says it still misses, and what that means for a shared phone.",
    date: "2026-09-30",
    dateModified: "2026-09-30",
    readTime: "4 min read",
    excerpt:
      "Switching to YouTube Kids feels like the safe option. Here is what its own safety guidance says it still misses, and what that means for a shared phone.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["stop-child-opening-youtube", "why-reels-shorts-harder-to-stop-than-video"],
    externalSources: [
      { label: "Parents' Ultimate Guide to YouTube Kids (Common Sense Media)", url: "https://www.commonsensemedia.org/articles/parents-ultimate-guide-to-youtube-kids" },
    ],
    body: [
      {
        type: "p",
        text: "Installing YouTube Kids instead of regular YouTube feels like solving the problem outright: a separate app, built for children, with a name that says so. It is genuinely better than the regular app in several concrete ways. It is not a version of YouTube you can hand over and walk away from, and its own safety guidance says as much.",
      },
      { type: "h2", text: "How does YouTube Kids actually moderate what shows up" },
      {
        type: "p",
        text: "Through a mix of an algorithm filtering out adult content and human reviewers checking flagged videos, with some videos also marked as manually verified. That is a real, meaningful moderation layer regular YouTube's main app does not have in the same form.",
      },
      { type: "h2", text: "Does anything inappropriate still get through" },
      {
        type: "p",
        text: "Yes, and not rarely. Independent research cited by Common Sense Media found that 27 percent of videos watched by children 8 and under on the platform were actually intended for an older audience, with violent content being the most common issue. There have also been documented cases of disturbing material spliced into otherwise normal-looking children's videos. The moderation layer reduces risk. It does not remove it.",
      },
      {
        type: "cta",
        text: "A time window on the app itself closes the gap moderation alone cannot.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What controls actually exist inside the app" },
      {
        type: "p",
        text: "A viewing timer up to one hour, age-based content tiers for under 4, 5 to 7, and 8 to 12, a toggle to turn search off entirely so only a pre-approved set of videos is reachable, a passcode to protect the settings themselves, and the ability to block or report a specific video directly. These are real tools, and turning search off in particular meaningfully narrows what a child can stumble into.",
      },
      { type: "h2", text: "Is it actually safe to hand over without watching along" },
      {
        type: "p",
        text: "According to Common Sense Media's own guidance, no, not fully. Their recommendation is active supervision, watching together when possible, for a platform they suggest is a better fit from around age 7 upward rather than for the youngest viewers unsupervised. That is a direct statement from a child-safety organization, not an assumption.",
      },
      { type: "h2", text: "So what does this actually mean for a shared phone" },
      {
        type: "p",
        text: "That YouTube Kids is a better starting point than the regular app, not a finished solution. Turning on the strictest content tier and disabling search closes most of the gap on its own. What it cannot do is control when the app is reachable in the first place, which still comes down to whatever schedule exists on the phone itself, the same as any other app a child might open unsupervised.",
      },
      {
        type: "p",
        text: "None of this is a reason to avoid YouTube Kids. It is a reason to treat it the way its own safety guidance already suggests: a real improvement, used alongside supervision and a time boundary, not instead of either one.",
      },
      {
        type: "cta",
        text: "Set hours for video apps, YouTube Kids included, rather than relying on the app alone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How does YouTube Kids actually moderate what shows up",
        answer:
          "Through a combination of algorithmic filtering and human reviewers checking flagged videos, with some content also marked as manually verified. This is a meaningful moderation layer the main YouTube app does not have in the same form.",
      },
      {
        question: "Does anything inappropriate still get through",
        answer:
          "Yes. Research cited by Common Sense Media found that 27 percent of videos watched by children 8 and under on the platform were intended for an older audience, most often involving violence, and there have been documented cases of disturbing content hidden inside normal-looking videos.",
      },
      {
        question: "What controls actually exist inside the app",
        answer:
          "A viewing timer up to one hour, age-based content tiers, a toggle to disable search entirely, a passcode protecting the settings, and the ability to block or report specific videos directly.",
      },
      {
        question: "Is it actually safe to hand over without watching along",
        answer:
          "According to Common Sense Media's own guidance, not fully. They recommend active supervision and suggest the app is a better fit from around age 7 upward rather than for unsupervised use by the youngest viewers.",
      },
      {
        question: "So what does this actually mean for a shared phone",
        answer:
          "YouTube Kids is a better starting point than the regular app, not a complete solution on its own. It does not control when the app is reachable, which still depends on whatever schedule exists on the phone itself.",
      },
    ],
  },
  {
    slug: "android-guest-mode-new-user-profile-bypass-blocking",
    title: "Can Android's Guest Mode or a New User Profile Get Around App Blocking",
    metaTitle: "Can a New User Profile Get Around App Blocking",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/android-guest-mode-new-user-profile-bypass-blocking.jpg",
    description:
      "Android lets anyone add a new user profile in a few taps, and it looks like a blank phone. Here is what that actually means for whatever you have blocked.",
    date: "2026-10-01",
    dateModified: "2026-10-01",
    readTime: "4 min read",
    excerpt:
      "Android lets anyone add a new user profile in a few taps, and it looks like a blank phone. Here is what that actually means for whatever you have blocked.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-pinning-vs-app-blocker-shared-phone", "why-blocked-app-reopens-xiaomi-realme-oppo-vivo"],
    externalSources: [
      { label: "Delete, switch, or add users (Google Android Help)", url: "https://support.google.com/android/answer/2865483?hl=en" },
    ],
    body: [
      {
        type: "p",
        text: "Android has a built-in feature that turns one phone into several separate ones: a new user profile, or a guest session, each with its own home screen and its own set of installed apps. It is meant for sharing a phone with another adult. Handed to a curious ten-year-old, it can look a lot like a blank phone with no rules on it at all.",
      },
      { type: "h2", text: "What guest mode and a new user profile actually are" },
      {
        type: "p",
        text: "Two separate features that work the same basic way, according to Google's own Android support documentation. A new user gets \"its own space on the device for personal, custom home screens, accounts, apps, settings, and more.\" Guest mode is the same idea with one difference: it is built to be thrown away, meant for a short-term borrow rather than a permanent second profile.",
      },
      { type: "h2", text: "Why a new profile can look like a blank phone" },
      {
        type: "p",
        text: "Because most of what makes a phone feel personal does not carry over. Google's documentation is specific about what stays separate between profiles: files and messages, and \"data from apps another user has installed.\" A profile the parent never touched has no history in it, and whatever settings the parent configured on their own profile were configured there, not system-wide. A child switching into a fresh profile is not looking at a stripped-down version of the family phone. They are looking at a different starting point entirely.",
      },
      {
        type: "cta",
        text: "Blocking apps by the hour matters more on a phone a child can reach freely.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does creating a new profile take more than the phone's regular unlock" },
      {
        type: "p",
        text: "No. Adding a user is a Settings menu action, Settings, System, Multiple users, Add user, not a separate security gate. Whatever unlock a child already has to use the phone at all, a PIN, a pattern, a fingerprint they have watched enough times, is all that is needed to reach that menu and create a new one. There is no extra password standing between \"has the phone unlocked\" and \"has a second profile.\"",
      },
      { type: "h2", text: "Is this something a child stumbles into, or does it take real effort" },
      {
        type: "p",
        text: "Somewhere in between. A three or four-year-old is not navigating a system settings menu. An eight to twelve-year-old who has been curious enough to open Settings once, maybe looking for something else entirely, can find \"Multiple users\" without much trouble, it is not hidden, and Android's own help pages walk through the exact steps in under a minute. It takes noticing the option exists, not technical skill.",
      },
      { type: "h2", text: "What should a parent actually do about this" },
      {
        type: "p",
        text: "Check whether the phone allows new users to be added at all, and turn that off if the option exists on that device, newer Android versions let the primary account holder restrict who can add a profile. Where that is not available, the more reliable habit is the same one that matters everywhere else on a shared phone: know which profile is active before handing it over, and treat a phone with multiple users the same way as one with several apps worth keeping an eye on.",
      },
      {
        type: "p",
        text: "A feature built for sharing a phone with a spouse or a houseguest was never designed with a child in mind. It is not a loophole exactly. It is just a part of Android that nobody thought to lock down for this particular use.",
      },
      {
        type: "cta",
        text: "Set the hours apps are blocked once, on the profile your child actually uses.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What guest mode and a new user profile actually are",
        answer:
          "Two related Android features that give a separate space on the same device, each with its own home screen, accounts, apps, and settings, according to Google's own documentation. Guest mode is built for a short-term borrow and is easier to delete when done.",
      },
      {
        question: "Why a new profile can look like a blank phone",
        answer:
          "Because files, messages, and data from apps installed under one profile are not shared with another, per Google's documentation. A profile a parent never touched has no history and none of that profile's own settings, so it can look like an entirely fresh phone.",
      },
      {
        question: "Does creating a new profile take more than the phone's regular unlock",
        answer:
          "No. Adding a user is reached through the regular Settings app, not a separate security gate. Whatever unlock a child already has to the phone is enough to reach that menu and create one.",
      },
      {
        question: "Is this something a child stumbles into, or does it take real effort",
        answer:
          "It takes noticing the option exists rather than any technical skill. A young child is unlikely to find it, but an eight to twelve-year-old curious enough to browse Settings can locate \"Multiple users\" without much trouble.",
      },
      {
        question: "What should a parent actually do about this",
        answer:
          "Check whether the phone lets the primary account holder restrict who can add a new user, and turn that off where it is available. Otherwise, the more reliable habit is knowing which profile is active before handing the phone over.",
      },
    ],
  },
  {
    slug: "upi-pin-child-payment-apps-shared-phone",
    title: "UPI PIN Safety When Your Child Uses Payment Apps on a Shared Phone",
    metaTitle: "UPI PIN Safety on a Shared Phone With Kids",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/upi-pin-child-payment-apps-shared-phone.jpg",
    description:
      "GPay, PhonePe, and Paytm are one tap away from a saved UPI PIN. Here is what that actually risks when your child uses your phone, and what helps.",
    date: "2026-10-02",
    dateModified: "2026-10-02",
    readTime: "4 min read",
    excerpt:
      "GPay, PhonePe, and Paytm are one tap away from a saved UPI PIN. Here is what that actually risks when your child uses your phone, and what helps.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["blinkit-parental-controls-shared-phone", "fantasy-sports-apps-real-money-risk-kids-phone"],
    body: [
      {
        type: "p",
        text: "GPay, PhonePe, and Paytm sit on most Indian phones already logged in, already linked to a bank account, and one tap away from moving real money. Handing that phone to a child for a game or a video is not the same as handing over a toy phone. The payment apps are still there, still signed in, and still working exactly as they were built to.",
      },
      { type: "h2", text: "How does a child end up making a UPI payment by accident" },
      {
        type: "p",
        text: "Usually through a QR code, not through opening the payment app on purpose. Scanning a QR code inside a game, a shopping app, or even a shared link can drop straight into a payment screen with the amount and payee already filled in. From there, the only thing standing between that screen and a completed transfer is the UPI PIN, which a child may have watched a parent type more times than anyone realizes.",
      },
      { type: "h2", text: "Can a child actually know or guess your UPI PIN" },
      {
        type: "p",
        text: "More easily than most parents assume. A four to six digit PIN typed the same way, in the same spot, dozens of times in front of a child who is simply present in the room, tends to get memorized without anyone trying to teach it. It is not a security flaw in the app. It is what happens when the same short code is entered visibly, repeatedly, on a device someone else also holds.",
      },
      {
        type: "cta",
        text: "Blocking payment apps for the hours your child has the phone removes the question entirely.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What actually stops an accidental UPI payment on a shared phone" },
      {
        type: "p",
        text: "Two things, used together. First, never let the phone's autofill or a saved-PIN shortcut sit active on a shared device, that single setting is often the real gap, not the app itself. Second, keep payment apps out of reach during the hours the phone is actually handed over, the same way any other risky app would be kept out of reach, rather than trusting that a PIN alone will hold up under repeated exposure.",
      },
      { type: "h2", text: "Should you just remove your card details and UPI ID entirely" },
      {
        type: "p",
        text: "That solves the problem but usually is not realistic, most people rely on that same phone for their own daily payments. A more workable middle ground is treating GPay, PhonePe, Paytm, and similar apps the same as any other app you would not want opened without you present: blocked during the hours the phone is handed over, available the rest of the time.",
      },
      { type: "h2", text: "So what should you actually do" },
      {
        type: "p",
        text: "Change the PIN if you suspect it has been seen, turn off any saved-PIN or biometric shortcut that skips typing it, and add your payment apps to a blocked list for the specific hours your child uses the phone. None of this requires deleting the apps or the accounts behind them, only making sure they are not sitting open and reachable during the exact windows a child is holding the phone unsupervised.",
      },
      {
        type: "p",
        text: "UPI made moving money fast on purpose. On a phone only one adult uses, that speed is the entire point. On a phone a child also uses, it is the part worth putting a boundary around.",
      },
      {
        type: "cta",
        text: "Add your payment apps to a blocked schedule for the hours your child has the phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How does a child end up making a UPI payment by accident",
        answer:
          "Usually by scanning a QR code inside a game, shopping app, or shared link, which can open directly to a payment screen with the amount and payee already filled in. The UPI PIN is often the only step left before the transfer completes.",
      },
      {
        question: "Can a child actually know or guess your UPI PIN",
        answer:
          "Yes, more easily than most parents assume. A short PIN typed the same way, in the same spot, many times in front of a child tends to get memorized without any deliberate attempt to learn it.",
      },
      {
        question: "What actually stops an accidental UPI payment on a shared phone",
        answer:
          "Turning off autofill or any saved-PIN shortcut on the shared device, and keeping payment apps blocked during the hours the phone is actually handed over, rather than relying on the PIN alone.",
      },
      {
        question: "Should you just remove your card details and UPI ID entirely",
        answer:
          "It solves the problem but is rarely realistic, since most people rely on the same phone for their own payments. Blocking the payment apps during handover hours is a more workable middle ground.",
      },
      {
        question: "So what should you actually do",
        answer:
          "Change the PIN if it may have been seen, disable any saved-PIN or biometric shortcut, and add payment apps to a blocked schedule for the hours a child uses the phone unsupervised.",
      },
    ],
  },
  {
    slug: "blocking-app-vs-same-service-in-browser",
    title: "If You Block an App, Can Your Child Still Reach It Through a Browser",
    metaTitle: "Does Blocking an App Stop the Same Site in a Browser",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/blocking-app-vs-same-service-in-browser.jpg",
    description:
      "You blocked the app, and your child still got to the same thing through Chrome. Here is why that happens and what blocking the app was actually supposed to cover.",
    date: "2026-10-03",
    dateModified: "2026-10-03",
    readTime: "4 min read",
    excerpt:
      "You blocked the app, and your child still got to the same thing through Chrome. Here is why that happens and what blocking the app was actually supposed to cover.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["monitoring-vs-blocking-parental-apps", "web-filter-doesnt-cover-second-browser"],
    externalSources: [
      { label: "Application Sandbox (Android Open Source Project)", url: "https://source.android.com/docs/security/app-sandbox" },
    ],
    body: [
      {
        type: "p",
        text: "Blocking Instagram's app and then finding instagram.com still loads in Chrome feels like the protection failed. It did not fail. It did exactly what app blocking is built to do, which is watch one specific app. A browser opening the same service's website is, to the phone, a completely different app doing a completely unrelated thing.",
      },
      { type: "h2", text: "Why blocking one app does not also block the website version" },
      {
        type: "p",
        text: "Because an app blocker watches for a specific app being opened, not for a specific company or service being reached. Instagram the app and instagram.com opened in Chrome are two separate pieces of software as far as the phone is concerned, built by the same company but running as entirely different programs. Closing one has no connection to the other.",
      },
      { type: "h2", text: "What is actually happening at a technical level" },
      {
        type: "p",
        text: "Every Android app runs isolated from every other app, under what Google's own security documentation calls a sandbox: \"Android assigns a unique user ID (UID) to each Android app and runs it in its own process,\" enforced by the operating system itself, not by any single app's settings. A tool that closes an app when it opens can only ever recognize that one app's identity. It has no way to see, and no reason to see, that a browser just opened a website belonging to the same company.",
      },
      {
        type: "cta",
        text: "Blocking the browser itself during set hours closes this gap entirely.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this mean the fix is to block websites instead" },
      {
        type: "p",
        text: "Not quite, and it is worth being precise about what that would and would not solve. Blocking a specific website address inside a browser is a different mechanism again, one with its own gap: a restriction set inside one browser generally does not carry over to a second browser installed on the same phone. Blocking the app, restricting a website, and blocking the browser itself are three separate tools, each covering a different piece of the problem.",
      },
      { type: "h2", text: "What happens if the browser itself is on the blocked list" },
      {
        type: "p",
        text: "That is where an app blocker's real strength shows up. A browser is, after all, just another app, and it can be chosen from the same block list as anything else. Block the browser during the hours a phone gets handed over, and it does not matter which site the child was trying to reach through it, the browser itself never opens.",
      },
      { type: "h2", text: "So what should a parent actually do" },
      {
        type: "p",
        text: "Think in terms of the app that gets opened, not the service behind it. If an app is blocked but its website still worries you, the browser carrying that website is also just an app, and it can go on the same list. That single step covers every site reachable through it, rather than trying to predict and restrict one website at a time.",
      },
      {
        type: "p",
        text: "A blocked app staying closed and a browser staying open to the same service are not a contradiction. They are two different programs, and a phone only ever sees them that way.",
      },
      {
        type: "cta",
        text: "Add the browser to your blocked list to cover this gap in one step.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why blocking one app does not also block the website version",
        answer:
          "Because an app blocker watches for a specific app opening, not for a company or service being reached. An app and that same company's website opened in a browser are separate pieces of software, so closing one has no connection to the other.",
      },
      {
        question: "What is actually happening at a technical level",
        answer:
          "Every Android app runs isolated from every other app under a system-level sandbox, assigned its own unique user ID and process by the operating system, according to Google's own Android security documentation. A tool watching one app has no way to see a browser opening a website from the same company.",
      },
      {
        question: "Does this mean the fix is to block websites instead",
        answer:
          "Not entirely. Restricting a specific website inside one browser is a separate mechanism with its own gap, since that restriction generally does not carry over to a second browser on the same phone. Blocking an app, restricting a website, and blocking a browser are three distinct tools.",
      },
      {
        question: "What happens if the browser itself is on the blocked list",
        answer:
          "A browser is just another app, so it can be added to the same block list as anything else. Blocking the browser during set hours covers every site reachable through it, regardless of which one a child was trying to reach.",
      },
      {
        question: "So what should a parent actually do",
        answer:
          "Think in terms of the app that actually gets opened. If a blocked app's website is the concern, the browser carrying that website can be blocked too, covering the gap in one step instead of restricting one site at a time.",
      },
    ],
  },
  {
    slug: "australia-under-16-social-media-ban-shared-phone",
    title: "Australia's Under-16 Social Media Law Is in Effect. Does It Help on a Shared Phone",
    metaTitle: "Australia's Social Media Ban and Your Shared Phone",
    tags: [],
    ogImage: "/blog-images/australia-under-16-social-media-ban-shared-phone.jpg",
    description:
      "Australia's under-16 social media law has been in effect since December 2025. Here is what it restricts, and what it was never built to catch on a shared phone.",
    date: "2026-10-04",
    dateModified: "2026-10-04",
    readTime: "4 min read",
    excerpt:
      "Australia's under-16 social media law has been in effect since December 2025. Here is what it restricts, and what it was never built to catch on a shared phone.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["karnataka-social-media-ban-under-16-status", "supreme-court-social-media-minors-rules-india"],
    externalSources: [
      { label: "Social media age restrictions (eSafety Commissioner)", url: "https://www.esafety.gov.au/about-us/industry-regulation/social-media-age-restrictions/faqs" },
    ],
    body: [
      {
        type: "p",
        text: "Australia's social media minimum age law has been fully in effect since 10 December 2025, according to the eSafety Commissioner's own guidance, and it is a real, enforceable restriction, not a proposal still working through parliament. It is also a narrower tool than its name suggests, and knowing exactly what it covers matters more than the headline version of it.",
      },
      { type: "h2", text: "What does the law actually restrict" },
      {
        type: "p",
        text: "Accounts, on ten named platforms: Facebook, Instagram, Kick, Reddit, Snapchat, Threads, TikTok, Twitch, X, and YouTube, per eSafety's own published guidance. Platforms have to find and deactivate existing under-16 accounts, stop new ones being created, and close off common workarounds. Messaging and gaming services, including WhatsApp, Messenger, and Discord, sit outside it entirely, since the law targets the account-holding platforms it was written for.",
      },
      { type: "h2", text: "Can a parent just give permission instead" },
      {
        type: "p",
        text: "No. eSafety's guidance is direct about this: there are no exceptions, and a parent cannot authorize an under-16 account on a restricted platform even with full consent. The law also does not penalize a child for accessing one of these platforms. The obligation sits entirely on the platforms to detect and act, not on the family.",
      },
      {
        type: "cta",
        text: "A law that watches accounts does not watch what opens on your own phone.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does this actually stop a child using one of these apps on a shared phone" },
      {
        type: "p",
        text: "Not by itself, and this is the part that matters most for a household where a child uses a parent's own phone rather than having an account of their own. The law restricts who can hold an account. It says nothing about what happens when a child opens an app that is already signed in under a parent's own account, because there is no under-16 account there for the platform to detect in the first place. The restriction and the shared-phone reality are solving two different problems.",
      },
      { type: "h2", text: "What is eSafety actually checking for, if not the device" },
      {
        type: "p",
        text: "Signals tied to the account itself. eSafety's guidance describes platforms using \"various signals, technologies, systems and processes,\" including age checks at signup and behavioral patterns like language, activity timing, and who an account is connected to, to catch an under-16 person who has their own account. None of that applies to a household's shared adult account being used by more than one person.",
      },
      { type: "h2", text: "So what should an Australian parent actually do" },
      {
        type: "p",
        text: "Treat the law as one layer, covering something a device-level tool cannot: a child who tries to sign up for their own account on a major platform. For the separate, more common situation, a child opening an app that is already logged in on a shared phone, the fix sits on the phone itself, not with the platform, by deciding which apps are reachable and when.",
      },
      {
        type: "p",
        text: "The law closed a real gap for under-16s trying to get their own account. It was never built to watch what happens on a phone that already has one.",
      },
      {
        type: "cta",
        text: "Set which apps your child can open on your own phone, and when.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What does the law actually restrict",
        answer:
          "Accounts on ten named platforms, Facebook, Instagram, Kick, Reddit, Snapchat, Threads, TikTok, Twitch, X, and YouTube, per the eSafety Commissioner's own guidance. Messaging and gaming services such as WhatsApp, Messenger, and Discord are not covered.",
      },
      {
        question: "Can a parent just give permission instead",
        answer:
          "No. eSafety's guidance states there are no exceptions, and a parent cannot authorize an under-16 account on a restricted platform. There is also no penalty for a child who accesses one of these platforms; the obligation sits with the platforms.",
      },
      {
        question: "Does this actually stop a child using one of these apps on a shared phone",
        answer:
          "Not by itself. The law restricts who can hold an account, but says nothing about a child using an app that is already signed into a parent's own account on a shared phone, since there is no under-16 account for a platform to detect there.",
      },
      {
        question: "What is eSafety actually checking for, if not the device",
        answer:
          "Signals tied to an account itself, including signup-stage age checks and behavioral patterns like language and activity timing, aimed at catching an under-16 person with their own account. None of that applies to a shared adult account used by more than one person.",
      },
      {
        question: "So what should an Australian parent actually do",
        answer:
          "Treat the law as covering a child trying to open their own account. For a child using an app already logged in on a shared phone, the fix is deciding which apps are reachable on that phone and when, rather than relying on the platform.",
      },
    ],
  },
  {
    slug: "parentaledge-vs-crenelview-shared-phone",
    title: "ParentalEdge vs CrenelView: Which One Actually Fits a Phone Your Child Doesn't Own",
    metaTitle: "ParentalEdge vs CrenelView: Which Fits a Shared Phone",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/parentaledge-vs-crenelview-shared-phone.jpg",
    description:
      "ParentalEdge and CrenelView solve different problems. Here is what each one actually assumes about whose phone your child is using, compared fact by fact.",
    date: "2026-10-05",
    dateModified: "2026-10-05",
    readTime: "4 min read",
    excerpt:
      "ParentalEdge and CrenelView solve different problems. Here is what each one actually assumes about whose phone your child is using, compared fact by fact.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["who-crenelview-is-built-for", "monitoring-vs-blocking-parental-apps"],
    body: [
      {
        type: "p",
        text: "ParentalEdge and CrenelView both get searched by parents looking for help with a child and a phone, and they are not really built for the same situation. One assumes your child already has their own device. The other assumes they don't. That one difference decides which of the two actually applies to your household, more than any feature list does.",
      },
      { type: "h2", text: "What is ParentalEdge actually built for" },
      {
        type: "p",
        text: "A child's own, separate device. According to ParentalEdge's own site, setup means installing its app on the child's phone and entering a registration code generated from a parent's account, after which a parent-facing web panel shows activity remotely: screen time, YouTube video titles and channels watched, the device's live whereabouts, and site-blocking that covers every browser including incognito. It runs on a paid annual plan with a trial period before billing starts, and registers as a device administrator so the app cannot be uninstalled from the child's device without first removing it from that parent-facing panel.",
      },
      { type: "h2", text: "What is CrenelView actually built for" },
      {
        type: "p",
        text: "A single phone that a parent and child share, because the child has no phone of their own yet. There is no second device to register and no remote panel, because there is nothing remote to manage, the parent picks which apps to block directly on the same phone the child uses. A chosen app closes the moment it opens. Blocking applies to the whole list either all the time or only during hours the parent sets, with a timed \"Allow for a while\" option covering exceptions. The app runs entirely on-device, with no account, no sign-in, and no INTERNET permission at all, checkable directly in the app's own permissions listing, not something to take on faith.",
      },
      {
        type: "cta",
        text: "If your child borrows your phone, there is no second device to register.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does CrenelView do the same things ParentalEdge does" },
      {
        type: "p",
        text: "No, and it is worth being direct about that rather than stretching a comparison where it does not fit. CrenelView does not show a device's whereabouts, does not block or screen specific websites, has no parent-facing web panel, and keeps no usage-time history, it blocks the specific apps a parent chooses, nothing broader than that. ParentalEdge's feature set is built around keeping an eye on a device that is out of a parent's hands during the day. CrenelView's is built around a phone that never actually leaves the parent's hands, just the child's.",
      },
      { type: "h2", text: "Why the two cannot really be scored against each other" },
      {
        type: "p",
        text: "Because a feature only matters if the situation calls for it. Knowing a device's live whereabouts is only useful when your child actually carries that device around independently. A single-phone app blocker is only useful when there is one phone being handed back and forth. Comparing them feature for feature would be comparing answers to two different questions.",
      },
      { type: "h2", text: "So which one should a parent actually pick" },
      {
        type: "p",
        text: "If a child has their own phone, tablet, or computer that leaves the house without a parent, a tool built to monitor a separate device, the way ParentalEdge is, matches that situation. If a child's only phone time is on a parent's own phone, handed over and taken back the same day, there is no second device for a tool like that to register in the first place, which is exactly the gap CrenelView is built to cover instead.",
      },
      {
        type: "p",
        text: "Neither app is wrong for existing. They are built for two different shapes of the same underlying worry, and the shape of your household decides which one actually does something useful.",
      },
      {
        type: "cta",
        text: "Built for the one-phone household, not a second device to manage.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is ParentalEdge actually built for",
        answer:
          "A child's own, separate device. Per ParentalEdge's own site, setup involves installing its app on the child's phone with a registration code, after which a parent-facing web panel shows activity remotely, including the device's location, YouTube viewing detail, and site-blocking across every browser, on a paid annual plan.",
      },
      {
        question: "What is CrenelView actually built for",
        answer:
          "A single phone shared between a parent and a child who has no phone of their own. There is no second device or remote panel; the parent blocks chosen apps directly on the same phone, which close immediately when opened, with no account, sign-in, or internet permission involved.",
      },
      {
        question: "Does CrenelView do the same things ParentalEdge does",
        answer:
          "No. CrenelView does not show a device's whereabouts, does not block specific websites, has no parent-facing web panel, and keeps no usage-time history. It blocks specifically chosen apps on one shared phone, a narrower and different tool than a device-monitoring app built for a separate phone a child carries independently.",
      },
      {
        question: "Why the two cannot really be scored against each other",
        answer:
          "Because each feature only matters in the situation it was built for. Knowing a device's live whereabouts helps when a child carries that device around alone; single-phone app blocking helps when there is one phone being handed back and forth. They answer different questions.",
      },
      {
        question: "So which one should a parent actually pick",
        answer:
          "A tool built to monitor a separate device fits a child who already has their own phone, tablet, or computer. A tool built for one shared phone fits a child whose only access is a parent's own phone, since there is no second device for a monitoring app to register.",
      },
    ],
  },
  {
    slug: "qustodio-vs-crenelview-shared-phone",
    title: "Qustodio vs CrenelView: Which One Actually Fits a Phone Your Child Doesn't Own",
    metaTitle: "Qustodio vs CrenelView: Which Fits a Shared Phone",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/qustodio-vs-crenelview-shared-phone.jpg",
    description:
      "Qustodio and CrenelView are built for two different households. Here is what each one actually assumes about whose phone your child is using, compared fact by fact.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "Qustodio and CrenelView are built for two different households. Here is what each one actually assumes about whose phone your child is using, compared fact by fact.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["who-crenelview-is-built-for", "monitoring-vs-blocking-parental-apps"],
    body: [
      {
        type: "p",
        text: "Qustodio and CrenelView get searched by the same worried parent, but they are answers to two different questions. Qustodio is a full suite built to keep an eye on a child's own device from afar. CrenelView is a narrower tool built for a phone that never actually leaves a parent's hands. Knowing which situation you are actually in matters more than comparing feature lists.",
      },
      { type: "h2", text: "What is Qustodio actually built for" },
      {
        type: "p",
        text: "A child's own device, watched closely from a parent's side. Per Qustodio's own site, it installs on the child's phone, tablet, or computer and gives a parent a web-based control panel covering site and app blocking, daily time limits and screen-free routines, visibility into a child's calls and texts, activity on apps like WhatsApp, Instagram, TikTok, Snapchat, and X, a family member's live whereabouts, automated alerts for risky in-game voice chat, and daily and weekly activity summaries. It starts as a free account, with its fuller paid feature set available during a trial window before any card is needed.",
      },
      { type: "h2", text: "What is CrenelView actually built for" },
      {
        type: "p",
        text: "A single phone that a parent and child share, because the child has no device of their own yet. There is nothing remote to manage, because there is no second device, the parent picks which apps to block directly on the same phone the child uses. A chosen app closes the moment it opens. Blocking applies to the whole list either all the time or only during hours the parent sets, with a timed \"Allow for a while\" option covering exceptions. It runs entirely on-device, with no account, no sign-in, and no INTERNET permission at all, checkable directly in the app's own permissions listing, not something to take on faith.",
      },
      {
        type: "cta",
        text: "If there is only one phone in the house, there is nothing for a suite like that to watch remotely.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does CrenelView do the same things Qustodio does" },
      {
        type: "p",
        text: "No, and it is worth being direct about that rather than stretching the comparison. CrenelView does not show a family member's whereabouts, does not block specific websites, keeps no record of a child's calls, texts, or social app activity, and produces no daily or weekly activity summaries, it blocks the specific apps a parent chooses, nothing broader than that. Qustodio's whole design assumes a device that spends the day away from a parent. CrenelView's design assumes the opposite: one phone, handed over and taken back the same day.",
      },
      { type: "h2", text: "Why the two cannot really be scored against each other" },
      {
        type: "p",
        text: "Because a feature only earns its place if the situation calls for it. Seeing a family member's live whereabouts only matters for a device that actually goes somewhere without a parent. A single-phone app blocker only matters when there is exactly one phone being handed back and forth. Lining the two apps up feature for feature would be comparing answers to two different questions.",
      },
      { type: "h2", text: "So which one should a parent actually pick" },
      {
        type: "p",
        text: "If a child already carries their own phone, tablet, or laptop out of the house, a full suite built to watch that separate device, the way Qustodio is, matches the actual situation. If a child's only phone time happens on a parent's own phone, handed over at home and given back the same day, there is no second device for a suite like that to install on in the first place, which is exactly the gap CrenelView exists to cover instead.",
      },
      {
        type: "p",
        text: "Neither app is solving a fake problem. They are built for two different shapes of the same worry, and the shape of your own household is what decides which one actually helps.",
      },
      {
        type: "cta",
        text: "Built for the one-phone household, not a separate device to watch.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is Qustodio actually built for",
        answer:
          "A child's own device, watched from a parent's side. Per Qustodio's own site, it installs on the child's phone, tablet, or computer and gives a parent a web-based control panel covering site and app blocking, time limits, visibility into calls, texts, and social apps, a family member's live whereabouts, automated voice-chat alerts, and daily activity summaries, starting free with a trial of its fuller paid feature set.",
      },
      {
        question: "What is CrenelView actually built for",
        answer:
          "A single phone shared between a parent and a child who has no device of their own. There is no second device to manage remotely; the parent blocks chosen apps directly on the same phone, which close immediately when opened, with no account, sign-in, or internet permission involved.",
      },
      {
        question: "Does CrenelView do the same things Qustodio does",
        answer:
          "No. CrenelView does not show anyone's whereabouts, does not block specific websites, keeps no record of calls, texts, or social app activity, and produces no activity summaries. It blocks specifically chosen apps on one shared phone, a narrower tool than a suite built for a device that spends the day away from a parent.",
      },
      {
        question: "Why the two cannot really be scored against each other",
        answer:
          "Because each feature only matters in the situation it was built for. Live whereabouts only matters for a device that goes somewhere without a parent; single-phone app blocking only matters when there is one phone being handed back and forth. They answer different questions.",
      },
      {
        question: "So which one should a parent actually pick",
        answer:
          "A full suite built to watch a separate device fits a child who already carries their own phone, tablet, or laptop. A tool built for one shared phone fits a child whose only access is a parent's own phone, since there is no second device for a suite like that to install on.",
      },
    ],
  },
  {
    slug: "bark-vs-crenelview-shared-phone",
    title: "Bark vs CrenelView: Which One Actually Fits a Phone Your Child Doesn't Own",
    metaTitle: "Bark vs CrenelView: Which One Fits a Shared Phone",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/bark-vs-crenelview-shared-phone.jpg",
    description:
      "Bark and CrenelView are built for different households. Here is what each one assumes about whose phone your child uses, compared fact by fact.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "Bark and CrenelView are built for different households. Here is what each one assumes about whose phone your child uses, compared fact by fact.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["who-crenelview-is-built-for", "monitoring-vs-blocking-parental-apps"],
    body: [
      {
        type: "p",
        text: "Bark and CrenelView both turn up when a parent searches for help with a child and a phone, but they answer different questions. Bark is built to read what happens on a child's own device and alert a parent. CrenelView is built for a phone the child doesn't own, where the only job is closing the apps a parent has chosen. Which one applies depends on whose phone it is.",
      },
      { type: "h2", text: "What is Bark actually built for" },
      {
        type: "p",
        text: "A child's own device, scanned for risk and reported to a parent. According to Bark's own site, a parent installs a companion app on the child's Android phone or tablet, and Bark then reads texts, emails, searches, and activity across more than 30 social apps, sending the parent an alert when its software spots something concerning. It also covers screen schedules, blocking sites and apps, and a child's live GPS position. For some social apps, Bark says setup means entering the child's own login details. It sells a paid plan, along with its own kids' phone and watch.",
      },
      { type: "h2", text: "What is CrenelView actually built for" },
      {
        type: "p",
        text: "A single phone that a parent and child share, because the child has no device of their own yet. There is no second device and nothing to scan, since the parent picks which apps to block directly on the same phone the child borrows. A chosen app closes the moment it opens. Blocking applies to the whole list either all the time or only during hours the parent sets, with a timed \"Allow for a while\" option covering exceptions. It runs entirely on-device, with no account, no sign-in, and no INTERNET permission at all, checkable directly in the app's own permissions listing.",
      },
      {
        type: "cta",
        text: "If your child only ever uses your phone, there is no second device to scan.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does CrenelView do the same things Bark does" },
      {
        type: "p",
        text: "No, and it is better to say so plainly than to stretch the comparison. CrenelView does not read texts or social activity, does not send alerts about what a child sees, does not show anyone's position, and does not block individual websites. It closes the specific apps a parent chooses, nothing broader than that. Bark's design assumes a child whose device goes out the door. CrenelView assumes one phone, handed over and taken back the same day.",
      },
      { type: "h2", text: "Is Bark available where you live" },
      {
        type: "p",
        text: "Check before you plan around it. Bark's own FAQ says the Bark app is currently available in the United States, South Africa, and Australia, and that its kids' phone is for US families only. That may change, and it is worth confirming on their site, but a parent outside those places should not assume it will install and work as described. CrenelView has no account and no server to depend on, so where you live does not change how it works.",
      },
      { type: "h2", text: "So which one should a parent actually pick" },
      {
        type: "p",
        text: "If a child already carries their own phone or tablet out of the house, a tool built to scan that separate device, the way Bark is, matches the situation. If a child's only phone time is on a parent's own phone, handed over at home and returned the same day, there is no second device for that kind of tool to sit on, which is the gap CrenelView exists to cover.",
      },
      {
        type: "p",
        text: "Neither one is solving an imaginary problem. They fit two different households, and yours decides.",
      },
      {
        type: "cta",
        text: "Built for the one-phone household, not a second device to scan.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is Bark actually built for",
        answer:
          "A child's own device, scanned for risk and reported to a parent. Per Bark's own site, a companion app goes on the child's Android phone or tablet, and Bark reads texts, emails, searches, and activity across 30-plus social apps, alerting the parent when its software spots something concerning, alongside screen schedules, site and app blocking, and live GPS position.",
      },
      {
        question: "What is CrenelView actually built for",
        answer:
          "A single phone shared between a parent and a child who has no device of their own. There is no second device to scan; the parent blocks chosen apps directly on the same phone, which close immediately when opened, with no account, sign-in, or internet permission involved.",
      },
      {
        question: "Does CrenelView do the same things Bark does",
        answer:
          "No. CrenelView does not read texts or social activity, does not send alerts about what a child sees, does not show anyone's position, and does not block individual websites. It closes the specific apps a parent chooses on one shared phone.",
      },
      {
        question: "Is Bark available where you live",
        answer:
          "Bark's own FAQ says the Bark app is currently available in the United States, South Africa, and Australia, and its kids' phone is for US families only. Availability can change, so confirm on Bark's site before planning around it. CrenelView has no account or server, so location does not change how it works.",
      },
      {
        question: "So which one should a parent actually pick",
        answer:
          "A tool that scans a separate device fits a child who already carries their own phone or tablet. A tool built for one shared phone fits a child whose only access is a parent's own phone, since there is no second device for a scanning tool to sit on.",
      },
    ],
  },
  {
    slug: "screen-time-childs-eyes-what-doctors-say",
    title: "Does Screen Time Damage a Child's Eyes, or Just Tire Them",
    metaTitle: "Does Screen Time Damage a Child's Eyes?",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/screen-time-childs-eyes-what-doctors-say.jpg",
    description:
      "Eye doctors say screen strain is real but temporary, and the bigger worry is close-up time indoors. Here is what that means for a phone your child borrows.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "Eye doctors say screen strain is real but temporary, and the bigger worry is close-up time indoors. Here is what that means for a phone your child borrows.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-time-by-age-2026-guidelines", "screen-time-before-bed-child-sleep"],
    externalSources: [
      { label: "Screen Use for Kids (American Academy of Ophthalmology)", url: "https://www.aao.org/eye-health/tips-prevention/screen-use-kids" },
    ],
    body: [
      {
        type: "p",
        text: "Mostly it tires them. According to the American Academy of Ophthalmology, the dry, itchy, blurry-eyed feeling after a long stretch on a screen is temporary and does not cause permanent damage. The bigger long-term question is nearsightedness, and there the screen is only part of the story. Hours of close-up work indoors, books included, matter too.",
      },
      { type: "h2", text: "What is digital eye strain" },
      {
        type: "p",
        text: "Not a disease, but a name for the symptoms that show up after looking at something up close for too long. The Academy lists dry eyes, itchy eyes, blurry vision, and headaches. A child rubbing their eyes after a long video session is the usual picture. These symptoms pass, and the Academy says no permanent damage is being done.",
      },
      { type: "h2", text: "Does a phone screen cause nearsightedness" },
      {
        type: "p",
        text: "Not on its own. The Academy points to a 2019 study in its journal Ophthalmology suggesting that part of the worldwide rise in nearsightedness comes from near work in general. Screens are one piece of eye development, it says, and traditional books and the total time spent indoors count as well. So a child who reads a lot and rarely goes outside carries the same risk factors as one glued to a phone.",
      },
      {
        type: "cta",
        text: "A set block on the video apps gives a long session a natural end.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does time outdoors really help" },
      {
        type: "p",
        text: "Yes, according to the same Academy page. The 2019 study it cites found that time outdoors, especially in early childhood, can slow the progression of nearsightedness. This is one of the few protective habits the Academy names, and it needs no equipment. Sending a child outside when the phone goes back in your bag does double duty.",
      },
      { type: "h2", text: "Do blue light glasses help" },
      {
        type: "p",
        text: "The Academy says there is no scientific evidence that the light from computer screens damages the eyes, and it does not recommend blue light glasses or any special eyewear for computer use. That is worth knowing before spending money on them. The Academy does say screens too close to bedtime can hurt sleep quality, which is a separate problem from eye health.",
      },
      { type: "h2", text: "What should a parent actually do" },
      {
        type: "p",
        text: "Small things, mostly. The Academy suggests keeping the screen about an arm's length away at a slight downward angle, avoiding glare from bright rooms or outdoor light, and giving the eyes regular breaks by looking at something far away. It also suggests using natural stopping points, like the end of a level in a game, as the cue to look up or go outside. Those breaks are easier to enforce when the phone is yours and goes back into your hands at a set time.",
      },
      {
        type: "p",
        text: "A tired-eyed child after a long video is a signal to take a break, not a sign of lasting harm. The habit worth building is the break itself, and a daily dose of daylight.",
      },
      {
        type: "cta",
        text: "Choose the hours a video app can open, and let the phone rest the rest of the day.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is digital eye strain",
        answer:
          "A name for temporary symptoms after looking at something up close for too long, including dry eyes, itchy eyes, blurry vision, and headaches. The American Academy of Ophthalmology says these symptoms pass and that no permanent damage is being done.",
      },
      {
        question: "Does a phone screen cause nearsightedness",
        answer:
          "Not on its own. The American Academy of Ophthalmology points to a 2019 study suggesting that part of the worldwide rise in nearsightedness comes from near work in general, including books, and from time spent indoors, with screens as just one piece.",
      },
      {
        question: "Does time outdoors really help",
        answer:
          "Yes. The same study found that time outdoors, especially in early childhood, can slow the progression of nearsightedness, according to the American Academy of Ophthalmology.",
      },
      {
        question: "Do blue light glasses help",
        answer:
          "The American Academy of Ophthalmology says there is no scientific evidence that light from screens damages the eyes and does not recommend blue light glasses or special eyewear for computer use. Screens too close to bedtime can still hurt sleep, which is a separate issue.",
      },
      {
        question: "What should a parent actually do",
        answer:
          "Keep the screen about an arm's length away at a slight downward angle, avoid glare, and give the eyes regular breaks by looking at something far away, using natural stopping points like the end of a game level as the cue.",
      },
    ],
  },
  {
    slug: "screen-while-eating-child-meals",
    title: "Is It Okay to Let Your Child Watch a Video While Eating",
    metaTitle: "Is It Okay to Let a Child Watch Videos While Eating?",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/screen-while-eating-child-meals.jpg",
    description:
      "Many parents hand over the phone to get a child to finish a meal. Here is what pediatricians say it does, and how to stop without a standoff.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "Many parents hand over the phone to get a child to finish a meal. Here is what pediatricians say it does, and how to stop without a standoff.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["giving-child-phone-to-keep-quiet-screen-addiction", "why-your-child-cries-when-you-say-no-to-the-phone"],
    externalSources: [
      { label: "Food and Media: Not a Healthy Mix (HealthyChildren.org, American Academy of Pediatrics)", url: "https://www.healthychildren.org/English/family-life/Media/Pages/Food-and-TV-Not-a-Healthy-Mix.aspx" },
    ],
    body: [
      {
        type: "p",
        text: "It works, which is why so many parents do it: a video on, a spoon going in, a finished plate. The American Academy of Pediatrics still advises against it. Its guidance says children distracted by a screen often eat well past the point of being full, and recommends keeping meals free of devices. The habit is common and understandable. It is also worth changing gently.",
      },
      { type: "h2", text: "Why does a child eat more in front of a screen" },
      {
        type: "p",
        text: "Because attention goes to the video instead of the stomach. The Academy's HealthyChildren site says that when children are distracted by the TV or an app, they will often keep eating long after they are full. A child who is watching is not noticing the usual signals that say enough, so the plate gets finished for the wrong reason.",
      },
      { type: "h2", text: "Is it only about how much they eat" },
      {
        type: "p",
        text: "No. The Academy names several other concerns. Children who watch a lot of TV are more likely to develop obesity, it says, and the food advertising children see on screens is often for high-sugar, high-fat products aimed straight at them. Screens at the table also take over time that could go to talking, which the Academy treats as part of what a family meal is for.",
      },
      {
        type: "cta",
        text: "Keep the video apps closed during the meal hours you choose.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What do pediatricians recommend instead" },
      {
        type: "p",
        text: "Meals with every device put away, eaten together at the table when the family can manage it, with conversation as the point. That is the Academy's own wording of the goal. It is not a rule that every meal must be perfect. It is a direction to move in.",
      },
      { type: "h2", text: "How do you stop without a fight at every meal" },
      {
        type: "p",
        text: "Pick one meal and start there, rather than banning screens everywhere on the same day. Tell the child the new rule before the meal, not during it, so it does not feel like a punishment handed down at the table. Keep the phone out of sight, since a visible phone is a standing invitation. Expect the first few meals to be slow and a little unhappy. That settles faster than most parents expect when the rule stays the same.",
      },
      { type: "h2", text: "What if the phone is the only thing that gets them to eat" },
      {
        type: "p",
        text: "Then it is worth saying so out loud, to yourself first. Some children have real feeding difficulties, and if a child eats very little without a screen, that is a conversation for your pediatrician, not a parenting failure. For most children the screen has simply become the habit, and the habit can be unlearned.",
      },
      {
        type: "p",
        text: "A screen-free meal does not need to be a long one. Ten calm minutes with the phone in another room counts.",
      },
      {
        type: "cta",
        text: "Block the video apps for the hour around dinner, then forget about it.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does a child eat more in front of a screen",
        answer:
          "Because attention goes to the video instead of the stomach. The American Academy of Pediatrics' HealthyChildren site says children distracted by a screen will often eat long after they are full, since they are not noticing the signals that say enough.",
      },
      {
        question: "Is it only about how much they eat",
        answer:
          "No. The Academy also names a higher likelihood of obesity in children who watch a lot of TV, food advertising aimed at children on screens, and time taken away from talking at the table.",
      },
      {
        question: "What do pediatricians recommend instead",
        answer:
          "Meals with every device put away, eaten together at the table when possible, with conversation as the point. It is a direction to move in, not a rule that every meal be perfect.",
      },
      {
        question: "How do you stop without a fight at every meal",
        answer:
          "Start with one meal, tell the child the rule before the meal rather than during it, keep the phone out of sight, and expect a few slow, unhappy meals before it settles.",
      },
      {
        question: "What if the phone is the only thing that gets them to eat",
        answer:
          "If a child eats very little without a screen, raise it with your pediatrician, since some children have real feeding difficulties. For most children the screen has simply become a habit, and habits can be unlearned.",
      },
    ],
  },
  {
    slug: "child-saw-something-upsetting-on-phone",
    title: "What to Do When Your Child Sees Something Upsetting on Your Phone",
    metaTitle: "What to Do If Your Child Sees Something Upsetting",
    tags: ["Risky Apps"],
    ogImage: "/blog-images/child-saw-something-upsetting-on-phone.jpg",
    description:
      "A scary video, a pop-up, a message from a stranger. Here is what child-safety guidance says to do in the first few minutes, and what to say after.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "A scary video, a pop-up, a message from a stranger. Here is what child-safety guidance says to do in the first few minutes, and what to say after.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["what-to-check-before-handing-your-child-your-phone", "how-to-explain-blocked-app-to-child"],
    externalSources: [
      { label: "What to do if your child has seen something inappropriate online (CEOP Education)", url: "https://www.ceopeducation.co.uk/parents/articles/what-to-do-if-your-child-has-seen-something-inappropriate-online/" },
    ],
    body: [
      {
        type: "p",
        text: "Stay calm, then let your child talk. That is the core of the guidance from CEOP Education, the child-protection education arm of the UK's National Crime Agency. Most of what children see online is harmless, but sooner or later something unsuitable turns up, and how you react in the first few minutes decides whether they come to you next time.",
      },
      { type: "h2", text: "How does a child end up seeing something they should not" },
      {
        type: "p",
        text: "Three common ways, according to CEOP Education. Someone sends it to them, they go looking out of curiosity, or it turns up by accident, such as a pop-up. On a phone you share, the accidental route is the most likely one. Knowing which it was changes how you respond, so it helps to find out before deciding anything.",
      },
      { type: "h2", text: "Why might your child not tell you" },
      {
        type: "p",
        text: "Because they may be embarrassed or afraid of being blamed. CEOP Education notes that children can feel worried they will be blamed for viewing something inappropriate, which makes it harder for them to talk about it. Some children are visibly upset by what they have seen and others are not, so a calm face does not mean nothing happened.",
      },
      {
        type: "cta",
        text: "If it happened in one app, you can close that app on your phone for good.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What should you do in the first few minutes" },
      {
        type: "p",
        text: "Look after your own reaction first. CEOP Education says it is normal to feel angry, shocked, or even in denial, and suggests taking a moment, or talking to another adult, before you speak to your child. Then find a good time, reassure them that you are there for them, and make clear you do not blame them for what they saw or searched for.",
      },
      { type: "h2", text: "What do you say to your child" },
      {
        type: "p",
        text: "Let them tell you what happened in their own words. The questions that matter are whether they stumbled onto it, were curious and went looking, or were sent it by someone. Listen first, and resist the urge to lecture. A child who feels heard will tell you more than one who feels interrogated.",
      },
      { type: "h2", text: "What should you do afterward" },
      {
        type: "p",
        text: "Work out the next steps together. Report the content to the platform where it appeared, since most platforms have a way to do that. If what your child saw was a nude or semi-nude image of a child, CEOP Education says it should be reported to the police. After that, keep checking in over the following days, because how a child feels about something often changes after the first conversation.",
      },
      {
        type: "p",
        text: "One upsetting moment does not mean you have failed. It means a child had somewhere safe to bring it.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How does a child end up seeing something they should not",
        answer:
          "Three common ways, according to CEOP Education: someone sends it to them, they go looking out of curiosity, or it turns up by accident, such as a pop-up. Knowing which it was changes how a parent should respond.",
      },
      {
        question: "Why might your child not tell you",
        answer:
          "Because they may be embarrassed or afraid of being blamed. CEOP Education notes this can make it harder for children to talk, and that some children are visibly upset while others are not.",
      },
      {
        question: "What should you do in the first few minutes",
        answer:
          "Look after your own reaction first, since anger or shock is normal, and take a moment or talk to another adult before speaking to your child. Then reassure them and make clear you do not blame them for what they saw or searched for.",
      },
      {
        question: "What do you say to your child",
        answer:
          "Let them tell you what happened in their own words, including whether they stumbled onto it, went looking out of curiosity, or were sent it. Listen first rather than lecturing.",
      },
      {
        question: "What should you do afterward",
        answer:
          "Work out next steps together, report the content to the platform where it appeared, and report any nude or semi-nude image of a child to the police, as CEOP Education advises. Keep checking in over the following days.",
      },
    ],
  },
  {
    slug: "youtube-autoplay-turn-off-child-phone",
    title: "Why YouTube Keeps Playing the Next Video, and How to Turn It Off on Your Phone",
    metaTitle: "How to Stop YouTube Playing the Next Video on Its Own",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/youtube-autoplay-turn-off-child-phone.jpg",
    description:
      "YouTube starts the next video by itself when autoplay is on. Here is what Google's own help page says and how to switch it off on a phone.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "YouTube starts the next video by itself when autoplay is on. Here is what Google's own help page says and how to switch it off on a phone.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["stop-child-opening-youtube", "why-reels-shorts-harder-to-stop-than-video"],
    externalSources: [
      { label: "Autoplay videos (YouTube Help, Google)", url: "https://support.google.com/youtube/answer/6327615?hl=en" },
    ],
    body: [
      {
        type: "p",
        text: "You hand over the phone for one cartoon and take it back forty minutes later. The video did not get longer. YouTube's Autoplay feature started another related video the moment the first one ended, and then another. It is a setting, and on your phone you can switch it off in about fifteen seconds.",
      },
      { type: "h2", text: "What does YouTube autoplay actually do" },
      {
        type: "p",
        text: "When Autoplay is on, another related video plays automatically after the current one ends. That is how Google's own YouTube Help page describes it, as a way of making it easier to decide what to watch next. For a child, it removes the decision altogether. Nobody has to choose anything, so nobody gets a natural moment to stop.",
      },
      { type: "h2", text: "How do you turn autoplay off on an Android phone or iPhone" },
      {
        type: "p",
        text: "On a video's watch screen, tap the Autoplay switch at the top of the player to set it to Off. You can also do it from settings: tap your profile picture, tap Settings at the top right, tap Autoplay, and switch it off. Those are the steps on YouTube's own help page for both Android and iOS. There is also a Cancel button at the end of a video if you only want to stop one next video.",
      },
      {
        type: "cta",
        text: "Autoplay off ends the chain. Blocking the app ends the session.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does the setting stay off on every device" },
      {
        type: "p",
        text: "No. YouTube's help page says you can have different Autoplay settings for different devices. You could have it on in the app on your phone and off on your computer. The same goes in the other direction, so if the family also watches on a TV or a tablet, each one has its own setting and needs its own check.",
      },
      { type: "h2", text: "Will autoplay ever stop by itself" },
      {
        type: "p",
        text: "Eventually, but not soon. According to the help page, on a mobile network Autoplay stops if you have been inactive for 30 minutes, and on Wi-Fi it stops automatically after 4 hours. That is a long time for a small child watching at home. It also says that scrolling past the player or typing something, like a search, stops the next video from playing.",
      },
      { type: "h2", text: "Is turning autoplay off enough" },
      {
        type: "p",
        text: "It helps, and it is worth doing, but it is not a time limit. With Autoplay off, a child still has to tap to start the next video, and a young child will often do exactly that. The page also notes that, for users aged 13 to 17, Autoplay is off by default. It does not say the same for younger children, so do not assume it is already off on your phone. Check it once, then decide whether the video app should be open at all during certain hours.",
      },
      {
        type: "p",
        text: "Autoplay is one small setting, and it is the one that quietly turns one cartoon into forty minutes.",
      },
      {
        type: "cta",
        text: "Pick the hours YouTube can open on your phone, and it closes itself the rest of the day.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What does YouTube autoplay actually do",
        answer:
          "When Autoplay is on, another related video plays automatically after the current one ends, according to YouTube's own help page. For a child it removes the moment of choosing what to watch next, and so the natural moment to stop.",
      },
      {
        question: "How do you turn autoplay off on an Android phone or iPhone",
        answer:
          "Tap the Autoplay switch at the top of the video player to set it to Off, or tap your profile picture, then Settings, then Autoplay, and switch it off. These are the steps on YouTube's help page for both Android and iOS.",
      },
      {
        question: "Does the setting stay off on every device",
        answer:
          "No. YouTube's help page says Autoplay settings can differ between devices, so it can be on in the phone app and off on a computer. A TV or tablet needs its own check.",
      },
      {
        question: "Will autoplay ever stop by itself",
        answer:
          "Eventually. On a mobile network Autoplay stops after 30 minutes of inactivity, and on Wi-Fi it stops automatically after 4 hours, per YouTube's help page. Scrolling past the player or typing something also stops the next video from playing.",
      },
      {
        question: "Is turning autoplay off enough",
        answer:
          "It helps but it is not a time limit, since a child can still tap the next video. YouTube says Autoplay is off by default for users aged 13 to 17 but does not say the same for younger children, so check the setting and consider limiting when the app can open.",
      },
    ],
  },
  {
    slug: "reduce-screen-time-school-vacation",
    title: "How to Reduce Your Child's Screen Time During a Long School Vacation",
    metaTitle: "How to Reduce Screen Time During a Long School Vacation",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/reduce-screen-time-school-vacation.jpg",
    description:
      "School is out and the phone keeps coming out. Here is how to plan a vacation with less screen time, using American Academy of Pediatrics guidance.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "School is out and the phone keeps coming out. Here is how to plan a vacation with less screen time, using American Academy of Pediatrics guidance.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["diwali-break-screen-time-plan", "should-screen-time-be-earned-or-scheduled"],
    externalSources: [
      { label: "How to Make a Family Media Use Plan (HealthyChildren.org, American Academy of Pediatrics)", url: "https://www.healthychildren.org/English/family-life/Media/Pages/How-to-Make-a-Family-Media-Use-Plan.aspx" },
    ],
    body: [
      {
        type: "p",
        text: "Plan it before the vacation starts, not on day four when the phone has already become the default. The American Academy of Pediatrics says summer and holiday breaks are good times to review your family's media plan. A few weeks of open days with no school bell is where screen habits quietly reset, usually upward, so the plan needs to exist before the first morning.",
      },
      { type: "h2", text: "Why does screen time jump during a vacation" },
      {
        type: "p",
        text: "Because school was doing a lot of quiet work. It gave the day a shape: a time to wake up, a block of hours with no phone, a reason to be tired by evening. Take that away and a child is left with long open stretches, and the phone is the easiest thing in the house to fill them with. The child has not become harder to manage. The structure that held the day up has gone for a few weeks.",
      },
      { type: "h2", text: "What should you do on the first day of the break" },
      {
        type: "p",
        text: "Sit down for ten minutes and settle the rules for the whole break, together. The Academy's family media plan advice is to discuss the rules with your children rather than hand them down, and to revisit the plan at the start of a break. Agree when screens are allowed, what is off-limits, and what the child does instead. Children keep rules they helped write more readily than rules they were handed.",
      },
      {
        type: "cta",
        text: "Keep the video apps blocked all day, and open them for a set time when you decide.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Which screen-free times matter most" },
      {
        type: "p",
        text: "The Academy suggests three: the dinner table, homework, and before bed. It says these screen-free zones strengthen face-to-face time with the family, support learning, and help sleep. A vacation still has two of the three. Even with no school, there is usually holiday homework to finish, and meals and bedtime carry on. Protect those first and the rest of the day gets easier to manage.",
      },
      { type: "h2", text: "What do they do instead" },
      {
        type: "p",
        text: "This is the part most plans skip, and it is the part that decides whether the plan survives past the second day. A child who is told to stop but handed nothing else will keep asking for the phone. Give each day one anchor that is not a screen: a morning outing, a visit to a cousin, a game outside at a fixed time, a small chore with a job title. Leave the rest of the day open. A little boredom in the middle is fine, and it often turns into the best part of the day.",
      },
      { type: "h2", text: "How do you cut back without a daily argument" },
      {
        type: "p",
        text: "Taper instead of stopping all at once, and keep the rule the same every day. Hours that change from one day to the next invite negotiation, while a fixed time becomes just how the day goes. The Academy also suggests switching off autoplay and notifications that are built to keep children watching, and following a one-screen-at-a-time rule so the TV is not running behind the phone. If the phone is yours, the simplest version is to keep the video and game apps blocked and open them for a set stretch when you choose.",
      },
      {
        type: "p",
        text: "The goal for a long vacation is not zero screens. It is days with a shape again, and a phone that fits inside that shape instead of defining it.",
      },
      {
        type: "cta",
        text: "Set the hours the games and videos can open, and let the vacation have its own shape.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does screen time jump during a vacation",
        answer:
          "Because school gave the day a shape: a time to wake up, a block of hours without a phone, and a reason to be tired by evening. Without it, children have long open stretches, and the phone is the easiest way to fill them.",
      },
      {
        question: "What should you do on the first day of the break",
        answer:
          "Spend ten minutes agreeing the rules for the whole break together. The American Academy of Pediatrics advises discussing the rules with children and reviewing the family media plan at the start of a break, covering when screens are allowed and what the child does instead.",
      },
      {
        question: "Which screen-free times matter most",
        answer:
          "The Academy suggests the dinner table, homework, and before bed, saying these zones strengthen family connection, support learning, and help sleep. Meals and bedtime carry on during a vacation, and there is often holiday homework.",
      },
      {
        question: "What do they do instead",
        answer:
          "Give each day one anchor that is not a screen, such as a morning outing, a visit to a cousin, a fixed-time game outside, or a small chore, and leave the rest open. A child told to stop but given nothing else will keep asking for the phone.",
      },
      {
        question: "How do you cut back without a daily argument",
        answer:
          "Taper rather than stopping all at once and keep the rule the same every day. The Academy also suggests switching off autoplay and notifications built to keep children watching, and keeping to one screen at a time.",
      },
    ],
  },
  {
    slug: "child-watching-gameplay-videos-reduce-screen-time",
    title: "Why Your Child Watches Other People Play Games, and How to Cut Down the Screen Time",
    metaTitle: "Why Kids Watch Others Play Games and How to Cut It Back",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/child-watching-gameplay-videos-reduce-screen-time.jpg",
    description:
      "Hours of watching someone else play a game still counts as screen time. Here is why children love it, what to watch for, and how to set a limit.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "Hours of watching someone else play a game still counts as screen time. Here is why children love it, what to watch for, and how to set a limit.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["stop-child-opening-youtube", "blocking-app-vs-same-service-in-browser"],
    externalSources: [
      { label: "What's the deal with YouTube gaming content (New Zealand Classification Office)", url: "https://www.classificationoffice.govt.nz/news/blog-posts/whats-the-deal-with-youtube-gaming-content/" },
    ],
    body: [
      {
        type: "p",
        text: "Count it as screen time, and set the limit on the watching, not just the playing. Videos of other people playing games are a huge part of what children watch on YouTube, and because the child is not holding a controller it rarely feels like gaming time to anyone. That gap is why a \"one hour of games\" rule can sit next to three hours of gameplay videos without anyone noticing.",
      },
      { type: "h2", text: "Why do children love watching other people play" },
      {
        type: "p",
        text: "For the same reasons adults watch sport. Common Sense Media says watching others play games is a form of social interaction, a way to pick up tips for the games a child plays, and a way to follow players who have become celebrities. New Zealand's Classification Office adds entertainment and community to the list. For a child, a favourite player is part company, part teacher, and part show.",
      },
      { type: "h2", text: "Is watching as bad as playing" },
      {
        type: "p",
        text: "It is different, and the difference matters. The Classification Office points out that games are rated for the people playing them, not for people watching, and that watching a streamer play through a game is a less intense way to meet its content than playing it yourself. That is reassuring up to a point. The same office lists what can still come with the video: swearing, aggression, and constant nudges to buy skins, loot boxes, or merchandise.",
      },
      {
        type: "cta",
        text: "Close YouTube during the hours you pick, so the watching has a stopping point.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What are the other things to watch for" },
      {
        type: "p",
        text: "The video is only half of it. Common Sense Media warns that the broadcasters and the comments beneath a video can be unfiltered, and that YouTube's Restricted Mode blocks some of the edgier material but not all of it. It also notes these videos often run past 30 minutes. The Classification Office cites research suggesting that repeated exposure to violence can make viewers less sensitive to it. None of this means the videos are off limits. It means the player matters as much as the game.",
      },
      { type: "h2", text: "How do you actually reduce the watching" },
      {
        type: "p",
        text: "Treat it like any other screen time and give it a fixed slot, instead of letting it fill whatever is left. Tell your child the plan beforehand, and keep to the same hours every day so there is nothing to negotiate. Switching off autoplay stops one video turning into five. The Classification Office suggests starting with curiosity instead of criticism, so ask who the favourite player is and what is good about them. A child who feels you are interested will tell you more than one who expects a lecture.",
      },
      { type: "h2", text: "Can you make the videos better, not just shorter" },
      {
        type: "p",
        text: "Yes. The Classification Office suggests curating who a child follows toward positive role models, using Restricted Mode or YouTube Kids for younger children, and watching together when you can. Sitting through one video with your child tells you more about their taste than any rule will, and it gives you an easy way to say \"that's enough for today\" without it feeling like a punishment.",
      },
      {
        type: "p",
        text: "None of this asks you to understand the games. It only asks you to treat the watching as part of the day's screen time, and to set its edges.",
      },
      {
        type: "cta",
        text: "Choose the hours YouTube can open on your phone, and let the rest of the day be.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why do children love watching other people play",
        answer:
          "Common Sense Media says watching others play is a form of social interaction, a way to pick up tips for games a child plays, and a way to follow players who have become celebrities. New Zealand's Classification Office adds entertainment and community.",
      },
      {
        question: "Is watching as bad as playing",
        answer:
          "It is different. The Classification Office says games are rated for players rather than viewers and that watching a streamer is a less intense way to meet a game's content, but it still lists swearing, aggression, and pressure to buy skins, loot boxes, or merchandise as concerns.",
      },
      {
        question: "What are the other things to watch for",
        answer:
          "Common Sense Media says broadcasters and comments can be unfiltered and that YouTube's Restricted Mode blocks some of the edgier material but not all of it, and that videos often run past 30 minutes. The Classification Office cites research that repeated exposure to violence can make viewers less sensitive to it.",
      },
      {
        question: "How do you actually reduce the watching",
        answer:
          "Treat it as screen time with a fixed slot at the same hours every day, tell your child the plan beforehand, and switch off autoplay so one video does not become five. Start with curiosity rather than criticism, as the Classification Office suggests.",
      },
      {
        question: "Can you make the videos better, not just shorter",
        answer:
          "Yes. The Classification Office suggests guiding a child toward positive role models, using Restricted Mode or YouTube Kids for younger children, and watching together when possible.",
      },
    ],
  },
  {
    slug: "counsel-students-about-screen-time-school",
    title: "How to Counsel Students About Screen Time in School",
    metaTitle: "How to Talk to Students About Screen Time as a Counsellor",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/counsel-students-about-screen-time-school.jpg",
    description:
      "A short guide for school counsellors and teachers on talking to students about screen time without lecturing, built on published guidance.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "A short guide for school counsellors and teachers on talking to students about screen time without lecturing, built on published guidance.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["how-to-explain-blocked-app-to-child", "research-kids-screen-time-india-studies"],
    externalSources: [
      { label: "10 Tips for Digital Wellness Conversations with Students and Caregivers (ASCD)", url: "https://ascd.org/blogs/10-tips-for-digital-wellness" },
    ],
    body: [
      {
        type: "p",
        text: "Start with curiosity, not a limit. Guidance from Common Sense Media and from education researchers at the University of Michigan agrees on this: students talk when they do not feel judged, and they stop talking when a conversation about their phone turns into a lecture. A counsellor's job in the first conversation is to understand how the student actually uses a screen, not to cut it down.",
      },
      { type: "h2", text: "What is the goal of a screen time conversation" },
      {
        type: "p",
        text: "To help the student notice their own habits and make choices about them, not to catch them out. The University of Michigan authors writing for ASCD say to set clear goals and to present yourself as a fellow learner instead of a talking head. For a one-to-one chat, a useful goal might be as small as \"we both understand what a normal evening looks like for you.\"",
      },
      { type: "h2", text: "How do you open the conversation" },
      {
        type: "p",
        text: "Ask about what they like. Common Sense Media suggests plain, non-judgmental openers such as \"What's your favorite app right now?\" and \"Can you show me how it works?\" Their advice is written for older children and teens, so for younger students keep the same spirit with simpler words: what game they play, who they play it with, what the best part is. A student who is allowed to be the expert often relaxes within a minute.",
      },
      { type: "h2", text: "Which questions help a student see their own habits" },
      {
        type: "p",
        text: "Questions about effects, not amounts. Common Sense Media suggests asking whether any habits get in the way of things like sleep, time with friends and family, or schoolwork, and sharing an example of your own first so the student does not feel singled out. It also suggests asking how a student feels while using a particular app, whether good, worried, or uncomfortable. The student, not the adult, names the problem, and that is what makes a change stick.",
      },
      {
        type: "cta",
        text: "For families that share one phone, a parent can choose which apps open and when.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Why might a student feel judged, and what helps" },
      {
        type: "p",
        text: "Because a counsellor is also a school adult. The ASCD piece describes a pilot where organisers set \"no judgment\" and a safe, comfortable space as explicit goals, and even used neutral outside facilitators so students would not feel judged by staff. A school counsellor cannot hand the conversation to an outsider, so the practical equivalent is to say out loud that this is not a disciplinary talk, and to keep what the student says out of any punishment.",
      },
      { type: "h2", text: "What should the student and their family take away" },
      {
        type: "p",
        text: "Something concrete. The same authors recommend tangible follow-ups, such as agreeing guidelines for use at home or a weekly check-in about digital life. For a counsellor that can be a one-page note for the family or a single sentence the student chose themselves. For families with a young child who shares one parent's phone, it is also reasonable to mention that a parent can block specific apps on that phone during hours she picks.",
      },
      {
        type: "p",
        text: "A good first conversation about screens ends with the student having said more than you did.",
      },
      {
        type: "cta",
        text: "Share a simple option with parents of younger students who borrow a phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What is the goal of a screen time conversation",
        answer:
          "To help the student notice their own habits and make choices about them, not to catch them out. The University of Michigan authors writing for ASCD advise setting clear goals and presenting yourself as a fellow learner rather than a talking head.",
      },
      {
        question: "How do you open the conversation",
        answer:
          "Ask about what they like. Common Sense Media suggests non-judgmental openers such as \"What's your favorite app right now?\" and \"Can you show me how it works?\" Its advice is for older children and teens, so use simpler versions of the same questions for younger students.",
      },
      {
        question: "Which questions help a student see their own habits",
        answer:
          "Questions about effects rather than amounts, such as whether habits get in the way of sleep, time with friends and family, or schoolwork, and how the student feels using a particular app, per Common Sense Media. Share an example of your own first so the student is not singled out.",
      },
      {
        question: "Why might a student feel judged, and what helps",
        answer:
          "A counsellor is also a school adult. The ASCD piece describes setting no judgment and a safe space as explicit goals and using neutral outside facilitators. A counsellor can say aloud that the talk is not disciplinary and keep what the student says out of any punishment.",
      },
      {
        question: "What should the student and their family take away",
        answer:
          "Something concrete, such as agreed guidelines for use at home or a weekly check-in about digital life, as the ASCD authors suggest. For families sharing one parent's phone, a parent can also block specific apps on that phone during chosen hours.",
      },
    ],
  },
  {
    slug: "parent-talk-child-reduce-screen-time-study",
    title: "How to Talk to Your Child About Reducing Screen Time and Focusing on Study",
    metaTitle: "How to Talk to Your Child About Screen Time and Study",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/parent-talk-child-reduce-screen-time-study.jpg",
    description:
      "How to talk to your child about less screen time and better focus on study, with calm, practical steps drawn from Mayo Clinic guidance.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "4 min read",
    excerpt:
      "How to talk to your child about less screen time and better focus on study, with calm, practical steps drawn from Mayo Clinic guidance.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["should-screen-time-be-earned-or-scheduled", "how-to-explain-blocked-app-to-child"],
    externalSources: [
      { label: "Screen time and children: How to guide your child (Mayo Clinic)", url: "https://www.mayoclinic.org/healthy-lifestyle/childrens-health/in-depth/screen-time/art-20047952" },
    ],
    body: [
      {
        type: "p",
        text: "Talk before you take away. A child who hears \"put that phone down and study\" at the exact moment the phone is in their hand will argue. A child who was asked about their day earlier, and helped choose the study hours, has far less to argue about. Mayo Clinic's guidance on children and screens starts from the same place: keep a regular conversation going, and set clear rules.",
      },
      { type: "h2", text: "When is the right time to have the talk" },
      {
        type: "p",
        text: "Not in the middle of a fight, and not while the screen is on. Pick a calm moment, such as after a snack or on a weekend morning, when nobody is hurrying anywhere. Say what you have noticed in one plain sentence, for example \"studying has been hard to start lately,\" and ask what your child thinks is getting in the way. Mayo Clinic suggests asking your child regularly which programs, games and apps they used during the day, so this can be a normal question, not a reaction to a bad report card.",
      },
      { type: "h2", text: "How do you explain why the screen has to wait" },
      {
        type: "p",
        text: "Link it to something your child cares about, in words they can picture. A younger child understands \"homework first, then your game\" better than a lecture about concentration. An older child can be told directly that the phone pulls attention away from the page, and asked what they would change. Mayo Clinic's advice on homework is short and direct: discourage entertainment media while your child does it. Giving that one reason, calmly, is enough.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone during study hours.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What rules help a child focus on study" },
      {
        type: "p",
        text: "Fixed ones, agreed together, and the same every day. Mayo Clinic recommends clear rules and reasonable limits, tech-free zones or times such as mealtimes, and no screens in the hour before bedtime. For study, that can be as simple as: the phone stays in another room during homework, and screens come back once it is done. Let your child have a say in the exact hours. Rules a child helped set are easier to keep than rules that arrive from nowhere.",
      },
      { type: "h2", text: "How do you stay calm when your child pushes back" },
      {
        type: "p",
        text: "Expect it, and do not treat it as a sign the plan failed. A child who is used to the phone will object for the first few days. Repeat the agreement in the same few words, skip the long explanation, and stay friendly. Raising your voice turns the talk into a contest, and the point of the talk is cooperation. If your child is upset, you can stop and return to it later that day. The rule stays; only the conversation pauses.",
      },
      { type: "h2", text: "Do you have to follow the same rules yourself" },
      {
        type: "p",
        text: "Yes, as far as you can. Mayo Clinic says your child is watching you for cues about when it is okay to use screens and how to use them. If you scroll through homework time, a rule about homework time will not carry much weight. You do not have to be perfect. Saying \"I will keep mine away for this hour too\" is enough to show your child the rule applies to everyone in the house.",
      },
      {
        type: "p",
        text: "The talk is not a single conversation. It is a few short ones over several weeks, and the child who feels heard is the one who starts to keep the agreement without being reminded.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, so study hours hold without a daily argument.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "When is the right time to have the talk",
        answer:
          "A calm moment, not during a fight and not while the screen is on. Say what you have noticed in one plain sentence and ask what your child thinks is getting in the way. Mayo Clinic suggests asking regularly which programs, games and apps your child used during the day.",
      },
      {
        question: "How do you explain why the screen has to wait",
        answer:
          "Link it to something your child cares about, in words they can picture, such as \"homework first, then your game.\" Mayo Clinic advises discouraging entertainment media during homework.",
      },
      {
        question: "What rules help a child focus on study",
        answer:
          "Fixed rules agreed together and kept the same every day. Mayo Clinic recommends clear rules, reasonable limits, tech-free zones or times, and no screens in the hour before bedtime. For study, keep the phone in another room during homework and let your child help choose the hours.",
      },
      {
        question: "How do you stay calm when your child pushes back",
        answer:
          "Expect objections for the first few days, repeat the agreement in the same few words, and stay friendly. If your child is upset, pause the conversation and return to it later, while the rule stays in place.",
      },
      {
        question: "Do you have to follow the same rules yourself",
        answer:
          "As far as you can. Mayo Clinic says children watch their parents for cues on when it is okay to use screens. Saying you will keep your own phone away during study hours shows the rule applies to everyone.",
      },
    ],
  },
  {
    slug: "excess-screen-time-effects-psychology-study-behaviour",
    title: "What Too Much Screen Time Does to a Child's Mind, Studies and Behaviour",
    metaTitle: "What Too Much Screen Time Does to a Child's Mind and Study",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/excess-screen-time-effects-psychology-study-behaviour.jpg",
    description:
      "What an Indian study of 537 school children found about heavy screen use, school results, anxiety and behaviour, and what parents can do.",
    date: "2026-10-06",
    dateModified: "2026-10-06",
    readTime: "5 min read",
    excerpt:
      "What an Indian study of 537 school children found about heavy screen use, school results, anxiety and behaviour, and what parents can do.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["research-kids-screen-time-india-studies", "screen-time-by-age-2026-guidelines"],
    externalSources: [
      { label: "Screen time exposure and academic performance, anxiety, and behavioral problems among school children (PeerJ)", url: "https://peerj.com/articles/19409/" },
    ],
    body: [
      {
        type: "p",
        text: "Heavy screen use goes together with weaker school results, more anxiety and more behaviour problems. That is the pattern in a 2024 survey of 537 school children in Greater Noida, India, published in the journal PeerJ. It is a pattern, not proof that screens caused it, and the study is open about that. What it does give parents is a reason to treat screen time as part of the study plan, not a separate topic.",
      },
      { type: "h2", text: "What did the research actually find" },
      {
        type: "p",
        text: "Parents of 537 children aged 8 to 14 answered an online questionnaire. The children averaged about three hours of screen time a day. The researchers found that more screen time went with lower academic performance, higher anxiety scores and more behavioural problems. The correlations with anxiety and with behaviour were stronger than the one with academic performance. The authors also found that screen time predicted body weight, which matters if you were wondering whether the effects stop at the mind.",
      },
      { type: "h2", text: "Does too much screen time harm a child's mind and mood" },
      {
        type: "p",
        text: "The study found higher anxiety scores in children with more screen time, across every type of anxiety it measured. It cannot tell you which came first. A child who is already anxious may reach for a screen to feel calmer, and heavy screen use may also make anxiety worse. Both can be true at once, which is why watching your child's mood matters as much as counting their hours.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone during study hours.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How does screen time connect to study and school results" },
      {
        type: "p",
        text: "In this study, children with more screen time had lower academic performance, and the authors describe screen time as a predictor of academic performance. The mechanism is not tested in the paper, so what follows is ordinary common sense. Hours on a screen are hours not spent on homework, reading or sleep, and a mind used to constant novelty can find a textbook slow. If your child's marks have dropped, ask about screens along with tuition and sleep.",
      },
      { type: "h2", text: "What about behaviour, such as anger and restlessness" },
      {
        type: "p",
        text: "Behaviour problems were higher too, as parents rated them on a standard checklist. If you have seen more temper, answering back or trouble settling when the phone is taken away, you are not imagining a pattern that researchers also found. It does not mean your child is a bad child. It usually means the phone has become the thing the child's mood depends on, which is a habit and habits can change.",
      },
      { type: "h2", text: "Should parents be worried, and what can they do" },
      {
        type: "p",
        text: "Be concerned, not alarmed. The study has real limits: parents reported the screen hours, only private schools took part, and the authors did not measure sleep, family circumstances or how involved parents were, any of which could explain part of the link. Their own conclusion is that children with heavy screen use may need support. For a parent, the practical steps are the ordinary ones. Set fixed hours, keep the phone away from homework and from bedtime, and talk with your child about how they feel after long sessions, as well as how long they were. If the phone is yours, closing the games and video apps during study hours takes the daily argument out of it.",
      },
      {
        type: "p",
        text: "None of this means a child must never touch a screen. It means a pattern of long, unplanned hours is worth noticing early, while changing it is still easy.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, so study hours stay study hours.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What did the research actually find",
        answer:
          "A 2024 survey of 537 Indian school children aged 8 to 14, published in PeerJ, found that more screen time went with lower academic performance, higher anxiety scores and more behavioural problems. The children averaged about three hours of screen time a day. It shows a link, not proof of cause.",
      },
      {
        question: "Does too much screen time harm a child's mind and mood",
        answer:
          "The study found higher anxiety scores in children with more screen time, but it cannot show which came first. An anxious child may use screens to feel calmer, and heavy use may also add to anxiety, so a child's mood is worth watching as well as their hours.",
      },
      {
        question: "How does screen time connect to study and school results",
        answer:
          "In the study, more screen time went with lower academic performance. The paper does not test why, but hours on a screen are hours not spent on homework, reading or sleep.",
      },
      {
        question: "What about behaviour, such as anger and restlessness",
        answer:
          "Parent-rated behaviour problems were also higher in children with more screen time. It does not mean the child is badly behaved. It often means the phone has become something the child's mood depends on, and habits can change.",
      },
      {
        question: "Should parents be worried, and what can they do",
        answer:
          "Be concerned, not alarmed, since the study was cross-sectional, relied on parent-reported hours, covered private schools only and did not measure sleep or family circumstances. Practical steps are fixed hours, no phone during homework or at bedtime, and talking about how your child feels after long sessions.",
      },
    ],
  },
  {
    slug: "child-in-app-purchases-google-play-verification",
    title: "How to Stop Your Child Buying Things Inside Games on Your Android Phone",
    metaTitle: "How to Stop Your Child Buying Things Inside Games on Android",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/child-in-app-purchases-google-play-verification.jpg",
    description:
      "Google Play can ask for verification on every purchase, even inside games. Here is how to check the setting on your phone and what it does not cover.",
    date: "2026-10-07",
    dateModified: "2026-10-07",
    readTime: "4 min read",
    excerpt:
      "Google Play can ask for verification on every purchase, even inside games. Here is how to check the setting on your phone and what it does not cover.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["upi-pin-child-payment-apps-shared-phone", "stop-child-downloading-new-games"],
    externalSources: [
      { label: "Purchase verification for Google Play (Google Play Help)", url: "https://support.google.com/googleplay/answer/1626831" },
    ],
    body: [
      {
        type: "p",
        text: "Check one setting on your phone today: how often Google Play asks you to verify a purchase. If it is set to never ask, a child holding your unlocked phone can buy things inside a game with a few taps. Google's own help page says the default is to verify every purchase, but the setting can be changed, and it is worth ten seconds to look.",
      },
      { type: "h2", text: "How does a child end up buying things inside a game" },
      {
        type: "p",
        text: "Usually by accident, and usually through the phone's own payment settings. Many games let a player buy extra items, coins or upgrades from inside the game. If your phone is set to skip verification, a tap on the wrong button can finish the purchase before anyone notices. Children often cannot tell the difference between pressing a button to play and pressing one to pay, especially when the game is built to make buying look like part of the fun.",
      },
      { type: "h2", text: "What does Google Play's purchase verification setting do" },
      {
        type: "p",
        text: "It decides when Google Play asks you to prove it is really you. According to Google's help page for India, there are three choices. The default is to verify every purchase made through Google Play's billing system. A second choice lets you keep buying, including inside apps, for the next 30 minutes without verifying again. The third is never to verify. Verification can use biometrics, your screen lock or your Google Account password.",
      },
      {
        type: "cta",
        text: "A game that is closed on your phone cannot be opened to make a purchase.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How do you check the setting on your phone" },
      {
        type: "p",
        text: "Open the Google Play app and tap your profile picture. Go into the payments section of that menu, tap Purchase verification, then Verification frequency, and confirm the option you want. These are the steps on Google's help page, and menus can change, so follow the page if yours looks different. For a phone your child uses, the default of verifying every purchase is the safest of the three choices. The 30-minute option is the one to be careful about, because a child who picks up your phone in that window may not be stopped at all.",
      },
      { type: "h2", text: "Does this setting cover every kind of purchase" },
      {
        type: "p",
        text: "No. The setting is about purchases made through Google Play's billing system, so it does not cover other ways of paying that an app or game may offer. If your phone has a payment app on it, that app has its own PIN or lock, and a child who knows or watches you enter it is a separate risk. We cover that in our article on payment apps on a shared phone. Google's page also adds that verification is always required for purchases in apps and games designed for ages 12 and under, whatever your setting is, which is a useful extra layer for young children's apps but not for everything on the phone.",
      },
      { type: "h2", text: "What else should you do so a child cannot buy things" },
      {
        type: "p",
        text: "Remove the chance, not just the payment. Keep your screen lock private and do not leave the phone unlocked in a child's hands. Choose which games stay on the phone at all, since a game that is not there cannot sell anything. If a game is on the phone, you can keep it closed and open it only when you decide, so a child is not tapping through a store screen alone. Then ask your child to tell you if something asks for money, and promise you will not be angry, so a mistake reaches you early and not on a bank message.",
      },
      {
        type: "p",
        text: "None of this needs a lecture. It needs one setting checked, one habit changed, and one rule your child can remember.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, so games are only open when you are nearby.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How does a child end up buying things inside a game",
        answer:
          "Usually by accident. Many games let players buy extra items from inside the game, and if the phone is set to skip verification, a tap on the wrong button can finish a purchase. Children often cannot tell the difference between pressing play and pressing pay.",
      },
      {
        question: "What does Google Play's purchase verification setting do",
        answer:
          "It decides when Google Play asks you to prove it is you. Google's help page for India lists three choices: verify every purchase, which is the default, keep buying for 30 minutes without verifying again, or never verify. Verification can use biometrics, your screen lock or your Google Account password.",
      },
      {
        question: "How do you check the setting on your phone",
        answer:
          "Open the Google Play app, tap your profile picture, go to the payments section, tap Purchase verification, then Verification frequency, and confirm your choice. Verifying every purchase is the safest choice on a phone a child uses. The 30-minute option can leave a short window open.",
      },
      {
        question: "Does this setting cover every kind of purchase",
        answer:
          "No. It covers purchases made through Google Play's billing system, not other ways of paying that an app may offer. Google also says verification is always required in apps and games designed for ages 12 and under, whatever the setting.",
      },
      {
        question: "What else should you do so a child cannot buy things",
        answer:
          "Keep your screen lock private, choose which games stay on the phone, keep games closed until you decide to open them, and tell your child to come to you without fear if something asks for money.",
      },
    ],
  },
  {
    slug: "parenting-tips-managing-screen-time-ages-3-to-11",
    title: "Parenting Tips for Managing Your Child's Screen Time at Ages 3 to 11",
    metaTitle: "Parenting Tips for Managing Screen Time at Ages 3 to 11",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/parenting-tips-managing-screen-time-ages-3-to-11.jpg",
    description:
      "Practical screen time tips for parents of children aged 3 to 11, from family rules to warnings before the screen goes off, plus a trusted parenting site.",
    date: "2026-10-07",
    dateModified: "2026-10-07",
    readTime: "4 min read",
    excerpt:
      "Practical screen time tips for parents of children aged 3 to 11, from family rules to warnings before the screen goes off, plus a trusted parenting site.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["why-your-child-cries-when-you-say-no-to-the-phone", "should-screen-time-be-earned-or-scheduled"],
    externalSources: [
      { label: "Managing screen time and digital technology use: children 3-11 years (Raising Children Network)", url: "https://raisingchildren.net.au/preschoolers/media-technology/screen-time-healthy-screen-use/managing-screen-time-3-11-years" },
    ],
    body: [
      {
        type: "p",
        text: "You do not have to work out screen time rules alone. Raising Children Network, an Australian parenting website supported by the Australian Government, has a full guide to managing screen time for children aged 3 to 11. This article pulls out the tips that matter most, and at the end points you to the site for the rest.",
      },
      { type: "h2", text: "Why should the whole family help make the rules" },
      {
        type: "p",
        text: "Because rules people helped write are rules people follow. Raising Children Network says it is important to involve all family members when you make family rules about screen time. It suggests the rules cover how your child uses technology, when, where, and how to stay safe. It also suggests revisiting them every few months, since a rule that fits a five-year-old will feel too tight for a nine-year-old.",
      },
      { type: "h2", text: "What does a screen time routine look like" },
      {
        type: "p",
        text: "A routine is a rule with a time attached. The site's own example is allowing the tablet only between 5 and 6 pm. The idea is to build screen time into family life so it becomes part of the day and stops being something to negotiate. When the hour is the same every day, a child learns when to expect it, and you stop hearing \"just five more minutes\" ten times a day.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone until the hour you choose.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How do you end screen time without a fight" },
      {
        type: "p",
        text: "Warn first, and stop at a natural break. Raising Children Network suggests telling your child in advance, for example \"you have 10 more minutes on the tablet,\" giving time to save their work, and planning the stop for the end of an episode or a level. It also suggests switching off autoplay where you can, so one video does not roll into the next. For a small child, a warning plus a clear end point usually works better than a sudden \"give it back.\"",
      },
      { type: "h2", text: "Why does it help to give a child choices" },
      {
        type: "p",
        text: "Because a child who has a say is more likely to cooperate. According to the site, children who have input into what they watch or when they use a device are more likely to follow the rules and limits. The choices can be small: which show, which of two time slots, or what to do when the screen goes off. You still set the limits. The child only picks inside them.",
      },
      { type: "h2", text: "Does your own screen use matter" },
      {
        type: "p",
        text: "Yes. The site says your choices about screen time and digital technology influence the way your child uses screens. It also suggests sharing screen time with your child and talking about why a show or game is good quality, which helps children learn to judge for themselves. You do not need to be perfect, but a child who sees you put your phone down at dinner will take the rule more seriously.",
      },
      { type: "h2", text: "Where can parents find more help" },
      {
        type: "p",
        text: "Visit Raising Children Network at raisingchildren.net.au. Its guide on managing screen time for ages 3 to 11 was last reviewed in December 2024. Read it alongside your own sense of your child, and keep the parts that fit your family.",
      },
      {
        type: "p",
        text: "Good screen time habits are built from small, repeated, friendly steps. A routine, a warning and a choice go further than a ban announced in a bad mood.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, so your routine holds on its own.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why should the whole family help make the rules",
        answer:
          "Because rules people helped write are rules people follow. Raising Children Network says to involve all family members and suggests rules cover how, when and where your child uses technology, and how to stay safe. It suggests revisiting them every few months.",
      },
      {
        question: "What does a screen time routine look like",
        answer:
          "A routine is a rule with a time attached. The site's example is allowing the tablet only between 5 and 6 pm, so screen time becomes part of the day and stops being a daily negotiation.",
      },
      {
        question: "How do you end screen time without a fight",
        answer:
          "Warn first and stop at a natural break. Raising Children Network suggests telling your child in advance, such as \"you have 10 more minutes,\" giving time to save their work, stopping at the end of an episode or level, and switching off autoplay where you can.",
      },
      {
        question: "Why does it help to give a child choices",
        answer:
          "According to the site, children who have input into what they watch or when they use a device are more likely to follow the rules and limits. The choices can be small, such as which show or which time slot, while you still set the limits.",
      },
      {
        question: "Does your own screen use matter",
        answer:
          "Yes. The site says your choices about screen time influence the way your child uses screens, and suggests sharing screen time and talking about why content is good quality.",
      },
      {
        question: "Where can parents find more help",
        answer:
          "Raising Children Network at raisingchildren.net.au has a guide on managing screen time for ages 3 to 11, last reviewed in December 2024. The site is supported by the Australian Government.",
      },
    ],
  },
  {
    slug: "educational-apps-really-educational-child-what-to-check",
    title: "Is an Educational App Really Educational? What Parents Should Check",
    metaTitle: "Is an Educational App Really Educational? What to Check",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/educational-apps-really-educational-child-what-to-check.jpg",
    description:
      "Any app can call itself educational. Here are five things researchers say to look for before you let your child spend time on one.",
    date: "2026-10-07",
    dateModified: "2026-10-07",
    readTime: "4 min read",
    excerpt:
      "Any app can call itself educational. Here are five things researchers say to look for before you let your child spend time on one.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["nonsense-videos-long-term-effect-young-child", "screen-time-by-age-2026-guidelines"],
    externalSources: [
      { label: "Educational App or Digital Candy? Helping Parents Choose Quality Apps for Kids (Association for Psychological Science)", url: "https://www.psychologicalscience.org/news/releases/educational-app-or-digital-candy-helping-parents-choose-quality-apps-for-kids.html" },
    ],
    body: [
      {
        type: "p",
        text: "The word \"educational\" on an app does not mean much by itself. In 2015, a team of child-development researchers wrote that claims about educational content are completely unregulated, so any app developer can slap the \"educational\" label on their product. Their advice to parents is to ignore the label and look at how the app actually works, using five questions that take a minute to ask.",
      },
      { type: "h2", text: "Who says an educational label cannot be trusted" },
      {
        type: "p",
        text: "Researchers from Temple University, Penn State, the University of Delaware, Sesame Workshop, the Fred Rogers Center and Swinburne University, who published their review in Psychological Science in the Public Interest in 2015. The Association for Psychological Science summarised it in a news release. The researchers also warned that many apps make dramatic promises, and that an app, even an educational one, is just an app and not a miracle. That is worth remembering the next time an advert promises your child will learn to read from a phone.",
      },
      { type: "h2", text: "What should an educational app ask your child to do" },
      {
        type: "p",
        text: "Real mental work. The first thing the researchers say to look for is an app that requires real mental effort and depends on the child's active participation. An app where a child mostly swipes, taps and watches things happen is closer to a cartoon than a lesson. A quick test is to watch your child use it for two minutes and ask whether they are thinking or only reacting.",
      },
      { type: "h2", text: "Does a good app keep your child focused on the task" },
      {
        type: "p",
        text: "It should. The second suggestion is to look for apps that support sustained engagement with the task at hand, which means the child stays on the activity itself and is not pulled away by noise, pop-ups and rewards that have nothing to do with learning. If your child keeps getting sent to something else inside the app, that app is not helping them concentrate.",
      },
      {
        type: "cta",
        text: "Keep learning apps open, and keep the games and videos closed until you decide.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How can an app connect to what your child already knows" },
      {
        type: "p",
        text: "By linking new information to something familiar. The researchers suggest looking for apps that help children make meaningful connections between new information and what they already know. A counting game that uses things from your child's day, such as chapatis, steps or toys, does this better than one that shows random numbers on a colourful screen. You can add to it yourself by asking, afterwards, where they have seen the same thing at home.",
      },
      { type: "h2", text: "Why does it help if you use the app with your child" },
      {
        type: "p",
        text: "Because talking and playing together is part of learning. Two more pieces of the researchers' advice point this way: look for apps that encourage social interaction through discussion, competition or conversation, and for apps that use guided exploration to help your child discover new information. An app that sends your child off alone into a maze of screens ticks neither box. An app you can sit beside and talk about, taking turns, usually ticks both.",
      },
      { type: "h2", text: "What should you do once an app passes the check" },
      {
        type: "p",
        text: "Treat it as one tool among many, and still set a time for it. A good app is better than a poor one, but it is still screen time, and it should not push out play, reading, talking or sleep. Decide when it can be used, how long, and what comes after, as you would for any other screen. If the phone is yours, you can leave the learning apps alone and keep the games and video apps closed during study or bedtime hours.",
      },
      {
        type: "p",
        text: "The label tells you what the maker wants you to believe. Two minutes of watching your child use the app tells you what it actually does.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, and leave room for the ones that help.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Who says an educational label cannot be trusted",
        answer:
          "Child-development researchers who published a review in Psychological Science in the Public Interest in 2015. They wrote that claims about educational content are completely unregulated, so any app developer can use the \"educational\" label, and that an app, even an educational one, is just an app and not a miracle.",
      },
      {
        question: "What should an educational app ask your child to do",
        answer:
          "Real mental work. The researchers say to look for apps that require real mental effort and depend on the child's active participation, rather than ones where a child mostly swipes and watches.",
      },
      {
        question: "Does a good app keep your child focused on the task",
        answer:
          "It should. The researchers suggest apps that support sustained engagement with the task at hand, without pop-ups and rewards that pull the child away from the activity.",
      },
      {
        question: "How can an app connect to what your child already knows",
        answer:
          "By linking new information to something familiar. The researchers suggest apps that help children make meaningful connections between new information and what they already know.",
      },
      {
        question: "Why does it help if you use the app with your child",
        answer:
          "The researchers suggest apps that encourage social interaction through discussion, competition or conversation, and that use guided exploration to help a child discover new information. An app you can sit beside and talk about usually does both.",
      },
      {
        question: "What should you do once an app passes the check",
        answer:
          "Treat it as one tool among many and still set a time for it, since a good app is still screen time and should not push out play, reading, talking or sleep.",
      },
    ],
  },
  {
    slug: "signs-child-screen-use-becoming-a-problem-when-to-get-help",
    title: "Signs Your Child's Screen Use Is Becoming a Problem, and When to Ask for Help",
    metaTitle: "Signs Your Child's Screen Use Is a Problem, and What to Do",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/signs-child-screen-use-becoming-a-problem-when-to-get-help.jpg",
    description:
      "Hours on a screen do not tell the whole story. Here are the warning signs a Mayo Clinic Health System counsellor lists, and when to ask for help.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "Hours on a screen do not tell the whole story. Here are the warning signs a Mayo Clinic Health System counsellor lists, and when to ask for help.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["why-your-child-cries-when-you-say-no-to-the-phone", "excess-screen-time-effects-psychology-study-behaviour"],
    externalSources: [
      { label: "Video games and screen addiction (Mayo Clinic Health System)", url: "https://www.mayoclinichealthsystem.org/hometown-health/speaking-of-health/are-video-games-and-screens-another-addiction" },
    ],
    body: [
      {
        type: "p",
        text: "Look at what screens are doing to your child's day, as well as how long they are on. Mayo Clinic Health System, in an article by a counsellor in psychiatry and psychology, describes screen use as a problem when it damages health and relationships and the person cannot control it. That gives parents a more useful test than a number of hours, and a list of signs to watch for.",
      },
      { type: "h2", text: "How do you tell ordinary screen use from a problem" },
      {
        type: "p",
        text: "By looking at control and consequences. The article defines addiction as an inability to control use of a substance or behaviour despite negative consequences, and says screen use can reach that point if it damages your health and relationships and you cannot control it. A child who watches a lot but stops when asked, sleeps, studies and plays with friends is in a different place from one who cannot stop and is losing other things because of it. The article is written for people of any age, not just children, so treat it as a guide for questions to ask and not as a test to pass or fail.",
      },
      { type: "h2", text: "Which signs does the article list" },
      {
        type: "p",
        text: "Several, and they are worth reading as a group. It lists intense urges for screens or games that block out other thoughts, cutting back on social or recreational activities, and continuing despite problems at school or home. It also lists irritability, anxiety or anger when made to stop, even briefly, lying about how much time is spent on screens, needing more time to get the same enjoyment, and spending money that cannot be afforded. For a child, that last one might show up as pestering for game purchases or taking a parent's phone to pay for them.",
      },
      {
        type: "cta",
        text: "Close the games on your phone and open them only when you decide.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Is it a problem if my child gets upset when the screen goes off" },
      {
        type: "p",
        text: "Not on its own. Most young children protest when something enjoyable ends. The article lists irritability or anger when forced to stop as one possible sign among many, and one sign does not make a pattern. What matters is whether it comes with others on the list, whether it is getting worse, and whether your child is giving up things they used to enjoy. Our article on why children cry when you say no to the phone looks at the everyday version of this.",
      },
      { type: "h2", text: "What should you do first if you see several of these signs" },
      {
        type: "p",
        text: "Stay calm and start with a conversation, not a punishment. Ask your child what they enjoy about the game or video and what they find hard about stopping. Then make the structure around the screen firmer: fixed hours, a clear end point, and other things to do at the time it ends. Beyond the article, if your child is lying about use or sneaking the phone, meet it with a clear rule and a calm talk, and keep the games closed until the agreed time.",
      },
      { type: "h2", text: "When should you ask a professional for help" },
      {
        type: "p",
        text: "When your own concern does not ease after you have changed the routine, or when school, sleep, friendships or mood are suffering. The article does not set a specific threshold. It says that if you are worried about a child's or loved one's screen use, consulting a behavioural or addictions specialist can help work out treatment options. Beyond what the article says, a sensible first step is to talk to your child's doctor or a school counsellor, who can tell you whether more help is needed and where to find it.",
      },
      {
        type: "p",
        text: "Seeing a few of these signs does not mean something is wrong with your child. It means it is time to look more closely, and early is easier than late.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, to give your child a clear end point.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How do you tell ordinary screen use from a problem",
        answer:
          "By looking at control and consequences. Mayo Clinic Health System defines addiction as an inability to control use despite negative consequences, and says screen use can reach that point if it damages your health and relationships and you cannot control it. A child who stops when asked and keeps up with sleep, study and friends is in a different place from one who cannot stop.",
      },
      {
        question: "Which signs does the article list",
        answer:
          "Intense urges for screens or games that block out other thoughts, cutting back on social or recreational activities, continuing despite problems at school or home, irritability or anger when made to stop, lying about use, needing more time for the same enjoyment, and spending money that cannot be afforded.",
      },
      {
        question: "Is it a problem if my child gets upset when the screen goes off",
        answer:
          "Not on its own. Irritability when made to stop is one possible sign among many, and one sign does not make a pattern. Look at whether it comes with others, is getting worse, or means your child is giving up things they used to enjoy.",
      },
      {
        question: "What should you do first if you see several of these signs",
        answer:
          "Stay calm and start with a conversation, not a punishment. Ask what your child enjoys and what is hard about stopping, then make the routine firmer with fixed hours, a clear end point and other things to do.",
      },
      {
        question: "When should you ask a professional for help",
        answer:
          "When your concern does not ease after changing the routine, or when school, sleep, friendships or mood are suffering. The article says a behavioural or addictions specialist can help work out treatment options. A child's doctor or a school counsellor is a sensible first step.",
      },
    ],
  },
  {
    slug: "how-much-active-play-young-child-needs-instead-of-screens",
    title: "How Much Active Play Does a Young Child Need Each Day, and Where Do Screens Fit",
    metaTitle: "How Much Active Play Does a Young Child Need Each Day",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/how-much-active-play-young-child-needs-instead-of-screens.jpg",
    description:
      "WHO says children aged 1 to 4 need at least 180 minutes of physical activity a day. Here is what that means and where screens fit around it.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "WHO says children aged 1 to 4 need at least 180 minutes of physical activity a day. Here is what that means and where screens fit around it.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-time-by-age-2026-guidelines", "screen-time-before-bed-child-sleep"],
    externalSources: [
      { label: "To grow up healthy, children need to sit less and play more (World Health Organization)", url: "https://www.who.int/news/item/24-04-2019-to-grow-up-healthy-children-need-to-sit-less-and-play-more" },
    ],
    body: [
      {
        type: "p",
        text: "Think about what the screen is replacing, as well as how long it is on. The World Health Organization's guidelines for children under 5, released in 2019, put it simply: the aim is to replace long stretches of sitting in front of a screen with more active play. They give daily targets for activity, sitting and sleep, and screens are only one line in the list.",
      },
      { type: "h2", text: "How much active play does WHO recommend for a young child" },
      {
        type: "p",
        text: "At least 180 minutes a day for children aged 1 to 4. That is three hours spread across the day, and it is made up of a variety of activities, not one long session. For babies under 1, WHO asks for active play several times a day, mainly on the floor, and at least 30 minutes of tummy time spread across the day for babies who are not yet moving around. These guidelines are for children under 5, so they do not apply as written to older children.",
      },
      { type: "h2", text: "Does all of that time have to be energetic" },
      {
        type: "p",
        text: "No. For children aged 1 to 2, WHO says the 180 minutes can be activity of any intensity, and that it should include some moderate to vigorous activity. For children aged 3 to 4, at least 60 of those 180 minutes should be moderate to vigorous, which means running, jumping, climbing and chasing games that get a child breathing harder. The rest can be gentler: walking, building, dancing slowly, playing in the sand.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone while your child plays.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How long can a young child sit in a pram, high chair or car seat" },
      {
        type: "p",
        text: "No longer than an hour at a time. WHO says infants and young children should not be restrained for more than one hour at once, whether in a pram, a high chair or strapped to a caregiver's back. It also advises avoiding long stretches of sitting. That matters on trips, where a phone is often the quickest way to keep a restrained child quiet. A short stop to let them move helps more than another video.",
      },
      { type: "h2", text: "Where do screens and sleep fit in" },
      {
        type: "p",
        text: "Around the play, not instead of it. WHO says sedentary screen time is not recommended for a child aged 1, and for children aged 2 to 4 it should be no more than one hour a day. Sleep has its own targets: 11 to 14 hours including naps for children aged 1 to 2, and 10 to 13 hours for children aged 3 to 4, on a regular schedule. WHO's Dr Juana Willumsen described the idea as making the shift from sedentary time to playtime while protecting sleep.",
      },
      { type: "h2", text: "What can you do in the hours when the screen is off" },
      {
        type: "p",
        text: "Offer play that is easy to start, and join in for the first few minutes. WHO says that when children are sitting, reading and storytelling with a caregiver is encouraged, so a book is a good place to begin. For the active hours, simple things work: a game of catch, a chase around the courtyard, a walk to the shop, an obstacle course made of cushions. These examples are ours, not WHO's.",
      },
      {
        type: "p",
        text: "None of this needs equipment. It needs a few spare hours in the day and a phone that is somewhere else.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, and leave the rest of the day for play.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "How much active play does WHO recommend for a young child",
        answer:
          "At least 180 minutes a day for children aged 1 to 4, spread across the day and made up of a variety of activities. For babies under 1, WHO asks for active play several times a day, mainly on the floor, with at least 30 minutes of tummy time for babies who are not yet mobile. The guidelines are for children under 5.",
      },
      {
        question: "Does all of that time have to be energetic",
        answer:
          "No. For ages 1 to 2 the 180 minutes can be activity of any intensity, including some moderate to vigorous activity. For ages 3 to 4, at least 60 of the 180 minutes should be moderate to vigorous.",
      },
      {
        question: "How long can a young child sit in a pram, high chair or car seat",
        answer:
          "No longer than an hour at a time, according to WHO, which also advises avoiding long stretches of sitting.",
      },
      {
        question: "Where do screens and sleep fit in",
        answer:
          "Around the play, not instead of it. WHO says sedentary screen time is not recommended at age 1 and should be no more than one hour a day for ages 2 to 4. Sleep targets are 11 to 14 hours for ages 1 to 2 and 10 to 13 hours for ages 3 to 4, including naps.",
      },
      {
        question: "What can you do in the hours when the screen is off",
        answer:
          "Offer play that is easy to start and join in for the first few minutes. WHO encourages reading and storytelling with a caregiver when children are sitting. Simple games such as catch, chasing or a walk are good for the active hours.",
      },
    ],
  },
  {
    slug: "my-son-uses-mobile-too-much-what-can-a-mother-do",
    title: "My Son Uses the Mobile Too Much. What Can I Do as a Mother?",
    metaTitle: "My Son Uses Mobile Too Much: What Can a Mother Do?",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/my-son-uses-mobile-too-much-what-can-a-mother-do.jpg",
    description:
      "A simple guide for mothers whose son is on the mobile all day, with easy steps drawn from UNICEF India's tips for cutting down screen time.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "3 min read",
    excerpt:
      "A simple guide for mothers whose son is on the mobile all day, with easy steps drawn from UNICEF India's tips for cutting down screen time.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["why-your-child-cries-when-you-say-no-to-the-phone", "block-apps-child-uses-my-phone"],
    externalSources: [
      { label: "Ten tips for cutting down on screen time (UNICEF India)", url: "https://www.unicef.org/india/parentingtips/ten-tips-cutting-down-screen-time-during-covid-19" },
    ],
    body: [
      {
        type: "p",
        text: "If your son is on the mobile all day, you are not alone, and it is not too late to change it. You do not need a big plan. A few small changes at home work better than shouting. The steps below come from UNICEF India's tips for cutting down screen time. They are for sons and daughters, and for fathers too.",
      },
      { type: "h2", text: "Why does my son want the mobile all the time" },
      {
        type: "p",
        text: "Because it is easy, it is fun, and it is always nearby. A game or a video never gets boring and never asks him to wait. Children also copy what they see, so if the mobile is in every adult's hand, he will want it too. This does not mean you did something wrong. It means the habit needs a new shape.",
      },
      { type: "h2", text: "Should I take the mobile away completely" },
      {
        type: "p",
        text: "Not suddenly, and not as a punishment. UNICEF India advises parents to look at what children do on devices, what they watch and who they talk to, more than to count the time only. A child who is learning, drawing or video calling a cousin is in a different place from one who watches the same clips again and again. Start by finding out what your son does on the mobile. Then decide what to keep and what to reduce.",
      },
      { type: "h2", text: "How do I make the mobile harder to grab" },
      {
        type: "p",
        text: "Keep it in one place, a little far from him. UNICEF India suggests keeping devices in a fixed spot away from the child, so they are not within reach the moment he feels bored. It also suggests keeping other things easy to reach instead, such as puzzles, board games and comic books. If he uses your phone, you can keep the games and video apps closed and open them only at the time you choose.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone until the time you decide.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What can my son do instead of using the mobile" },
      {
        type: "p",
        text: "Give him a few easy choices. UNICEF India suggests at least one family meal a day without any device, because talking at the table helps a child's feelings and eases stress for everyone. It also suggests puzzles, board games and comic books at hand. Beyond UNICEF's tips, our advice is to sit with him for the first ten minutes of a game or puzzle, and then let him go on by himself. It is also fine if he is bored. UNICEF India says it is good for children to get bored sometimes, because they use their imagination to find a way out, and that children do not need constant entertainment. If he says \"I am bored,\" you do not have to rush with the mobile. You can say \"think of something,\" and wait.",
      },
      { type: "h2", text: "What about the mobile at bedtime" },
      {
        type: "p",
        text: "Put it away well before sleep. UNICEF India suggests no screens for two hours before bedtime, because it makes it easier for children to fall asleep and wake up on time. Keep the mobile out of the bedroom at night, so that the plan does not depend on his willpower or yours.",
      },
      { type: "h2", text: "Do I also have to change" },
      {
        type: "p",
        text: "Yes, a little. UNICEF India says household rules about too much use of electronics should apply to everyone, not just to children. If you scroll during dinner, a no-mobile dinner for him will not last. You do not have to be perfect. Saying \"I will keep my phone away too\" is enough to start.",
      },
      {
        type: "p",
        text: "You do not need to fix everything this week. Pick one step, such as a device-free dinner, and do it every day. Then add the next one.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, and give your son a clear end to the day's screen.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does my son want the mobile all the time",
        answer:
          "Because it is easy, fun and always nearby, and children copy what they see adults do. It does not mean you did something wrong. It means the habit needs a new shape.",
      },
      {
        question: "Should I take the mobile away completely",
        answer:
          "Not suddenly and not as a punishment. UNICEF India advises parents to look at what children do on devices, what they watch and who they talk to, more than to count the time only.",
      },
      {
        question: "How do I make the mobile harder to grab",
        answer:
          "Keep it in a fixed spot away from your son, so it is not within reach when he feels bored, and keep puzzles, board games and comic books easy to reach instead, as UNICEF India suggests.",
      },
      {
        question: "What can my son do instead of using the mobile",
        answer:
          "Give him a few easy choices, such as puzzles, board games and comic books, and keep at least one family meal a day without devices, as UNICEF India suggests. It is also fine if he is bored, because UNICEF India says children use their imagination to find a way out.",
      },
      {
        question: "What about the mobile at bedtime",
        answer:
          "UNICEF India suggests no screens for two hours before bedtime, because it helps children fall asleep and wake up on time. Keeping the mobile out of the bedroom at night helps the plan hold.",
      },
      {
        question: "Do I also have to change",
        answer:
          "A little. UNICEF India says household rules about too much use of electronics should apply to everyone, not just to children.",
      },
    ],
  },
  {
    slug: "bacche-ki-mobile-ki-lat-kaise-chhudayen",
    title: "Bacche Ki Mobile Ki Lat Kaise Chhudayen: Mummy-Papa Ke Liye Aasan Tarike",
    metaTitle: "Bacche Ki Mobile Ki Lat Kaise Chhudayen: Aasan Tarike",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/bacche-ki-mobile-ki-lat-kaise-chhudayen.jpg",
    description:
      "Bacha din bhar mobile mein laga rehta hai? Ek NIMHANS doctor ki salah aur ek report ke aadhar par, bina daante, mobile ki aadat kam karne ke aasan tarike.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "3 min read",
    excerpt:
      "Bacha din bhar mobile mein laga rehta hai? Ek NIMHANS doctor ki salah aur ek report ke aadhar par, bina daante, mobile ki aadat kam karne ke aasan tarike.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["app-lock-kaise-lagaye-for-kids", "why-your-child-cries-when-you-say-no-to-the-phone"],
    externalSources: [
      { label: "Google, Nimhans roll out digital wellbeing guidebook for Indian families (Deccan Herald)", url: "https://www.deccanherald.com/amp/story/technology/google-nimhans-roll-out-digital-wellbeing-guidebook-for-indian-families-4080626" },
    ],
    body: [
      {
        type: "p",
        text: "Mobile ki lat ek din mein nahi chhutti, aur daantne se bhi nahi. Is saal Google India aur YouTube ne Indian parivaron ke liye ek digital wellbeing guidebook jaari ki. NIMHANS ke SHUT Clinic ke Dr Manoj Kumar Sharma ne is mauke par kaha ki parents ko achanak rok-tok ke bajay bacche ke saath milkar niyam banane chahiye. Neeche ke tarike isi soch par aadharit hain, aur aap ek-ek karke apna sakte hain.",
      },
      { type: "h2", text: "Bacha din bhar mobile kyun maangta hai" },
      {
        type: "p",
        text: "Kyunki mobile mein khel aur video kabhi khatam nahi hote, aur wo hamesha haath ke paas hota hai. Bacche wahi karte hain jo ghar mein dekhte hain. Agar sab bade mobile mein lage rehte hain, to bacha bhi wahi chahega. Ismein aapki koi galti nahi hai. Bas aadat ko ek nayi shakal deni hai.",
      },
      { type: "h2", text: "Kya mobile achanak le lena theek hai" },
      {
        type: "p",
        text: "Nahi. Dr Sharma ne kaha ki Wi-Fi band kar dena ya device zabt kar lena jaise achanak ke kadam ghar mein takrav badhate hain. Unki salah hai ki niyam sab ki rajamandi se, milkar tay kiye jayen, na ki jhatke mein. Isliye pehle bacche se baat karein. Poochhein ki wo mobile par kya karta hai aur use kya pasand hai. Phir milkar tay karein ki kab aur kitni der.",
      },
      { type: "h2", text: "Pehle khud kya badalna chahiye" },
      {
        type: "p",
        text: "Apni aadat. Ek report ke mutabik guidebook ka pehla niyam hai ki parents aur dada-dadi bacche ke saamne apna mobile kam use karein, aur uski jagah bacche se baat karein, jaise poochhein ki aaj ka din kaisa raha. Report mein yeh bhi likha hai ki bade jaisa karte hain, bacche wahi seekhte hain. Chhoti shuruaat bhi kaafi hai: khana khate waqt apna phone door rakh dein.",
      },
      {
        type: "cta",
        text: "Agar bacha aapka phone use karta hai, to games aur video apps band rakhein aur jab chahein tab kholein.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Ghar ke kaun se niyam kaam karte hain" },
      {
        type: "p",
        text: "Deccan Herald ki report ke mutabik guidebook teen aasan niyam batati hai. Pehla, sone ke kamre mein mobile nahi, aur bacche ka computer ya smartphone ghar ki kisi saajhi jagah par chale. Doosra, khane ki mez par koi smartphone nahi. Teesra, sone se kam se kam ek ghanta pehle smartphone door. Yeh niyam sab par lagu karein, sirf bacche par nahi, to bacha unhe sazaa nahi samjhega.",
      },
      { type: "h2", text: "Bacha mobile ke badle kya kare" },
      {
        type: "p",
        text: "Kuch aisa jo shuru karna aasan ho. Report mein khel-kood, bagwani aur saath mein khana banane jaise kaam sujhaye gaye hain. Pehle kuch minute aap bhi saath baith jayein, phir bacha khud lag jayega. Yeh saath baithne wali baat humari salah hai, report ki nahi. Report yeh bhi kehti hai ki digital niyam ek saath nahi, ek-ek karke laye jayen. To is hafte sirf ek niyam chunein, jaise khane par mobile nahi. Jab wo chal jaye, tab agla jodein.",
      },
      {
        type: "p",
        text: "Lat ek raat mein nahi aayi, to ek raat mein jayegi bhi nahi. Shaanti se, ek-ek kadam par chalte rahein.",
      },
      {
        type: "cta",
        text: "Chunein ki aapke phone par kaun se apps kab khulen, aur bacche ko din ka saaf antim samay dein.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Bacha din bhar mobile kyun maangta hai",
        answer:
          "Kyunki mobile mein khel aur video kabhi khatam nahi hote aur wo hamesha paas hota hai. Bacche wahi karte hain jo ghar mein dekhte hain. Ismein aapki galti nahi, bas aadat ko nayi shakal deni hai.",
      },
      {
        question: "Kya mobile achanak le lena theek hai",
        answer:
          "Nahi. NIMHANS SHUT Clinic ke Dr Manoj Kumar Sharma ke mutabik Wi-Fi band karna ya device zabt karna jaise achanak ke kadam takrav badhate hain. Behtar hai ki niyam bacche ke saath milkar tay kiye jayen.",
      },
      {
        question: "Pehle khud kya badalna chahiye",
        answer:
          "Apni aadat. Ek report ke mutabik guidebook ka pehla niyam hai ki parents aur dada-dadi bacche ke saamne mobile kam use karein aur bacche se baat karein.",
      },
      {
        question: "Ghar ke kaun se niyam kaam karte hain",
        answer:
          "Deccan Herald ki report ke mutabik: sone ke kamre mein mobile nahi aur bacche ka device saajhi jagah par, khane ki mez par smartphone nahi, aur sone se ek ghanta pehle smartphone door.",
      },
      {
        question: "Bacha mobile ke badle kya kare",
        answer:
          "Report mein khel-kood, bagwani aur saath mein khana banane jaise kaam sujhaye gaye hain. Digital niyam ek saath nahi, ek-ek karke laye jayen. Pehle ek niyam chunein.",
      },
    ],
  },
  {
    slug: "why-doctors-worried-children-mobile-screen-time-india",
    title: "Why Doctors Are Worried About Children and Mobile Screens, and What Parents Can Do",
    metaTitle: "Why Doctors Are Worried About Children and Mobile Screens",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/why-doctors-worried-children-mobile-screen-time-india.jpg",
    description:
      "Why Indian doctors are worried about children and screens: what the Indian Academy of Pediatrics says, the limits it suggests, and what parents can do.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "Why Indian doctors are worried about children and screens: what the Indian Academy of Pediatrics says, the limits it suggests, and what parents can do.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-time-by-age-2026-guidelines", "research-kids-screen-time-india-studies"],
    externalSources: [
      { label: "Indian Academy of Pediatrics Guidelines on Screen Time and Digital Wellness in Infants, Children and Adolescents (Indian Pediatrics)", url: "https://www.indianpediatrics.net/mar2022/235.pdf" },
    ],
    body: [
      {
        type: "p",
        text: "Doctors are worried because screens now fill hours that children need for play, sleep and study. The Indian Academy of Pediatrics, the country's main body of child doctors, issued guidelines in 2022 saying so. Its concern is plain: excessive screen use is linked to physical, developmental and emotional problems, and Indian children are using screens more often than before.",
      },
      { type: "h2", text: "Why are doctors worried about children and mobile screens" },
      {
        type: "p",
        text: "Because screens are everywhere and children start early. The Indian Academy of Pediatrics guidelines, published in the journal Indian Pediatrics in March 2022, say screen media have become a normal part of daily life, that they are easy to get and used more often by Indian children, and that excessive use is linked to physical, developmental and emotional problems. The guidelines also note that very early exposure and excessive screen time are widespread among Indian children.",
      },
      { type: "h2", text: "What limits do Indian pediatricians suggest" },
      {
        type: "p",
        text: "For the youngest children, none at all. The guidelines say children under 2 years should have no screen exposure of any kind. For ages 24 to 59 months, they suggest no more than one hour a day, and supervised. For ages 5 to 10 years, they suggest less than two hours a day. These are the limits of one expert committee in 2022. Other bodies set limits differently and update them, so treat the numbers as a guide and not a rule. Our article on how much screen time is right for your child compares them.",
      },
      { type: "h2", text: "What should screens never replace" },
      {
        type: "p",
        text: "Play, sleep, family time and study. The guidelines say screen time should not displace outdoor play, sleep, family and peer interaction, studies or skill development. That is the practical test for parents. Ask how long the screen is on, and also what it pushed out of the day. If your child skipped dinner conversation, outdoor time or homework to stay on the mobile, the screen has taken more than its share.",
      },
      {
        type: "cta",
        text: "Keep the games and video apps closed on your phone until the hours you choose.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What signs are doctors and clinics seeing" },
      {
        type: "p",
        text: "In February 2026, a news report said more than 8,000 parents had sought help from NIMHANS in Bengaluru through its SHUT programme, which stands for Service for Healthy Use of Technology. The report lists signs that worried parents described: anger and restlessness when devices are taken away, sleep problems and late-night use, less interest in studies, trouble concentrating, social withdrawal, sudden mood changes, and lying or stealing money. It quotes the advice to seek professional help early. The report does not name individual doctors or give children's ages, so read it as a sign of how many families are struggling and not as a medical measure.",
      },
      { type: "h2", text: "What can parents do now" },
      {
        type: "p",
        text: "Start with the home, then the routine. The guidelines call for a warm, supportive home and say parents should check that what a child watches is educational, age-appropriate and non-violent. They also say families, schools and pediatricians should ask about screen exposure at routine check-ups, watch for bullying online and for media addiction, and seek expert help when needed. At home, a fixed hour for the mobile and a clear end to it usually works better than a daily argument. If the phone is yours, you can keep the games and video apps closed and open them at the time you decide.",
      },
      {
        type: "p",
        text: "Doctors are not asking parents to ban screens. They are asking us to notice what the screen is replacing, and to act early.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, and keep the rest of the day for play and sleep.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why are doctors worried about children and mobile screens",
        answer:
          "Because screens are everywhere and children start early. The Indian Academy of Pediatrics guidelines of 2022 say excessive screen use is linked to physical, developmental and emotional problems, that screens are easy to get and used more often by Indian children, and that very early exposure is widespread.",
      },
      {
        question: "What limits do Indian pediatricians suggest",
        answer:
          "The 2022 Indian Academy of Pediatrics guidelines say no screen exposure under 2 years, no more than one supervised hour a day for ages 24 to 59 months, and less than two hours a day for ages 5 to 10 years. Other bodies set limits differently, so treat them as a guide.",
      },
      {
        question: "What should screens never replace",
        answer:
          "Outdoor play, sleep, family and peer interaction, studies and skill development, according to the guidelines. A useful test is what the screen pushed out of the day.",
      },
      {
        question: "What signs are doctors and clinics seeing",
        answer:
          "A February 2026 news report said more than 8,000 parents had sought help through NIMHANS's SHUT programme. Signs parents described included anger when devices are taken away, sleep problems, less interest in studies, trouble concentrating, social withdrawal and sudden mood changes. The report advises seeking professional help early.",
      },
      {
        question: "What can parents do now",
        answer:
          "Keep a warm, supportive home, check that content is educational, age-appropriate and non-violent, ask about screen exposure at check-ups, and seek expert help when needed. A fixed hour for the mobile with a clear end usually works better than a daily argument.",
      },
    ],
  },
  {
    slug: "how-teachers-teach-students-effects-of-excessive-mobile-use",
    title: "How Teachers Can Educate Students About the Ill Effects of Too Much Mobile Use",
    metaTitle: "How Teachers Can Teach Students About Too Much Phone Use",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/how-teachers-teach-students-effects-of-excessive-mobile-use.jpg",
    description:
      "A practical guide for teachers on teaching students about the effects of too much mobile use, using classroom methods from a University of Michigan educator.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "A practical guide for teachers on teaching students about the effects of too much mobile use, using classroom methods from a University of Michigan educator.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["counsel-students-about-screen-time-school", "parent-talk-child-reduce-screen-time-study"],
    externalSources: [
      { label: "Teaching Better Screen Habits (ASCD, Educational Leadership, by Liz Kolb)", url: "https://www.ascd.org/el/articles/teaching-better-screen-habits" },
    ],
    body: [
      {
        type: "p",
        text: "A single lecture will not change how students use their phones. Liz Kolb, a University of Michigan professor who trains teachers in learning technology, argues in ASCD's Educational Leadership that behaviour changes through sustained, safe conversations, not one-off lessons. Her advice for teachers is practical, and most of it needs nothing more than a classroom and a little time each week.",
      },
      { type: "h2", text: "Why does a one-time talk on mobile addiction not work" },
      {
        type: "p",
        text: "Because students hear it as a lecture and switch off. Kolb lists what tends to fail: guest speakers such as police officers or lawyers, whom students often cannot relate to; rare and extreme stories that feel far from their own lives; lessons given only a few times a year; online modules where students pass the quiz but do not change their habits; and lists of dos and don'ts written by adults. She suggests that everyday problems, such as a mean comment, a scam message or being left out of a group photo, connect better than dramatic cases.",
      },
      { type: "h2", text: "What should a teacher actually say about the ill effects" },
      {
        type: "p",
        text: "Give facts, stay honest, and avoid sweeping statements. Kolb suggests explaining to students how apps are designed to be addictive and which features keep people using them, so students can see why adults are concerned. She also warns that blanket statements such as \"technology is ruining our youth\" are the quickest way to turn students off. Ask them instead how technology helps their friendships, mood and schoolwork, and accept that their feelings about it are mixed. A student who feels understood is more willing to hear about the downsides.",
      },
      { type: "h2", text: "How can a class talk about it in small groups" },
      {
        type: "p",
        text: "Keep the groups small and the talk regular. Kolb recommends groups of four to six students who set their own ground rules and take part in discussion or game-like activities, because large lectures discourage personal sharing. She also suggests setting aside about 30 minutes of class each week for a digital debrief, so the conversation continues over months. In an Indian school, a form period or a weekly life-skills slot could work, which is our adaptation and not Kolb's.",
      },
      {
        type: "cta",
        text: "For younger students who share a parent's phone, tell parents they can choose which apps open and when.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Why should students make their own rules" },
      {
        type: "p",
        text: "Because rules students write themselves are easier to keep. Kolb notes that lists of dos and don'ts made by adults work less well than lists that students create and adapt to their own situations. She also describes near-peer facilitation, where slightly older students lead the discussion, because teenagers can feel less judged than they do with teachers. In your school this might mean Class 10 students talking with Class 7, which is again our suggestion for how to apply it.",
      },
      { type: "h2", text: "How can parents be part of it" },
      {
        type: "p",
        text: "Bring them in, and give them the same facts. Kolb recommends informational evenings for parents and joint sessions that include games, conversation starters and shared activities for parents and students. A short note home after each class discussion can do a similar job. When parents hear the same message as their children, the rules at home and at school stop contradicting each other.",
      },
      {
        type: "p",
        text: "Students rarely change a habit because they were told to. They change when they have been heard, shown the facts, and given a part in deciding what comes next.",
      },
      {
        type: "cta",
        text: "Share a simple option with parents of younger students who borrow a phone.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Why does a one-time talk on mobile addiction not work",
        answer:
          "Because students hear it as a lecture and switch off. Liz Kolb of the University of Michigan lists guest speakers, extreme stories, lessons given only a few times a year, online modules and adult-written dos and don'ts as approaches that often fail, and says everyday problems connect better.",
      },
      {
        question: "What should a teacher actually say about the ill effects",
        answer:
          "Give facts, stay honest and avoid sweeping statements. Kolb suggests explaining how apps are designed to be addictive and which features drive compulsive use, while also asking students how technology helps them.",
      },
      {
        question: "How can a class talk about it in small groups",
        answer:
          "Kolb recommends groups of four to six students who set their own ground rules, plus about 30 minutes of class each week for a digital debrief so the conversation continues.",
      },
      {
        question: "Why should students make their own rules",
        answer:
          "Kolb says lists made by students and fitted to their own situations work better than lists of dos and don'ts written by adults. Slightly older students leading the discussion can also help.",
      },
      {
        question: "How can parents be part of it",
        answer:
          "Kolb recommends informational evenings and joint sessions for parents and students with games, conversation starters and shared activities, so home and school give the same message.",
      },
    ],
  },
  {
    slug: "teacher-approved-parental-control-app-what-to-check",
    title: "Is There a Teacher-Approved Parental Control App? What to Check Before You Install One",
    metaTitle: "Teacher-Approved Parental Control App: What to Check",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/teacher-approved-parental-control-app-what-to-check.jpg",
    description:
      "We found no official teacher approval list for parental control apps. Here are the checks educators use for apps, and how to use them yourself.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "We found no official teacher approval list for parental control apps. Here are the checks educators use for apps, and how to use them yourself.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["how-to-tell-parental-control-app-from-spyware", "monitoring-vs-blocking-parental-apps"],
    externalSources: [
      { label: "5 ways to evaluate educational apps for safety, quality and equity (ISTE)", url: "https://iste.org/blog/5-ways-to-evaluate-educational-apps-for-safety-quality-and-equity" },
    ],
    body: [
      {
        type: "p",
        text: "When we searched, we found no official list or seal that certifies a parental control app as approved by teachers, and several of the first results were pages published by app makers themselves. So treat the words \"teacher approved\" with care, on our site and on any other. What follows is better than a label: the checks educators use when they judge an app for children, and how a parent can apply them in ten minutes.",
      },
      { type: "h2", text: "Is there an official teacher approval for parental control apps" },
      {
        type: "p",
        text: "Not that we could find. There is an educator-led scheme for classroom technology: the ISTE seal of alignment, which ISTE says requires testing for quality and usability, accessibility and data privacy. It is built for educational tools, so it does not settle the question for a parental control app. Any page that calls an app \"teacher approved\" should name the teachers or the body, and say what they checked. If it does not, the phrase is only marketing.",
      },
      { type: "h2", text: "What do educators check when they judge an app for children" },
      {
        type: "p",
        text: "Mostly what it does with the child's data. Liz Kolb of the University of Michigan, writing for ISTE in 2024, tells educators to check what data an app collects, how much of it, and who it is shared with. She also points to the Common Sense privacy ratings, a colour-coded evaluation of apps, and recommends piloting a tool before rolling it out to everyone. Her article is about educational apps, not parental control apps, so the next section is our adaptation.",
      },
      {
        type: "cta",
        text: "See what CrenelView does on your phone, and judge it by these same checks.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "How can a parent check a parental control app the same way" },
      {
        type: "p",
        text: "Ask five plain questions. These are our suggestions, built on the checks above. What data does the app collect, and where does it go? Read the data safety section of its Play Store listing. Does it need an account or an internet connection to do its job? After installing, open the phone's app settings and look at the permissions list. Does it promise more than you need? An app that blocks a game at bedtime does not need to know everything your child does. Who made it, and can you reach them? Finally, try it for a week on your own phone before you rely on it, which is the pilot idea applied at home.",
      },
      { type: "h2", text: "Is CrenelView teacher approved" },
      {
        type: "p",
        text: "No. CrenelView has not been reviewed or approved by any teachers' group, school or education body, and we do not claim it has. It is a simple app that closes the apps you choose on your own phone, always or during hours you set. You can test it against the questions above: it has no account or sign-in, no ads, no analytics, and no internet permission, which you can see in its permissions list. It does not show a child's activity, filter websites or follow a phone's location, because it does only one job.",
      },
      { type: "h2", text: "What should a teacher do before recommending an app to parents" },
      {
        type: "p",
        text: "Check it first, and say what you checked. Following Kolb's advice, look at what data the app collects and who receives it, read an independent privacy rating if one exists, and try the app yourself before recommending it. When you do recommend one, tell parents what you looked at and what you did not, so the recommendation means something. A teacher's trust is worth more than any badge.",
      },
      {
        type: "p",
        text: "A label can be printed in a minute. A real check takes a little longer, and it is the one that protects your child.",
      },
      {
        type: "cta",
        text: "Run the checks yourself: choose which apps open on your phone, and when.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Is there an official teacher approval for parental control apps",
        answer:
          "Not that we could find when we searched. The ISTE seal of alignment, which tests for quality and usability, accessibility and data privacy, is built for educational tools, so it does not settle the question for parental control apps. A claim of teacher approval should name the teachers or body and say what they checked.",
      },
      {
        question: "What do educators check when they judge an app for children",
        answer:
          "Mostly what it does with the child's data. Liz Kolb, writing for ISTE in 2024, advises checking what data an app collects, how much, and who it is shared with, looking up an independent privacy rating, and piloting the tool before wide use.",
      },
      {
        question: "How can a parent check a parental control app the same way",
        answer:
          "Ask five plain questions: what data it collects and where it goes, whether it needs an account or internet connection, what permissions it asks for, who made it and how to reach them, and how it behaves in a week of use on your own phone. These are our suggestions built on the educator checks.",
      },
      {
        question: "Is CrenelView teacher approved",
        answer:
          "No. CrenelView has not been reviewed or approved by any teachers' group, school or education body. It closes the apps you choose on your own phone, has no account, ads or analytics, and has no internet permission, which you can see in its permissions list.",
      },
      {
        question: "What should a teacher do before recommending an app to parents",
        answer:
          "Check what data the app collects and who receives it, read an independent privacy rating if one exists, try the app, and tell parents what was and was not checked.",
      },
    ],
  },
  {
    slug: "best-parental-control-app-india-which-type-fits",
    title: "Which Parental Control App Is Best for Indian Parents? It Depends on Your Situation",
    metaTitle: "Best Parental Control App in India: Which Type Fits You",
    tags: ["About CrenelView"],
    ogImage: "/blog-images/best-parental-control-app-india-which-type-fits.jpg",
    description:
      "No single parental control app is best for every Indian family. Here is how the main types differ and which situation each one fits.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "5 min read",
    excerpt:
      "No single parental control app is best for every Indian family. Here is how the main types differ and which situation each one fits.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["who-crenelview-is-built-for", "monitoring-vs-blocking-parental-apps"],
    externalSources: [
      { label: "Google Family Link", url: "https://families.google/familylink/" },
    ],
    body: [
      {
        type: "p",
        text: "No single app is best for every family, and we are not going to tell you ours is. The right choice depends on one question: does your child have a phone of their own, or do they borrow yours? The five options below do different jobs, and each fits a different household. We make one of them, CrenelView, so we have described it last and listed what it does not do.",
      },
      { type: "h2", text: "What are the main types of parental control for Android phones" },
      {
        type: "p",
        text: "Five, and they differ mainly in whose phone they run on. There is Google Family Link, which works through a Google Account for the child. There are phone-maker tools such as Samsung Kids. There is Android's own screen pinning. There are apps that show a parent what a child is doing, usually on the child's own phone. And there are apps that run on the parent's phone and close the apps the parent chooses. Each of the next sections covers one.",
      },
      { type: "h2", text: "What does Google Family Link do, and who is it for" },
      {
        type: "p",
        text: "It is for a child who has their own device and Google Account. Google's Family Link page says parents can set screen time limits and downtime schedules, set time limits for individual apps, block apps, require approval for new apps, block inappropriate sites, and see children on a map while they carry their device. It also says a child under 13, or the applicable age in their country, needs a Google Account managed by Family Link, and that Family Link runs on certain Android and Chrome devices. So it fits when each child has a phone or tablet and an account. It does not fit when a child simply borrows a parent's phone.",
      },
      { type: "h2", text: "What do Samsung Kids and Android screen pinning do" },
      {
        type: "p",
        text: "Samsung Kids turns a Galaxy phone into a separate child space. Samsung's support page says parents choose which apps appear, set time limits and keep an eye on usage through PIN-protected options. To leave it, a parent opens the menu, closes Samsung Kids and enters the PIN. It runs on Galaxy phones and tablets with Android 9 or later, and Samsung says availability may vary by carrier. That page is Samsung's US site, so check your own phone in India. Screen pinning is Android's own option. Google's Android Help page says it keeps one app's screen in view until you unpin it, so a child can use only that app. To unpin, you hold a gesture, and if you have set a lock it asks for your PIN, pattern or password. It suits a short handover, one app at a time.",
      },
      {
        type: "cta",
        text: "If your child borrows your phone, see what CrenelView does and judge it for yourself.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What about apps that show a parent what their child is doing" },
      {
        type: "p",
        text: "These suit a parent who wants to see activity on the child's own phone. This group includes well-known names such as Qustodio, Bark and ParentalEdge. In general, apps like these are installed on the child's device, tied to an account, and send information back to the parent. They suit a family where the child has their own phone. They collect more about the child, which is a trade-off worth reading about before you install one. We compared three of them with CrenelView in separate articles, and our guide to monitoring and blocking explains the difference in how they work.",
      },
      { type: "h2", text: "Where does CrenelView fit, and what does it not do" },
      {
        type: "p",
        text: "It fits a parent whose child uses the parent's phone. CrenelView runs on your own phone. You pick the apps from a list, and when one opens it closes straight away and the phone goes back to the home screen. The block list has a single setting: every chosen app is blocked all the time, or blocked only during hours you set. \"Allow for a while\" opens the list for 10, 15, 30 or 60 minutes. There is no account or sign-in, and the app has no internet permission, which you can see in its permissions list. It does not show what a child did, filter websites, follow a phone's location, or work on a child's separate phone. If you need any of that, one of the other types will suit you better.",
      },
      { type: "h2", text: "How do you choose between them" },
      {
        type: "p",
        text: "Start with the phone. If your child has their own phone and Google Account, look at Family Link or an activity-showing app. If you have a Samsung phone and want a separate child space, try Samsung Kids. If you need to hand over the phone for a few minutes with one app, use screen pinning. If your child borrows your phone and you want chosen apps closed on a schedule, a blocking app on your own phone is the simple fit. Many Indian children share a parent's phone, as government survey data we cover elsewhere shows, but your own child's situation is the one that counts.",
      },
      {
        type: "p",
        text: "There is no best app in general. There is only the one that matches whose phone your child uses.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What are the main types of parental control for Android phones",
        answer:
          "Five, which differ mainly in whose phone they run on: Google Family Link, phone-maker tools such as Samsung Kids, Android screen pinning, apps that show a parent what a child is doing on the child's own phone, and apps that run on the parent's phone and close chosen apps.",
      },
      {
        question: "What does Google Family Link do, and who is it for",
        answer:
          "It is for a child who has their own device and Google Account. Google says parents can set screen time limits, set limits for individual apps, block apps, require approval for new apps, block inappropriate sites, and see children on a map while they carry their device. A child under 13, or the applicable age in their country, needs a Google Account managed by Family Link.",
      },
      {
        question: "What do Samsung Kids and Android screen pinning do",
        answer:
          "Samsung Kids creates a separate child space on a Galaxy phone where parents choose apps and set time limits through PIN-protected options, and it runs on Android 9 or later. Screen pinning, from Android Help, keeps one app's screen in view until you unpin it, so a child can use only that app.",
      },
      {
        question: "What about apps that show a parent what their child is doing",
        answer:
          "They suit a parent who wants to see activity on the child's own phone. Apps like these are generally installed on the child's device, tied to an account and send information back to the parent. They collect more about the child, which is a trade-off to read about first.",
      },
      {
        question: "Where does CrenelView fit, and what does it not do",
        answer:
          "It fits a parent whose child uses the parent's phone. It closes chosen apps on the parent's own phone, always or during set hours, with no account and no internet permission. It does not show what a child did, filter websites, follow a phone's location, or work on a child's separate phone.",
      },
      {
        question: "How do you choose between them",
        answer:
          "Start with whose phone your child uses. A child with their own phone and Google Account fits Family Link or an activity-showing app, a Samsung phone can use Samsung Kids, a short handover can use screen pinning, and a child who borrows your phone fits a blocking app on your own phone.",
      },
    ],
  },
  {
    slug: "factors-affecting-child-academic-performance-sleep-diet-screen-time",
    title: "Factors That Affect a Child's Academic Performance, and What Parents Can Change",
    metaTitle: "Factors That Affect a Child's Academic Performance",
    tags: ["Screen Time & Habits"],
    ogImage: "/blog-images/factors-affecting-child-academic-performance-sleep-diet-screen-time.jpg",
    description:
      "What affects how a child does at school? A study of 4,253 students links sleep, diet and screen time to results. Here is what parents can change.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "4 min read",
    excerpt:
      "What affects how a child does at school? A study of 4,253 students links sleep, diet and screen time to results. Here is what parents can change.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["screen-time-before-bed-child-sleep", "excess-screen-time-effects-psychology-study-behaviour"],
    externalSources: [
      { label: "The combined impact of diet, physical activity, sleep and screen time on academic achievement (International Journal of Behavioral Nutrition and Physical Activity, Springer Nature)", url: "https://link.springer.com/article/10.1186/s12966-017-0476-0" },
    ],
    body: [
      {
        type: "p",
        text: "Many things shape how a child does at school, and daily habits are among the ones a parent can influence. A Canadian study that followed 4,253 students in Nova Scotia found that lifestyle behaviours such as diet, sleep and screen time were strongly linked to exam results a year later, while body weight was not. The study shows an association, not proof of cause, but its findings give parents a short, practical list to start with.",
      },
      { type: "h2", text: "What affects a child's academic performance" },
      {
        type: "p",
        text: "Teaching, family circumstances and a child's own learning needs all matter, and this study did not measure them. What it did measure were habits. Researchers recorded the diet, physical activity, sleep and screen time of grade 5 students, about 10 to 11 years old, and compared them with the students' grade 6 provincial exams in maths, reading and writing. The US Centers for Disease Control and Prevention makes a similar point on its Health and Academics page: research shows a strong connection between healthy behaviours and academic achievement. The authors of the Nova Scotia study add that unmeasured factors, such as IQ and mental health, could also play a part.",
      },
      { type: "h2", text: "Do diet and sleep make a difference to marks" },
      {
        type: "p",
        text: "In this study, yes, in a modest and measurable way. Students who met dietary recommendations were more likely to meet academic expectations. Students who met both the sleep and the screen recommendations were more likely to meet writing expectations. Sleep was judged against 9 to 11 hours a night and was reported by parents. Each extra healthy habit a child had was linked to better chances of meeting expectations in maths, reading and writing, which suggests that small habits add up.",
      },
      { type: "h2", text: "Does screen time affect school results" },
      {
        type: "p",
        text: "In part, and the measure was limited. Meeting the screen recommendation together with the sleep one was linked to meeting writing expectations. The researchers asked parents about television viewing only, with a recommendation of under two hours a day, and did not ask about phones. So the study cannot tell you what phone or tablet use did to these children's marks. It also relied on what parents reported. Even so, it supports the idea that screen habits belong in the same conversation as sleep and meals, and our article on the effects of too much screen time looks at an Indian study that asked about it more broadly.",
      },
      {
        type: "cta",
        text: "Keep games and videos closed on your phone during homework and bedtime hours.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "Does exercise help with marks" },
      {
        type: "p",
        text: "This study did not find a link. Physical activity was not associated with academic achievement, and neither was body weight. That does not make exercise pointless: the activity questionnaire used did not capture how long or how hard children exercised, and the CDC lists physical inactivity among the behaviours linked to poorer academic outcomes. Studies differ, so treat exercise as good for the child in other ways, and do not expect it alone to raise marks.",
      },
      { type: "h2", text: "What should parents change first" },
      {
        type: "p",
        text: "Pick one habit and fix it, then add another. The study's authors conclude that the combined effect of several healthy behaviours is stronger than any single one, and that each extra recommendation met was linked to better odds of meeting expectations. The habits below are our suggestions, built on those findings: a regular bedtime, a screen-free hour before it, meals at fixed times, and a set slot for games or videos after homework. If your child uses your phone, you can keep the games and video apps closed until the time you choose.",
      },
      {
        type: "p",
        text: "Marks are shaped by many things. Sleep, meals and screens are among the few a family can change this week.",
      },
      {
        type: "cta",
        text: "Choose which apps open on your phone, and when, so study and sleep keep their hours.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "What affects a child's academic performance",
        answer:
          "Many things, including teaching, family circumstances and learning needs. A Canadian study of 4,253 grade 5 students found that diet, sleep and screen time were strongly linked to exam results a year later, and body weight was not. It shows an association, not proof of cause.",
      },
      {
        question: "Do diet and sleep make a difference to marks",
        answer:
          "In this study, yes. Students who met dietary recommendations were more likely to meet academic expectations, and those who met both the sleep and screen recommendations were more likely to meet writing expectations. Each extra healthy habit was linked to better chances overall.",
      },
      {
        question: "Does screen time affect school results",
        answer:
          "In part. Meeting the screen and sleep recommendations together was linked to meeting writing expectations, but the researchers asked only about television viewing, with a recommendation of under two hours a day, as reported by parents. The study cannot say what phone or tablet use did to the children's marks.",
      },
      {
        question: "Does exercise help with marks",
        answer:
          "This study found no link between physical activity and academic achievement, but its activity questionnaire did not capture duration or intensity, and the CDC lists physical inactivity among the behaviours linked to poorer outcomes. Studies differ.",
      },
      {
        question: "What should parents change first",
        answer:
          "Pick one habit and fix it, then add another. The authors conclude that several healthy behaviours together have a stronger effect than any single one. A regular bedtime, a screen-free hour before it and a set slot for games after homework are sensible places to start.",
      },
    ],
  },
  {
    slug: "block-distracting-apps-during-homework-hours-keep-study-apps",
    title: "How to Block Distracting Apps During Homework Hours and Keep Study Apps Open",
    metaTitle: "Block Distracting Apps During Homework, Keep Study Apps",
    tags: ["Setup & Troubleshooting"],
    ogImage: "/blog-images/block-distracting-apps-during-homework-hours-keep-study-apps.jpg",
    description:
      "Your child uses your phone for homework. Here is how to keep study apps available and close games and videos during set hours, step by step.",
    date: "2026-10-09",
    dateModified: "2026-10-09",
    readTime: "5 min read",
    excerpt:
      "Your child uses your phone for homework. Here is how to keep study apps available and close games and videos during set hours, step by step.",
    relatedProductPath: "/",
    relatedProductLabel: "See the full setup, step by step",
    relatedSlugs: ["block-apps-only-at-bedtime", "should-you-block-whatsapp-school-groups"],
    externalSources: [
      { label: "Union HRD Minister virtually releases PRAGYATA Guidelines on Digital Education (Press Information Bureau, Government of India)", url: "https://www.pib.gov.in/PressReleasePage.aspx?PRID=1638541" },
    ],
    body: [
      {
        type: "p",
        text: "Your child needs your phone for a video lesson, and ten minutes later is watching something else. The fix is to close the distracting apps during homework hours and leave the study apps alone. With CrenelView you do that by choosing which apps go on the block list. The study apps stay off it.",
      },
      { type: "h2", text: "Can you keep study apps open and block the rest during homework" },
      {
        type: "p",
        text: "You can keep study apps open and block the distracting ones, which is a slightly different thing. CrenelView blocks the apps you tick. It does not have a mode that allows only a short list and closes everything else. So the plan is simple: tick the games, videos and other apps that pull your child away, and do not tick the apps your child needs for study. When a ticked app opens during blocked hours, it closes at once and the phone returns to the home screen.",
      },
      { type: "h2", text: "Which apps should go on the block list" },
      {
        type: "p",
        text: "The ones your child reaches for instead of working. Start with games and video apps, then add social apps, and think about the rest case by case. Newly installed apps join the list automatically only if the phone's own system files them as games, so check the list now and then for other new apps. Two choices need thought. If the lessons are on YouTube, see the question on blocked lessons below. If your child's school class uses a WhatsApp group, our article on blocking WhatsApp with school groups helps you decide.",
      },
      { type: "h2", text: "How do you set homework hours" },
      {
        type: "p",
        text: "Choose the setting that blocks during hours you set, and enter the homework window. For example, you might block from 17:00 to 20:00, so the games and videos are closed while your child studies and open again afterwards. This is a single setting for the whole block list. Either every ticked app is blocked all the time, or every ticked app is blocked only during your hours. You cannot have some apps blocked always and others only in homework hours. Your settings are protected by your PIN, so your child cannot change the hours. If you also use the list at night, our bedtime article covers that.",
      },
      {
        type: "cta",
        text: "Pick the apps that distract, set the homework hours, and leave the study apps open.",
        buttonLabel: "Get it on Google Play",
      },
      { type: "h2", text: "What if the lesson is on an app you have blocked" },
      {
        type: "p",
        text: "Use \"Allow for a while.\" It opens the blocked list for 10, 15, 30 or 60 minutes, then blocks again by itself. Because the setting covers the whole list, every ticked app opens during that time, not just the one your child needs. So stay nearby, pick the shortest time that fits the lesson, and let the blocking return on its own. If the same app is used for study every day, you may decide to leave it off the list for the school term and rely on a daily routine for that one.",
      },
      { type: "h2", text: "How long should a child spend on a screen for study" },
      {
        type: "p",
        text: "India's Ministry of Education gave limits for online classes in its PRAGYATA guidelines, released in July 2020. For pre-primary children it said not more than 30 minutes a day. For classes 1 to 8 it said not more than two sessions of 30 to 45 minutes each, and for classes 9 to 12 not more than four sessions of 30 to 45 minutes each, on days when States and Union Territories hold online classes. The guidelines also stress balancing online and offline activities and avoiding long stretches on a device. Those figures were written for online classes during the pandemic, not for homework on a shared phone, so use them as a rough guide to balance, not as a rule for your child's evening.",
      },
      { type: "h2", text: "How do you know the blocking is working" },
      {
        type: "p",
        text: "Run the app's built-in check on your phone. Some phones close background apps to save battery, which can silently switch blocking off, and the check tells you whether blocking is active on your specific phone. Then test it once yourself by opening a ticked app during homework hours. If it closes, the plan works. Tell your child the hours and the reason, as a rule they can predict is easier to accept than a surprise.",
      },
      {
        type: "p",
        text: "Homework hours work best when the rule is the same every day. Pick the apps once, set the hours once, and let the phone do the reminding.",
      },
      {
        type: "cta",
        text: "Set homework hours once, and your phone closes the distractions for you.",
        buttonLabel: "Get it on Google Play",
      },
    ],
    faqs: [
      {
        question: "Can you keep study apps open and block the rest during homework",
        answer:
          "You can keep study apps open and block the distracting ones. CrenelView blocks the apps you tick and has no mode that allows only a short list, so tick the games, videos and other apps that distract and leave the study apps unticked.",
      },
      {
        question: "Which apps should go on the block list",
        answer:
          "The ones your child reaches for instead of working, starting with games and video apps. New apps join the list automatically only if the phone's system files them as games, so check the list now and then.",
      },
      {
        question: "How do you set homework hours",
        answer:
          "Choose the setting that blocks during hours you set and enter the homework window, such as 17:00 to 20:00. It is one setting for the whole list: either everything ticked is blocked all the time or only during your hours. A PIN protects the settings.",
      },
      {
        question: "What if the lesson is on an app you have blocked",
        answer:
          "Use \"Allow for a while\", which opens the blocked list for 10, 15, 30 or 60 minutes and then blocks again by itself. It opens every ticked app during that time, not just one.",
      },
      {
        question: "How long should a child spend on a screen for study",
        answer:
          "India's Ministry of Education PRAGYATA guidelines of July 2020 gave online class limits: not more than 30 minutes for pre-primary, two sessions of 30 to 45 minutes for classes 1 to 8 and four for classes 9 to 12, on days when online classes are held. They were written for online classes, so use them as a guide to balance.",
      },
      {
        question: "How do you know the blocking is working",
        answer:
          "Run the app's built-in check, which tells you whether blocking is active on your phone, since some phones close background apps to save battery. Then open a ticked app during homework hours to test it.",
      },
    ],
  },
];

export const getPostBySlug = (slug: string) => blogPosts.find((p) => p.slug === slug);
